export const SUPPORTED_LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    direction: 'ltr'
  },
  ja: {
    code: 'ja', 
    name: '日本語',
    direction: 'ltr'
  },
  'zh-TW': {
    code: 'zh-TW',
    name: '繁體中文',
    direction: 'ltr'
  }
};

export const DEFAULT_LANGUAGE = 'en';

// Get browser language preference
export const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  
  // Map browser codes to supported languages
  if (browserLang.startsWith('ja')) return 'ja';
  if (browserLang.startsWith('zh-TW') || browserLang === 'zh-Hant') return 'zh-TW';
  return 'en';
};

// Get language-specific instruction for AI
export const getLanguageInstruction = (language) => {
  const instructions = {
    en: 'Please provide your response in English.',
    ja: 'Please respond in Japanese (日本語で回答してください).',
    'zh-TW': 'Please respond in Traditional Chinese (請用繁體中文回答).'
  };
  
  return instructions[language] || instructions.en;
};

// Helper function to get random quote from a category
export const getRandomQuote = (quoteCategory, language = 'en') => {
  const quotes = translations[language]?.[`quotes.${quoteCategory}`] || translations.en[`quotes.${quoteCategory}`];
  if (!quotes || !Array.isArray(quotes)) return '';
  return quotes[Math.floor(Math.random() * quotes.length)];
};

// Translation storage
export const translations = {
  en: {
    // Language Switcher
    'lang.current': 'Language',
    
    // Navigation
    'nav.home': 'Home',
    'nav.meanings': 'Meanings', 
    'nav.spreads': 'Spreads',
    'nav.diary': 'My Diary',
    'nav.about': 'About',
    'nav.signin': 'Sign In',
    'nav.signup': 'Sign Up',
    
    // User Menu
    'user.profileSettings': 'Profile Settings',
    'user.signOut': 'Sign Out',
    
    // Profile Page
    'profile.title': 'Profile Settings',
    'profile.subtitle': 'Manage your personal information and account settings',
    'profile.loading': 'Loading...',
    'profile.tabProfile': 'Profile Info',
    'profile.tabPassword': 'Change Password',
    'profile.personalInfo': 'Personal Information',
    'profile.firstName': 'First Name',
    'profile.lastName': 'Last Name',
    'profile.firstNamePlaceholder': 'Enter your first name',
    'profile.lastNamePlaceholder': 'Enter your last name',
    'profile.emailAddress': 'Email Address',
    'profile.emailCannotChange': 'Email cannot be changed',
    'profile.saveChanges': 'Save Changes',
    'profile.saving': 'Saving...',
    'profile.changePasswordTitle': 'Change Password',
    'profile.currentPassword': 'Current Password',
    'profile.newPassword': 'New Password',
    'profile.confirmPassword': 'Confirm New Password',
    'profile.currentPasswordPlaceholder': 'Enter your current password',
    'profile.newPasswordPlaceholder': 'Enter new password',
    'profile.confirmPasswordPlaceholder': 'Confirm your new password',
    'profile.passwordRequirements': 'Password must be at least 8 characters with uppercase, lowercase, number and special character',
    'profile.passwordsDoNotMatch': 'Passwords do not match',
    'profile.changePasswordButton': 'Change Password',
    'profile.changing': 'Changing...',
    'profile.profileUpdated': 'Profile updated successfully!',
    'profile.passwordChanged': 'Password changed successfully!',
    
    // Homepage
    'home.title': 'TAROCA - Free Tarot Reading and Spreads',
    'home.subtitle': 'A Journey of Self-Reflection, Transformation, and Empowerment',
    'home.drawCard': 'Draw Your Card',
    'home.dailyCard': 'Tarot Card of the Day',
    'home.categoriesTitle': 'Free Tarot Reading Categories',
    'home.categoriesDescription': 'Choose a category that resonates with your current needs and questions',
    'home.sampleQuestions': 'Sample questions:',
    'home.getStarted': 'Get Started',
    
    // Categories
    'category.love': 'Love & Relationships',
    'category.career': 'Career & Purpose', 
    'category.growth': 'Personal Growth',
    'category.health': 'Health',
    'category.loveDesc': 'Explore matters of the heart and connection',
    'category.careerDesc': 'Guidance for your professional journey',
    'category.growthDesc': 'Insights for your spiritual evolution',
    'category.healthDesc': 'Wellness for body, mind, and soul.',
    
    // Category Questions
    'questions.love1': 'How can I improve my relationship?',
    'questions.love2': 'What does love mean for me today?',
    'questions.love3': 'How can I show myself more love?',
    'questions.career1': 'What career path aligns with my soul?',
    'questions.career2': 'How can I find more fulfillment at work?',
    'questions.career3': 'What skills should I develop?',
    'questions.growth1': 'What lesson am I meant to learn?',
    'questions.growth2': 'How can I become my best self?',
    'questions.growth3': 'What limiting belief should I release?',
    'questions.health1': 'How can I access my body\'s wisdom?',
    'questions.health2': 'How can I release old wounds?',
    'questions.health3': 'What brings me true peace?',
    
    // Inspirational Questions
    'inspire.question1': 'What does your soul need to hear today?',
    'inspire.question2': 'What energy should you embrace this week?',
    'inspire.question3': 'What lesson is the universe trying to teach you?',
    'inspire.question4': 'What hidden strength can you tap into today?',
    'inspire.question5': 'What do you need to release to move forward?',
    'inspire.question6': 'What opportunity is waiting for your attention?',
    'inspire.question7': 'What wisdom do you already possess?',
    'inspire.question8': 'What new perspective could transform your day?',
    'inspire.question9': 'What message does your heart want to share?',
    'inspire.question10': 'What magic is already present in your life?',
    
    // General Reading Section
    'home.notSureCategory': 'Not sure which category fits?',
    'home.generalReadingDesc': 'Get personalized guidance with our flexible general reading spreads',
    'home.createOwnReading': 'Create Your Own Reading',
    
    // Growth Section
    'home.whyTaroca': 'Why TAROCA?',
    'home.growthTitle': 'A Tarot Experience That Feels Real, Teaches Deep, and Helps You Grow',
    'home.growthDesc': 'TAROCA combines the magic of authentic Tarot rituals with guided insights and personal reflection — so every reading becomes a step forward in your journey.',
    
    // Features
    'home.featureCategory1': 'Choose with intuition, not just chance',
    'home.featureTitle1': 'Draw Like the Real Ritual',
    'home.featureDesc1': 'Experience Tarot as it\'s meant to be — cards arranged in a mystical circle or fan, waiting for your hand to choose. Each draw feels authentic, just like sitting at a real Tarot table.',
    'home.featureCategory2': 'More than just meanings — true understanding',
    'home.featureTitle2': 'Learn & Explore with Depth',
    'home.featureDesc2': 'Access a complete Tarot card library with upright and reversed meanings, symbolic insights, and pre-built spreads for love, career, and self-growth. Whether you\'re seeking answers or just learning, TAROCA guides you with clarity.',
    'home.featureCategory3': 'Turn readings into lessons for life',
    'home.featureTitle3': 'Reflect, Save, and Grow',
    'home.featureDesc3': 'Every spread you draw can be saved into your personal Tarot Diary. Reflect with guided prompts, track recurring cards, and review constructive suggestions tailored to your draws — helping you see patterns, lessons, and progress over time.',
    
    // Diary Section
    'home.diaryTitle': 'Your Tarot Diary',
    'home.diaryDesc': 'Keep track of your spiritual journey with your personal tarot diary. Save readings, reflect on insights, and watch your intuition grow over time.',
    'home.diaryFeature1': 'Save and organize readings',
    'home.diaryFeature2': 'Track patterns and insights',
    'home.diaryFeature3': 'Monitor your spiritual growth',
    'home.viewDiary': 'View My Diary',
    
    // Diary Page
    'diary.title': 'My Tarot Diary',
    'diary.subtitle': 'Your personal collection of tarot readings and insights',
    'diary.signInRequired': 'Sign In Required',
    'diary.signInMessage': 'Please sign in to access your tarot diary',
    'diary.signIn': 'Sign In',
    'diary.loading': 'Loading your tarot diary...',
    'diary.totalReadings': 'Total Readings',
    'diary.favoriteSpread': 'Favorite Spread',
    'diary.filterAll': 'All Spreads',
    'diary.filterLove': 'Love',
    'diary.filterCareer': 'Career',
    'diary.filterPersonal': 'Personal Growth',
    'diary.filterMental': 'Mental Healing',
    'diary.filterSingle': 'Tarot Card of the Day',
    'diary.noReadings': 'No readings yet',
    'diary.emptyMessage': 'Your tarot diary is empty. Complete a tarot spread and save it to start your collection!',
    'diary.importReading': 'Import a Reading',
    'diary.deleteConfirm': 'Are you sure you want to delete this reading?',
    'diary.deleteReading': 'Delete Reading',
    'diary.generateAI': 'Ask TAROCA',
    'diary.generatingAI': 'TAROCA is Revealing...',
    'diary.aiInsight': "TAROCA's Insight:",
    'diary.question': 'Question:',
    'diary.notes': 'Notes:',
    'diary.cardsDrawn': 'cards drawn',
    'diary.untitledReading': 'Untitled Reading',
    'diary.moreCards': 'more',
    'diary.previous': 'Previous',
    'diary.next': 'Next',
    'diary.page': 'Page',
    'diary.of': 'of',
    'diary.refreshing': 'Refreshing...',
    
    // Reading Detail Page
    'readingDetail.loading': 'Loading reading...',
    'readingDetail.notFound': 'Reading not found',
    'readingDetail.backToDiary': 'Back to Diary',
    'readingDetail.editNotes': 'Edit Notes',
    'readingDetail.save': 'Save',
    'readingDetail.cancel': 'Cancel',
    'readingDetail.defaultTitle': 'Tarot Reading Details',
    'readingDetail.questionAsked': 'Question Asked:',
    'readingDetail.yourNotes': 'Your Notes:',
    'readingDetail.questionPlaceholder': 'What question did you ask?',
    'readingDetail.notesPlaceholder': 'Add your thoughts, interpretations, and insights...',
    'readingDetail.askTarocaTitle': 'Ask TAROCA for Deeper Insights',
    'readingDetail.askTarocaDesc': 'Get personalized guidance that connects all your cards into a meaningful story and actionable next steps.',
    'readingDetail.benefit1': 'Detailed interpretation of your entire spread',
    'readingDetail.benefit2': 'Practical guidance tailored to your question',
    'readingDetail.benefit3': 'Insights into card relationships and patterns',
    'readingDetail.benefit4': 'Actionable advice for your next steps',
    'readingDetail.askTarocaBtn': 'Ask TAROCA - US$2.49',
    'readingDetail.tarocaRevealing': 'TAROCA is Revealing...',
    'readingDetail.tarocaExplanation': "✨ TAROCA's Explanation",
    'readingDetail.aiError': 'Having some Error:',
    'readingDetail.learnMore': '📖 Learn More About This Card',
    
    // Spreads
    'spread.love': 'Love Reading',
    'spread.career': 'Career Reading', 
    'spread.personal': 'Personal Growth',
    'spread.mental': 'Mental Healing',
    'spread.general': 'General Reading',
    'spread.chooseCards': 'Choose Number of Cards',
    'spread.askQuestion': 'Ask Your Question (Optional)',
    'spread.questionPlaceholder': 'What would you like to know about your love life?',
    'spread.startReading': 'Start Tarot Reading',
    'spread.selectType': 'Choose Your Situation',
    'spread.welcomePersonal': 'Free Tarot Reading for Personal Growth',
    'spread.welcomeHealing': 'Free Tarot Reading for Healing',
    'spread.welcomeGeneral': 'Free Tarot Reading for General Guidance',
    'spread.welcomeLove': 'Free Tarot Reading for Love & Relationships',
    'spread.welcomeCareer': 'Free Tarot Reading for Career & Purpose',

    // Footer
    'footer.spreadsHeading': 'Tarot Spreads',
    'footer.learnHeading': 'Learn Tarot',
    'footer.companyHeading': 'TAROCA',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.disclaimer': 'Tarot readings are for reflection and personal insight only. They are not medical, legal, or financial advice.',
    'footer.copyright': '© TAROCA. All rights reserved.',

    // 404 page
    'notFound.title': 'This page could not be found',
    'notFound.body': 'The page you are looking for may have moved or never existed. Try starting a new reading instead.',
    'notFound.cta': 'Back to tarot spreads',

    // Image alt text
    'alt.demoScreen': 'The TAROCA app showing a tarot reading with its interpretation',
    'alt.generalReading': 'Build your own custom tarot spread',
    'alt.iconRitual': 'Icon representing a daily tarot ritual',
    'alt.iconInsight': 'Icon representing insight from a tarot reading',
    'alt.iconGrowth': 'Icon representing personal growth through tarot',

    // Spread Drawing Page
    'spreadDrawing.customSpread': 'Custom Spread',
    'spreadDrawing.drawingCard': 'Drawing Tarot Card...',
    'spreadDrawing.drawCard': 'Draw Tarot Card',
    'spreadDrawing.drawAllCards': 'TAROCA Draws for You',
    'spreadDrawing.drawingCards': 'Drawing Cards...',
    'spreadDrawing.of': 'of',
    'spreadDrawing.for': 'for:',
    'spreadDrawing.newReading': 'New Reading',
    'spreadDrawing.saveToDiary': 'Save to Diary',
    'spreadDrawing.saving': 'Saving...',
    'spreadDrawing.tryAgain': 'Try Again',
    'spreadDrawing.signIn': 'Sign In',
    'spreadDrawing.signUpFree': 'Sign Up Free',
    'spreadDrawing.unlockExperience': 'Unlock Your Full Tarot Experience',
    'spreadDrawing.signInToUnlock': 'New registered users can get one free interpretation (Original Price US$2.49)! Sign in to unlock the complete power of your tarot journey:',
    'spreadDrawing.signInMessage': 'Sign in to save your reading and unlock TAROCA insights!',
    'spreadDrawing.personalDiary': '📖 Personal Diary',
    'spreadDrawing.personalDiaryDesc': 'Save and track your readings to discover patterns in your spiritual growth',
    'spreadDrawing.progressTracking': '📊 Progress Tracking',
    'spreadDrawing.progressTrackingDesc': 'View your reading history and spiritual journey statistics',
    'spreadDrawing.joinUsers': 'Join thousands of users discovering their spiritual path with TAROCA',
    'spreadDrawing.failedToSave': 'Failed to save reading. Please try again.',
    'spreadDrawing.paymentSuccess': 'Payment successful! Please save your reading manually using the "Save to Diary" button.',
    'spreadDrawing.cardPosition': 'Card',
    'spreadDrawing.personalNotes': 'Personal Notes',
    'spreadDrawing.notesPlaceholder': 'Write your thoughts and reflections about this reading...',
    'spreadDrawing.findLesson': 'Find the lesson in your reading and turn your spread into clear guidance you can act on. New registered users can get one free interpretation!',
    'spreadDrawing.getTarocaWisdom': "Get TAROCA's Personalized Explanation - US$2.49",
    'spreadDrawing.tarocaRevealing': 'TAROCA is Revealing...',
    'spreadDrawing.interpretingSpread': 'Interpreting your spread and preparing your guidance',
    'spreadDrawing.spreadOverview': 'Your Spread Overview',
    'spreadDrawing.learnMoreCard': '📖 Learn More About This Card',
    'spreadDrawing.yourQuestion': 'Your Question',
    'spreadDrawing.cardMeaning': 'Meaning:',
    'spreadDrawing.tarocaExplanationTitle': '🔮 TAROCA\'s Explanation',
    'spreadDrawing.drawAllCards': 'TAROCA Draws for You',
    'spreadDrawing.drawingCards': 'Drawing Cards...',

    // Payment Modal
    'payment.unlockWisdom': 'Unlock TAROCA\'s Wisdom',
    'payment.personalizedInsights': 'Get personalized insights for your tarot reading',
    'payment.whatYouGet': 'What you\'ll get:',
    'payment.tarocaInterpretation': 'TAROCA\'s interpretation of your spread',
    'payment.personalizedGuidance': 'Personalized guidance based on your question',
    'payment.actionableInsights': 'Actionable insights for your situation',
    'payment.savedToDiary': 'Saved to your diary for future reference',
    'payment.couponApplied': 'Coupon Applied:',
    'payment.save': 'Save',
    'payment.haveCoupon': 'Have a coupon code? Click here',
    'payment.couponCode': 'Coupon Code',
    'payment.enterCoupon': 'Enter coupon code',
    'payment.applyCoupon': 'Apply Coupon',
    'payment.applying': 'Applying...',
    'payment.cancel': 'Cancel',
    'payment.securePayment': 'Secure payment powered by Stripe',
    'payment.initializing': 'Initializing secure payment...',
    'payment.processing': 'Processing...',
    'payment.pay': 'Pay',
    'payment.thankYou': 'Thank you for your payment! 🎉',
    'payment.paymentSuccessful': 'Payment Successful! ✨ What\'s included:',
    'payment.readingSaved': 'Your reading has been saved to your diary',
    'payment.accessExplanations': 'Access to TAROCA\'s explanations',
    'payment.nextSteps': '💫 Next Steps:',
    'payment.waitForExplanation': 'Wait for a second. TAROCA\'s explanation will provide deep spiritual insights tailored to your unique situation.',
    'payment.viewAllReadings': 'View All Readings',
    'payment.tarocaRevealing': 'TAROCA is revealing...',
    'payment.viewThisReading': 'View This Reading',

    // Daily Card Positions
    'dailyCard.position': 'Today\'s Guidance',
    'dailyCard.healingPosition': 'Today\'s Healing Guidance',
    'dailyCard.lovePosition': 'Today\'s Love Guidance',
    'dailyCard.careerPosition': 'Today\'s Career Guidance',
    'dailyCard.growthPosition': 'Today\'s Growth Guidance',
    
    // Daily Card Spread Names
    'dailyCard.spreadName': 'Card of the Day',
    'dailyCard.growthSpreadName': 'Growth Card of the Day',

    // Love Spread
    'love.title': 'Love & Relationships Reading',
    'love.subtitle': 'Discover insights into your romantic life and relationships',
    'love.singles': 'For Singles to Find Love',
    'love.couple': 'For Couples',
    'love.singlesDesc': 'Love tarot spreads for attracting and finding your soulmate',
    'love.coupleDesc': 'Strengthen and understand your relationship',
    'love.chooseLoveSpread': 'Choose Your Love Spread',
    'love.selectCardCount': 'Select the number of tarot cards for your love reading',
    'love.selectSituation': 'Select Your Relationship Situation',
    'love.askLoveQuestion': 'Ask Your Love Question',
    'love.drawLoveCard': 'Draw Your Love Tarot Card',
    'love.loveCardOfDay': 'Love Tarot Card of the Day',
    'love.selectRelationship': 'Choose Your Relationship Type',
    'love.startLoveReading': 'Start Love Tarot Reading',
    'love.focusAreas': 'Focus areas:',
    'love.questionPlaceholder': 'What would you like to know about your relationships? (Optional)',
    'love.selfLovePrep': 'Self-love preparation',
    'love.attractPartner': 'Attracting the right partner',
    'love.removeLoveBlocks': 'Removing love blocks',
    'love.communication': 'Communication',
    'love.intimacy': 'Intimacy',
    'love.relationshipGrowth': 'Relationship growth',
    'love.family': 'For Family',
    'love.familyDesc': 'Heal and harmonize family relationships',
    'love.familyDynamics': 'Family dynamics',
    'love.healingOldWounds': 'Healing old wounds',
    'love.creatingHarmony': 'Creating harmony',
    'love.business': 'For Business Relationships',
    'love.businessDesc': 'Professional partnerships and business connections',
    'love.networking': 'Networking',
    'love.partnerships': 'Business partnerships',
    'love.professionalBounds': 'Professional boundaries',
    'love.reset': 'Reset',
    'love.yourLoveGuidance': 'Your Love Guidance',

    // Love Spread Card Positions - Singles
    'loveSpread.singles.1.loveReadiness': 'Love Readiness',
    'loveSpread.singles.3.pastLessons': 'Past Lessons',
    'loveSpread.singles.3.currentEnergy': 'Current Energy',
    'loveSpread.singles.3.futureLove': 'Future Love',
    'loveSpread.singles.5.yourDesire': 'Your Desire',
    'loveSpread.singles.5.whatAttractsLove': 'What Attracts Love',
    'loveSpread.singles.5.loveBlocks': 'Love Blocks',
    'loveSpread.singles.5.actionToTake': 'Action to Take',
    'loveSpread.singles.5.loveManifestation': 'Love Manifestation',
    'loveSpread.singles.6.selfLoveStatus': 'Self-Love Status',
    'loveSpread.singles.6.whatYouSeek': 'What You Seek',
    'loveSpread.singles.6.whatYouOffer': 'What You Offer',
    'loveSpread.singles.6.currentObstacles': 'Current Obstacles',
    'loveSpread.singles.6.divineTiming': 'Divine Timing',
    'loveSpread.singles.6.loveOutcome': 'Love Outcome',
    'loveSpread.singles.7.rootSecurity': 'Root - Security in Love',
    'loveSpread.singles.7.sacralDesires': 'Sacral - Romantic Desires',
    'loveSpread.singles.7.solarConfidence': 'Solar Plexus - Confidence',
    'loveSpread.singles.7.heartCapacity': 'Heart - Love Capacity',
    'loveSpread.singles.7.throatExpression': 'Throat - Love Expression',
    'loveSpread.singles.7.thirdEyeIntuition': 'Third Eye - Love Intuition',
    'loveSpread.singles.7.crownConnection': 'Crown - Soul Connection',
    "loveSpread.singles.10.loveSituation": "Current Situation",
    "loveSpread.singles.10.challengeCross": "Challenge",
    "loveSpread.singles.10.distantPast": "Past",
    "loveSpread.singles.10.recentPast": "Future",
    "loveSpread.singles.10.possibleOutcome": "Desired Outcome",
    "loveSpread.singles.10.nearFuture": "Foundation",
    "loveSpread.singles.10.yourApproach": "Self",
    "loveSpread.singles.10.externalInfluences": "External Influences",
    "loveSpread.singles.10.hopesFears": "Hopes & Fears",
    "loveSpread.singles.10.finalOutcome": "Final Outcome",
    'loveSpread.singles.12.janNewLoveEnergy': 'Jan',
    'loveSpread.singles.12.febSelfLove': 'Feb',
    'loveSpread.singles.12.marTakingAction': 'Mar',
    'loveSpread.singles.12.aprBuildingFoundation': 'Apr',
    'loveSpread.singles.12.mayAdventure': 'May',
    'loveSpread.singles.12.junHarmony': 'Jun',
    'loveSpread.singles.12.julReflection': 'Jul',
    'loveSpread.singles.12.augStrength': 'Aug',
    'loveSpread.singles.12.sepCompletion': 'Sep',
    'loveSpread.singles.12.octTransformation': 'Oct',
    'loveSpread.singles.12.novInnerWork': 'Nov',
    'loveSpread.singles.12.decLoveManifestation': 'Dec',

    // Love Spread Card Positions - Couple
    'loveSpread.couple.1.relationshipEnergy': 'Relationship Energy',
    'loveSpread.couple.3.relationshipPast': 'Relationship Past',
    'loveSpread.couple.3.currentDynamic': 'Current Dynamic',
    'loveSpread.couple.3.futureTogether': 'Future Together',
    'loveSpread.couple.5.youInRelationship': 'You in Relationship',
    'loveSpread.couple.5.yourPartner': 'Your Partner',
    'loveSpread.couple.5.relationshipBond': 'Relationship Bond',
    'loveSpread.couple.5.challengeToAddress': 'Challenge to Address',
    'loveSpread.couple.5.relationshipFuture': 'Relationship Future',
    'loveSpread.couple.6.yourFeelings': 'Your Feelings',
    'loveSpread.couple.6.partnersFeelings': 'Partner\'s Feelings',
    'loveSpread.couple.6.relationshipFoundation': 'Relationship Foundation',
    'loveSpread.couple.6.currentChallenge': 'Current Challenge',
    'loveSpread.couple.6.whatsNeeded': 'What\'s Needed',
    'loveSpread.couple.6.relationshipPath': 'Relationship Path',
    'loveSpread.couple.7.rootSecurity': 'Root - Security Together',
    'loveSpread.couple.7.sacralIntimacy': 'Sacral - Intimacy & Passion',
    'loveSpread.couple.7.solarConfidence': 'Solar Plexus - Confidence as Couple',
    'loveSpread.couple.7.heartLove': 'Heart - Love & Compassion',
    'loveSpread.couple.7.throatCommunication': 'Throat - Communication',
    'loveSpread.couple.7.thirdEyeVision': 'Third Eye - Shared Vision',
    'loveSpread.couple.7.crownBond': 'Crown - Spiritual Bond',
    'loveSpread.couple.10.relationshipStatus': 'Relationship Status',
    'loveSpread.couple.10.challengeCross': 'Challenge',
    'loveSpread.couple.10.foundation': 'Pass',
    'loveSpread.couple.10.recentPast': 'Near Future',
    'loveSpread.couple.10.possibleOutcome': 'Desired Outcome',
    'loveSpread.couple.10.nearFuture': 'Foundation',
    'loveSpread.couple.10.yourRole': 'Your Role',
    'loveSpread.couple.10.externalInfluences': 'External Influences',
    'loveSpread.couple.10.hopesFears': 'Hopes & Fears',
    'loveSpread.couple.10.relationshipFuture': 'Relationship Future',
    'loveSpread.couple.12.janNewBeginnings': 'Jan',
    'loveSpread.couple.12.febGrowthTogether': 'Feb',
    'loveSpread.couple.12.marTakingAction': 'Mar',
    'loveSpread.couple.12.aprStability': 'Apr',
    'loveSpread.couple.12.mayAdventure': 'May',
    'loveSpread.couple.12.junHarmony': 'Jun',
    'loveSpread.couple.12.julReflection': 'Jul',
    'loveSpread.couple.12.augStrength': 'Aug',
    'loveSpread.couple.12.sepCompletion': 'Sep',
    'loveSpread.couple.12.octChange': 'Oct',
    'loveSpread.couple.12.novDeepWork': 'Nov',
    'loveSpread.couple.12.decCelebration': 'Dec',

    // Love Spread Card Positions - Family
    'loveSpread.family.1.familyEnergy': 'Family Energy',
    'loveSpread.family.3.familyPast': 'Family Past',
    'loveSpread.family.3.currentDynamic': 'Current Dynamic',
    'loveSpread.family.3.familyFuture': 'Family Future',
    'loveSpread.family.5.yourRole': 'Your Role',
    'loveSpread.family.5.familyFoundation': 'Family Foundation',
    'loveSpread.family.5.currentChallenge': 'Current Challenge',
    'loveSpread.family.5.healingNeeded': 'Healing Needed',
    'loveSpread.family.5.familyHarmony': 'Family Harmony',
    'loveSpread.family.6.yourFeelings': 'Your Feelings',
    'loveSpread.family.6.familysNeeds': 'Family\'s Needs',
    'loveSpread.family.6.familyStrengths': 'Family Strengths',
    'loveSpread.family.6.currentIssues': 'Current Issues',
    'loveSpread.family.6.healingPath': 'Healing Path',
    'loveSpread.family.6.familyFuture': 'Family Future',
    'loveSpread.family.7.rootSecurity': 'Root - Family Security',
    'loveSpread.family.7.sacralCreativity': 'Sacral - Family Creativity',
    'loveSpread.family.7.solarPower': 'Solar Plexus - Family Power',
    'loveSpread.family.7.heartLove': 'Heart - Family Love',
    'loveSpread.family.7.throatCommunication': 'Throat - Family Communication',
    'loveSpread.family.7.thirdEyeWisdom': 'Third Eye - Family Wisdom',
    'loveSpread.family.7.crownSpirit': 'Crown - Family Spirit',
    'loveSpread.family.10.familySituation': 'Family Situation',
    'loveSpread.family.10.challengeCross': 'Challenge',
    'loveSpread.family.10.familyFoundation': 'Past',
    'loveSpread.family.10.recentPast': 'Near Future',
    'loveSpread.family.10.possibleOutcome': 'Desired Outcome',
    'loveSpread.family.10.nearFuture': 'Foundation',
    'loveSpread.family.10.yourRole': 'Your Role',
    'loveSpread.family.10.externalInfluences': 'External Influences',
    'loveSpread.family.10.familyHopesFears': 'Family Hopes & Fears',
    'loveSpread.family.10.familyFuture': 'Family Future',
    'loveSpread.family.12.janNewFamilyStart': 'Jan',
    'loveSpread.family.12.febFamilyGrowth': 'Feb',
    'loveSpread.family.12.marFamilyAction': 'Mar',
    'loveSpread.family.12.aprFamilyStability': 'Apr',
    'loveSpread.family.12.mayFamilyFun': 'May',
    'loveSpread.family.12.junFamilyHarmony': 'Jun',
    'loveSpread.family.12.julFamilyReflection': 'Jul',
    'loveSpread.family.12.augFamilyStrength': 'Aug',
    'loveSpread.family.12.sepFamilyCompletion': 'Sep',
    'loveSpread.family.12.octFamilyChange': 'Oct',
    'loveSpread.family.12.novFamilyHealing': 'Nov',
    'loveSpread.family.12.decFamilyJoy': 'Dec',

    // Love Spread Card Positions - Business
    'loveSpread.business.1.businessRelationship': 'Business Relationship',
    'loveSpread.business.3.professionalPast': 'Professional Past',
    'loveSpread.business.3.currentDynamic': 'Current Dynamic',
    'loveSpread.business.3.professionalFuture': 'Professional Future',
    'loveSpread.business.5.yourProfessionalRole': 'Your Professional Role',
    'loveSpread.business.5.partnerColleague': 'Partner/Colleague',
    'loveSpread.business.5.businessFoundation': 'Business Foundation',
    'loveSpread.business.5.challengeToAddress': 'Challenge to Address',
    'loveSpread.business.5.professionalOutcome': 'Professional Outcome',
    'loveSpread.business.6.yourProfessionalFeelings': 'Your Professional Feelings',
    'loveSpread.business.6.theirPerspective': 'Their Perspective',
    'loveSpread.business.6.businessFoundation': 'Business Foundation',
    'loveSpread.business.6.currentChallenge': 'Current Challenge',
    'loveSpread.business.6.strategyNeeded': 'Strategy Needed',
    'loveSpread.business.6.businessFuture': 'Business Future',
    'loveSpread.business.7.rootSecurity': 'Root - Business Security',
    'loveSpread.business.7.sacralCollaboration': 'Sacral - Creative Collaboration',
    'loveSpread.business.7.solarPower': 'Solar Plexus - Professional Power',
    'loveSpread.business.7.heartRespect': 'Heart - Mutual Respect',
    'loveSpread.business.7.throatCommunication': 'Throat - Business Communication',
    'loveSpread.business.7.thirdEyeVision': 'Third Eye - Business Vision',
    'loveSpread.business.7.crownPurpose': 'Crown - Higher Purpose',
    'loveSpread.business.10.businessSituation': 'Business Situation',
    'loveSpread.business.10.challengeCross': 'Challenge',
    'loveSpread.business.10.businessFoundation': 'Past',
    'loveSpread.business.10.recentPast': 'Near Future',
    'loveSpread.business.10.possibleOutcome': 'Desired Outcome',
    'loveSpread.business.10.nearFuture': 'Foundation',
    'loveSpread.business.10.yourApproach': 'Your Approach',
    'loveSpread.business.10.externalInfluences': 'External Influences',
    'loveSpread.business.10.businessHopesFears': 'Business Hopes & Fears',
    'loveSpread.business.10.professionalFuture': 'Professional Future',
    'loveSpread.business.12.janNewBusinessEnergy': 'Jan',
    'loveSpread.business.12.febProfessionalGrowth': 'Feb',
    'loveSpread.business.12.marBusinessAction': 'Mar',
    'loveSpread.business.12.aprStableFoundation': 'Apr',
    'loveSpread.business.12.mayBusinessAdventure': 'May',
    'loveSpread.business.12.junProfessionalHarmony': 'Jun',
    'loveSpread.business.12.julBusinessReflection': 'Jul',
    'loveSpread.business.12.augBusinessStrength': 'Aug',
    'loveSpread.business.12.sepProjectCompletion': 'Sep',
    'loveSpread.business.12.octBusinessTransformation': 'Oct',
    'loveSpread.business.12.novStrategicPlanning': 'Nov',
    'loveSpread.business.12.decBusinessSuccess': 'Dec',

    // Personal Growth
    'growth.chooseGrowthSpread': 'Choose Your Personal Growth Spread',
    'growth.selectCardCount': 'Select the number of tarot cards for your personal development reading',
    'growth.growthTypeTitle': 'Growth Type',
    'growth.selectGrowthType': 'Choose the type of personal growth you want guidance for',
    'growth.focusIntention': 'Focus Your Intention',
    'growth.intentionDesc': 'What aspect of your personal growth would you like guidance on?',
    'growth.focusAreas': 'Focus areas:',
    'growth.questionPlaceholder': 'What would you like to develop or understand about yourself? (Optional)',
    'growth.startDrawing': 'Start Drawing Tarot Cards',
    'growth.cards': 'Cards',
    'growth.singleInsight': 'Single Insight',
    'growth.singleInsightDesc': 'Current growth opportunity or lesson',
    'growth.growthTriangle': 'Growth Triangle',
    'growth.growthTriangleDesc': 'Past lessons, present state, future potential',
    'growth.personalDevelopmentCross': 'Personal Development Cross',
    'growth.personalDevelopmentCrossDesc': 'Core self, challenges, strengths, guidance, outcome',
    'growth.lifeAspectsSpread': 'Life Aspects Spread',
    'growth.lifeAspectsSpreadDesc': 'Mind, body, spirit, relationships, career, purpose',
    'growth.chakraGrowth': 'Chakra Growth',
    'growth.chakraGrowthDesc': 'Personal growth through your energy centers',
    'growth.lifeJourneyCelticCross': 'Life Journey Celtic Cross',
    'growth.lifeJourneyCelticCrossDesc': 'Comprehensive personal development reading',
    'growth.yearOfGrowth': 'Year of Growth',
    'growth.yearOfGrowthDesc': 'Monthly guidance for your personal development journey',
    'growth.lifeJourney': 'Life Journey',
    'growth.lifeJourneyDesc': 'Navigate major life transitions and find your path',
    'growth.lifePurpose': 'Life purpose',
    'growth.majorDecisions': 'Major decisions',
    'growth.lifeDirection': 'Life direction',
    'growth.personalMission': 'Personal mission',
    'growth.heroJourney': 'Hero Journey',
    'growth.heroJourneyDesc': 'Embrace challenges and transform through trials',
    'growth.overcomingObstacles': 'Overcoming obstacles',
    'growth.innerStrength': 'Inner strength',
    'growth.personalTrials': 'Personal trials',
    'growth.transformation': 'Transformation',
    'growth.spiritualGrowth': 'Spiritual Growth',
    'growth.spiritualGrowthDesc': 'Deepen your connection to the divine and higher self',
    'growth.spiritualAwakening': 'Spiritual awakening',
    'growth.higherConsciousness': 'Higher consciousness',
    'growth.divineConnection': 'Divine connection',
    'growth.soulPurpose': 'Soul purpose',
    'growth.transformationDesc': 'Embrace change and personal metamorphosis',
    'growth.personalChange': 'Personal change',
    'growth.breakingPatterns': 'Breaking patterns',
    'growth.newBeginnings': 'New beginnings',
    'growth.lifeRenewal': 'Life renewal',
    'growth.selfReflection': 'Self Reflection',
    'growth.selfReflectionDesc': 'Deep introspection and self-understanding',
    'growth.selfAwareness': 'Self-awareness',
    'growth.innerWisdom': 'Inner wisdom',
    'growth.personalInsights': 'Personal insights',
    'growth.selfDiscovery': 'Self-discovery',
    'growth.selfImprovement': 'Self Improvement',
    'growth.selfImprovementDesc': 'Develop skills, habits, and personal capabilities',
    'growth.skillDevelopment': 'Skill development',
    'growth.habitFormation': 'Habit formation',
    'growth.personalGoals': 'Personal goals',
    'growth.performanceGrowth': 'Performance growth',
    'growth.selfDefeating': 'Self Defeating',
    'growth.selfDefeatingDesc': 'Identify and overcome limiting patterns and behaviors',
    'growth.breakingLimitations': 'Breaking limitations',
    'growth.selfSabotagePatterns': 'Self-sabotage patterns',
    'growth.negativeBeliefs': 'Negative beliefs',
    'growth.personalBarriers': 'Personal barriers',
    'growth.drawGrowthCard': 'Draw Your Growth Card',
    'growth.reset': 'Reset',

    // Growth spread position translations
    // Life Journey
    'growth.spread.lifeJourney.1.position1': 'Current Growth Focus',
    'growth.spread.lifeJourney.3.position1': 'Past Lessons',
    'growth.spread.lifeJourney.3.position2': 'Present State',
    'growth.spread.lifeJourney.3.position3': 'Growth Potential',
    'growth.spread.lifeJourney.5.position1': 'Your Core Self',
    'growth.spread.lifeJourney.5.position2': 'Current Challenges',
    'growth.spread.lifeJourney.5.position3': 'Hidden Strengths',
    'growth.spread.lifeJourney.5.position4': 'Guidance Needed',
    'growth.spread.lifeJourney.5.position5': 'Growth Outcome',

    // Hero Journey
    'growth.spread.heroJourney.1.position1': 'Hero\'s Call',
    'growth.spread.heroJourney.3.position1': 'The Challenge',
    'growth.spread.heroJourney.3.position2': 'Inner Strength',
    'growth.spread.heroJourney.3.position3': 'Victory Path',
    'growth.spread.heroJourney.5.position1': 'Current Trial',
    'growth.spread.heroJourney.5.position2': 'Fear to Face',
    'growth.spread.heroJourney.5.position3': 'Ally Support',
    'growth.spread.heroJourney.5.position4': 'Wisdom Gained',
    'growth.spread.heroJourney.5.position5': 'Transformed Self',

    // Spiritual Growth
    'growth.spread.spiritualGrowth.1.position1': 'Spiritual Focus',
    'growth.spread.spiritualGrowth.3.position1': 'Soul Calling',
    'growth.spread.spiritualGrowth.3.position2': 'Divine Connection',
    'growth.spread.spiritualGrowth.3.position3': 'Enlightenment Path',
    'growth.spread.spiritualGrowth.5.position1': 'Spiritual Blocks',
    'growth.spread.spiritualGrowth.5.position2': 'Higher Self',
    'growth.spread.spiritualGrowth.5.position3': 'Divine Guidance',
    'growth.spread.spiritualGrowth.5.position4': 'Sacred Practice',
    'growth.spread.spiritualGrowth.5.position5': 'Awakened State',

    // Transformation
    'growth.spread.transformation.1.position1': 'Change Focus',
    'growth.spread.transformation.3.position1': 'What Must End',
    'growth.spread.transformation.3.position2': 'Transformation Process',
    'growth.spread.transformation.3.position3': 'New Beginning',
    'growth.spread.transformation.5.position1': 'Old Pattern',
    'growth.spread.transformation.5.position2': 'Release Process',
    'growth.spread.transformation.5.position3': 'Catalyst Energy',
    'growth.spread.transformation.5.position4': 'Integration Step',
    'growth.spread.transformation.5.position5': 'Renewed Self',

    // Self Reflection
    'growth.spread.selfReflection.1.position1': 'Inner Truth',
    'growth.spread.selfReflection.3.position1': 'Hidden Aspects',
    'growth.spread.selfReflection.3.position2': 'Self-Awareness',
    'growth.spread.selfReflection.3.position3': 'Inner Wisdom',
    'growth.spread.selfReflection.5.position1': 'Shadow Self',
    'growth.spread.selfReflection.5.position2': 'Conscious Mind',
    'growth.spread.selfReflection.5.position3': 'Subconscious Truth',
    'growth.spread.selfReflection.5.position4': 'Soul Message',
    'growth.spread.selfReflection.5.position5': 'Integrated Self',

    // Self Improvement
    'growth.spread.selfImprovement.1.position1': 'Development Focus',
    'growth.spread.selfImprovement.3.position1': 'Current Skills',
    'growth.spread.selfImprovement.3.position2': 'Growth Area',
    'growth.spread.selfImprovement.3.position3': 'Mastery Path',
    'growth.spread.selfImprovement.5.position1': 'Strength Foundation',
    'growth.spread.selfImprovement.5.position2': 'Skill Gap',
    'growth.spread.selfImprovement.5.position3': 'Learning Method',
    'growth.spread.selfImprovement.5.position4': 'Practice Needed',
    'growth.spread.selfImprovement.5.position5': 'Achieved Goal',

    // Self Defeating
    'growth.spread.selfDefeating.1.position1': 'Limiting Pattern',
    'growth.spread.selfDefeating.3.position1': 'Self-Sabotage',
    'growth.spread.selfDefeating.3.position2': 'Root Cause',
    'growth.spread.selfDefeating.3.position3': 'Liberation Path',
    'growth.spread.selfDefeating.5.position1': 'Limiting Belief',
    'growth.spread.selfDefeating.5.position2': 'Fear Pattern',
    'growth.spread.selfDefeating.5.position3': 'Inner Strength',
    'growth.spread.selfDefeating.5.position4': 'Healing Action',
    'growth.spread.selfDefeating.5.position5': 'Empowered Self',
    'growth.spread.selfDefeating.7.position1': 'Root - Security & Foundation',
    'growth.spread.selfDefeating.7.position2': 'Sacral - Creativity & Emotions',
    'growth.spread.selfDefeating.7.position3': 'Solar Plexus - Personal Power',
    'growth.spread.selfDefeating.7.position4': 'Heart - Love & Compassion',
    'growth.spread.selfDefeating.7.position5': 'Throat - Self-Expression',
    'growth.spread.selfDefeating.7.position6': 'Third Eye - Intuition & Wisdom',
    'growth.spread.selfDefeating.7.position7': 'Crown - Spiritual Connection',

    // Base growth spread positions (for other layouts)
    'growth.spread.base.6.position1': 'Mental Growth',
    'growth.spread.base.6.position2': 'Physical Wellbeing',
    'growth.spread.base.6.position3': 'Spiritual Development',
    'growth.spread.base.6.position4': 'Relationship Growth',
    'growth.spread.base.6.position5': 'Career Evolution',
    'growth.spread.base.6.position6': 'Life Purpose',

    'growth.spread.base.7.position1': 'Root - Security & Foundation',
    'growth.spread.base.7.position2': 'Sacral - Creativity & Emotions',
    'growth.spread.base.7.position3': 'Solar Plexus - Personal Power',
    'growth.spread.base.7.position4': 'Heart - Love & Compassion',
    'growth.spread.base.7.position5': 'Throat - Self-Expression',
    'growth.spread.base.7.position6': 'Third Eye - Intuition & Wisdom',
    'growth.spread.base.7.position7': 'Crown - Spiritual Connection',

    'growth.spread.base.10.position1': 'Current Situation',
    'growth.spread.base.10.position2': 'Growth Challenge',
    'growth.spread.base.10.position3': 'Past',
    'growth.spread.base.10.position4': 'Near Future',
    'growth.spread.base.10.position5': 'Possible Growth Path',
    'growth.spread.base.10.position6': 'Foundation',
    'growth.spread.base.10.position7': 'Your Approach',
    'growth.spread.base.10.position8': 'External Influences',
    'growth.spread.base.10.position9': 'Inner Hopes & Fears',
    'growth.spread.base.10.position10': 'Ultimate Growth Potential',

    'growth.spread.base.12.position1': 'January - New Beginnings',
    'growth.spread.base.12.position2': 'February - Self-Reflection',
    'growth.spread.base.12.position3': 'March - Taking Action',
    'growth.spread.base.12.position4': 'April - Building Stability',
    'growth.spread.base.12.position5': 'May - Embracing Change',
    'growth.spread.base.12.position6': 'June - Finding Balance',
    'growth.spread.base.12.position7': 'July - Inner Wisdom',
    'growth.spread.base.12.position8': 'August - Personal Strength',
    'growth.spread.base.12.position9': 'September - Completion & Harvest',
    'growth.spread.base.12.position10': 'October - Transformation',
    'growth.spread.base.12.position11': 'November - Inner Work',
    'growth.spread.base.12.position12': 'December - Integration & Celebration',

    // Mental Healing
    'healing.chooseHealingSpread': 'Choose Your Healing Spread',
    'healing.selectCardCount': 'Select the number of tarot cards for your healing reading',
    'healing.healingTypeTitle': 'Healing Type',
    'healing.selectHealingType': 'Choose the type of healing you need guidance for',
    'healing.focusHealingIntention': 'Focus Your Healing Intention',
    'healing.healingIntentionDesc': 'What specific aspect of your healing journey would you like guidance on?',
    'healing.healingPlaceholder': 'What would you like to heal or transform? (Optional)',
    'healing.drawHealingCard': 'Draw Your Healing Card',
    'healing.startDrawing': 'Start Drawing Tarot Cards',
    'healing.cards': 'Cards',
    'healing.focusAreas': 'Focus areas:',

    // Healing spread position translations
    // Self Healing
    'healing.spread.selfHealing.1.position1': 'Current Healing Focus',
    'healing.spread.selfHealing.3.position1': 'Inner Wounds',
    'healing.spread.selfHealing.3.position2': 'Self-Compassion Practice',
    'healing.spread.selfHealing.3.position3': 'Healing Integration',
    'healing.spread.selfHealing.5.position1': 'Core Wound',
    'healing.spread.selfHealing.5.position2': 'Self-Judgment',
    'healing.spread.selfHealing.5.position3': 'Inner Strength',
    'healing.spread.selfHealing.5.position4': 'Self-Care Action',
    'healing.spread.selfHealing.5.position5': 'Healed Self',

    // Energy Healing
    'healing.spread.energyHealing.1.position1': 'Energy Focus',
    'healing.spread.energyHealing.3.position1': 'Energy Blocks',
    'healing.spread.energyHealing.3.position2': 'Current Energy State',
    'healing.spread.energyHealing.3.position3': 'Energy Clearing Path',
    'healing.spread.energyHealing.5.position1': 'Energy Drain',
    'healing.spread.energyHealing.5.position2': 'Blocked Chakra',
    'healing.spread.energyHealing.5.position3': 'Natural Energy',
    'healing.spread.energyHealing.5.position4': 'Energy Practice',
    'healing.spread.energyHealing.5.position5': 'Balanced Energy',

    // Soulmate Healing
    'healing.spread.soulmateHealing.1.position1': 'Soul Connection Focus',
    'healing.spread.soulmateHealing.3.position1': 'Soul Wound',
    'healing.spread.soulmateHealing.3.position2': 'Current Connection',
    'healing.spread.soulmateHealing.3.position3': 'Divine Love Path',
    'healing.spread.soulmateHealing.5.position1': 'Past Life Pattern',
    'healing.spread.soulmateHealing.5.position2': 'Current Challenge',
    'healing.spread.soulmateHealing.5.position3': 'Soul Strength',
    'healing.spread.soulmateHealing.5.position4': 'Healing Action',
    'healing.spread.soulmateHealing.5.position5': 'Soul Union',

    // Pregnancy
    'healing.spread.pregnancy.1.position1': 'Fertility Focus',
    'healing.spread.pregnancy.3.position1': 'Fertility Blocks',
    'healing.spread.pregnancy.3.position2': 'Current State',
    'healing.spread.pregnancy.3.position3': 'Conception Path',
    'healing.spread.pregnancy.5.position1': 'Physical Readiness',
    'healing.spread.pregnancy.5.position2': 'Emotional Blocks',
    'healing.spread.pregnancy.5.position3': 'Natural Fertility',
    'healing.spread.pregnancy.5.position4': 'Support Needed',
    'healing.spread.pregnancy.5.position5': 'New Life Energy',

    // Physical Healing
    'healing.spread.physicalHealing.1.position1': 'Body Focus',
    'healing.spread.physicalHealing.3.position1': 'Physical Imbalance',
    'healing.spread.physicalHealing.3.position2': 'Body Wisdom',
    'healing.spread.physicalHealing.3.position3': 'Healing Protocol',
    'healing.spread.physicalHealing.5.position1': 'Root Cause',
    'healing.spread.physicalHealing.5.position2': 'Body Resistance',
    'healing.spread.physicalHealing.5.position3': 'Natural Healing',
    'healing.spread.physicalHealing.5.position4': 'Lifestyle Change',
    'healing.spread.physicalHealing.5.position5': 'Restored Health',

    // Rejection Healing
    'healing.spread.rejectionHealing.1.position1': 'Confidence Focus',
    'healing.spread.rejectionHealing.3.position1': 'Rejection Wound',
    'healing.spread.rejectionHealing.3.position2': 'Current Self-Worth',
    'healing.spread.rejectionHealing.3.position3': 'Confidence Path',
    'healing.spread.rejectionHealing.5.position1': 'Core Rejection',
    'healing.spread.rejectionHealing.5.position2': 'Self-Doubt',
    'healing.spread.rejectionHealing.5.position3': 'Inner Value',
    'healing.spread.rejectionHealing.5.position4': 'Healing Practice',
    'healing.spread.rejectionHealing.5.position5': 'Renewed Confidence',

    // Base healing spread positions (for other layouts)
    'healing.spread.base.6.position1': 'Mental Healing',
    'healing.spread.base.6.position2': 'Physical Healing',
    'healing.spread.base.6.position3': 'Emotional Healing',
    'healing.spread.base.6.position4': 'Spiritual Healing',
    'healing.spread.base.6.position5': 'Past Healing',
    'healing.spread.base.6.position6': 'Future Wholeness',

    'healing.spread.base.7.position1': 'Root - Safety & Security',
    'healing.spread.base.7.position2': 'Sacral - Emotions & Creativity',
    'healing.spread.base.7.position3': 'Solar Plexus - Personal Power',
    'healing.spread.base.7.position4': 'Heart - Love & Compassion',
    'healing.spread.base.7.position5': 'Throat - Expression & Truth',
    'healing.spread.base.7.position6': 'Third Eye - Intuition & Clarity',
    'healing.spread.base.7.position7': 'Crown - Spiritual Connection',

    'healing.spread.base.10.position1': 'Current Healing Situation',
    'healing.spread.base.10.position2': 'Main Challenge',
    'healing.spread.base.10.position3': 'Past Influences',
    'healing.spread.base.10.position4': 'Recent Future',
    'healing.spread.base.10.position5': 'Desired Outcome',
    'healing.spread.base.10.position6': 'Foundation',
    'healing.spread.base.10.position7': 'Your Approach',
    'healing.spread.base.10.position8': 'External Support',
    'healing.spread.base.10.position9': 'Inner Wisdom',
    'healing.spread.base.10.position10': 'Complete Healing',

    'healing.spread.base.12.position1': 'January - New Healing Cycle',
    'healing.spread.base.12.position2': 'February - Emotional Release',
    'healing.spread.base.12.position3': 'March - Taking Action',
    'healing.spread.base.12.position4': 'April - Building Foundation',
    'healing.spread.base.12.position5': 'May - Growth & Change',
    'healing.spread.base.12.position6': 'June - Balance & Harmony',
    'healing.spread.base.12.position7': 'July - Inner Reflection',
    'healing.spread.base.12.position8': 'August - Personal Strength',
    'healing.spread.base.12.position9': 'September - Integration',
    'healing.spread.base.12.position10': 'October - Transformation',
    'healing.spread.base.12.position11': 'November - Deep Healing',
    'healing.spread.base.12.position12': 'December - Renewal & Celebration',
    'healing.singleHealingFocus': 'Single Healing Focus',
    'healing.singleHealingFocusDesc': 'One key area for healing attention',
    'healing.healingTriangle': 'Healing Triangle',
    'healing.healingTriangleDesc': 'Root cause, current state, healing path',
    'healing.healingCross': 'Healing Cross',
    'healing.healingCrossDesc': 'Comprehensive healing guidance',
    'healing.holisticHealing': 'Holistic Healing',
    'healing.holisticHealingDesc': 'Mind, body, spirit, heart, past, future',
    'healing.chakraHealing': 'Chakra Healing',
    'healing.chakraHealingDesc': 'Healing through your energy centers',
    'healing.deepHealingJourney': 'Deep Healing Journey',
    'healing.deepHealingJourneyDesc': 'Complete healing transformation spread',
    'healing.yearOfHealing': 'Year of Healing',
    'healing.yearOfHealingDesc': 'Monthly healing guidance and progress',
    'healing.selfHealing': 'Self Healing',
    'healing.selfHealingDesc': 'Inner work and personal healing journey',
    'healing.selfCompassion': 'Self-compassion',
    'healing.innerChildWork': 'Inner child work',
    'healing.personalBoundaries': 'Personal boundaries',
    'healing.selfForgiveness': 'Self-forgiveness',
    'healing.energyHealing': 'Energy Healing',
    'healing.energyHealingDesc': 'Clearing and balancing your energy field',
    'healing.chakraAlignment': 'Chakra alignment',
    'healing.auraCleansing': 'Aura cleansing',
    'healing.energyBlocks': 'Energy blocks',
    'healing.spiritualProtection': 'Spiritual protection',
    'healing.soulmateHealing': 'Soulmate Healing',
    'healing.soulmateHealingDesc': 'Healing for deep soul connections and twin flame journeys',
    'healing.soulContracts': 'Soul contracts',
    'healing.twinFlameHealing': 'Twin flame healing',
    'healing.karmicRelationships': 'Karmic relationships',
    'healing.divineLove': 'Divine love',
    'healing.pregnancyFertility': 'Pregnancy & Fertility',
    'healing.pregnancyFertilityDesc': 'Healing support for conception, pregnancy, and motherhood',
    'healing.fertilityJourney': 'Fertility journey',
    'healing.pregnancySupport': 'Pregnancy support',
    'healing.maternalHealing': 'Maternal healing',
    'healing.newLifeEnergy': 'New life energy',
    'healing.physicalHealing': 'Physical Healing',
    'healing.physicalHealingDesc': 'Supporting your body\'s natural healing processes',
    'healing.bodyWisdom': 'Body wisdom',
    'healing.healingEnergy': 'Healing energy',
    'healing.vitalityRestoration': 'Vitality restoration',
    'healing.physicalStrength': 'Physical strength',
    'healing.rejectionHealing': 'Healing from Rejection',
    'healing.rejectionHealingDesc': 'Transforming rejection wounds into wisdom and strength',
    'healing.selfWorthHealing': 'Self-worth healing',
    'healing.rejectionRecovery': 'Rejection recovery',
    'healing.confidenceRebuilding': 'Confidence rebuilding',
    'healing.trustRestoration': 'Trust restoration',
    'healing.reset': 'Reset',

    // General Spread
    'general.generalReading': 'General Reading',
    'general.chooseSpreadSize': 'Choose Your Tarot Spread Size',
    'general.selectCardCount': 'Select the number of tarot cards for your reading',
    'general.singleCard': 'Single Card',
    'general.singleCardDesc': 'Quick guidance for your question',
    'general.pastPresentFuture': 'Past, Present, Future',
    'general.pastPresentFutureDesc': 'Timeline perspective on your situation',
    'general.fiveCardCross': 'Five-Card Cross',
    'general.fiveCardCrossDesc': 'Situation, Challenge, Past, Future, Outcome',
    'general.hexagramSpread': 'Hexagram Spread',
    'general.hexagramSpreadDesc': 'Comprehensive guidance on your path',
    'general.chakraAlignment': 'Chakra Alignment',
    'general.chakraAlignmentDesc': 'Balance across all aspects of life',
    'general.celticCross': 'Celtic Cross',
    'general.celticCrossDesc': 'Deep dive into your situation and future',
    'general.yearAhead': 'Year Ahead',
    'general.yearAheadDesc': 'Monthly guidance for the year',
    'general.pastInfluences': 'Past Influences',
    'general.presentSituation': 'Present Situation',
    'general.futurePotential': 'Future Potential',
    'general.currentSituation': 'Current Situation',
    'general.challengeCross': 'Challenge/Cross',
    'general.pastFoundation': 'Past Foundation',
    'general.futurePossibility': 'Future Possibility',
    'general.finalOutcome': 'Final Outcome',
    'general.generalGuidanceCard': 'General Guidance Card',
    'general.focusIntention': 'Focus Your Intention',
    'general.centerMindQuestion': 'Take a moment to center your mind and focus on your question',
    'general.drawGuidanceCard': 'Draw Your Guidance Card',
    'general.drawCards': 'Draw Cards',
    'general.reset': 'Reset',
    'general.questionPlaceholder': 'What would you like to know? (e.g., \'Should I change careers?\', \'How can I improve my health?\', \'What should I focus on this month?\') - Optional',

    // General spread position translations
    'general.spread.1.position1': 'Guidance',
    
    'general.spread.3.position1': 'Past Influences',
    'general.spread.3.position2': 'Present Situation',
    'general.spread.3.position3': 'Future Potential',
    
    'general.spread.5.position1': 'Current Situation',
    'general.spread.5.position2': 'Challenge/Cross',
    'general.spread.5.position3': 'Past Foundation',
    'general.spread.5.position4': 'Future Possibility',
    'general.spread.5.position5': 'Final Outcome',
    
    'general.spread.6.position1': 'Your Inner Self',
    'general.spread.6.position2': 'Your Current Path',
    'general.spread.6.position3': 'Hidden Influences',
    'general.spread.6.position4': 'Your Strengths',
    'general.spread.6.position5': 'External Factors',
    'general.spread.6.position6': 'Guidance Forward',
    
    'general.spread.7.position1': 'Root - Foundation',
    'general.spread.7.position2': 'Sacral - Creativity',
    'general.spread.7.position3': 'Solar Plexus - Personal Power',
    'general.spread.7.position4': 'Heart - Emotions',
    'general.spread.7.position5': 'Throat - Communication',
    'general.spread.7.position6': 'Third Eye - Intuition',
    'general.spread.7.position7': 'Crown - Spiritual Guidance',
    
    'general.spread.10.position1': 'Current Situation',
    'general.spread.10.position2': 'Challenge',
    'general.spread.10.position3': 'Past',
    'general.spread.10.position4': 'Near Future',
    'general.spread.10.position5': 'Desired Outcome',
    'general.spread.10.position6': 'Foundation',
    'general.spread.10.position7': 'Your Approach',
    'general.spread.10.position8': 'External Influences',
    'general.spread.10.position9': 'Hopes & Fears',
    'general.spread.10.position10': 'Final Outcome',
    
    'general.spread.12.position1': 'January',
    'general.spread.12.position2': 'February',
    'general.spread.12.position3': 'March',
    'general.spread.12.position4': 'April',
    'general.spread.12.position5': 'May',
    'general.spread.12.position6': 'June',
    'general.spread.12.position7': 'July',
    'general.spread.12.position8': 'August',
    'general.spread.12.position9': 'September',
    'general.spread.12.position10': 'October',
    'general.spread.12.position11': 'November',
    'general.spread.12.position12': 'December',
    
    // Card Position Interpretations
    'general.pastInfluences': 'Past Influences',
    'general.presentSituation': 'Present Situation',
    'general.futurePotential': 'Future Potential',
    'general.currentSituation': 'Current Situation',
    'general.challengeCross': 'Challenge/Cross',
    'general.pastFoundation': 'Past Foundation',
    'general.futurePossibility': 'Future Possibility',
    'general.finalOutcome': 'Final Outcome',

    // Inspirational Quotes
    'quotes.love': [
      "Who is my soul mate?",
      "What does that person think of me right now?",
      "Should I make a move or step back? Teach me the rules of love!",
      "Who's secretly in love with me?",
      "Does my partner have thoughts of cheating?!",
      "Why does that person desire my body?",
      "When will my next love come into my life?",
      "Can I get back together with the one I still care about?",
      "Why haven't they replied even though they've seen my message?",
      "Does that person want to see me?",
      "Will I have a time when everyone finds me attractive?",
      "Is that person my destined one?",
      "Is it okay to message them right now?",
      "What would happen if I confessed my feelings now?",
      "Is it wrong to fall for someone who already has a partner?",
      "Will I find someone I truly like?",
      "Do they like me, or am I just a bother?",
      "Is that person hiding something from me?",
      "The end of my current love—and the next one waiting for me.",
      "Was their first impression of me real?",
      "Should I forgive my partner?",
      "Is that person interested in me?",
      "Can I trust my partner's love?",
      "Why can't I seem to find a lover?",
      "Should I move on to my next love?",
      "How's my physical compatibility with that person?",
      "Has my ex already started a new relationship?"
    ],
    'quotes.career': [
      "What is my true calling?",
      "Is now the time to change jobs?",
      "Is my current job right for me?",
      "What will my future income be like?",
      "Change jobs? Go independent? Take a side job? What's the best way for me to work?",
      "How should I deal with a boss or subordinate I don't get along with?",
      "How am I evaluated at work?",
      "Can I overcome failures at work?",
      "What skills should I develop to advance my career?",
      "Am I on the right path to achieve my career goals?",
      "How can I improve my work-life balance?",
      "What opportunities for growth are available in my current role?",
      "How can I enhance my professional relationships?",
      "What steps can I take to increase my job satisfaction?",
      "Is further education or training beneficial for my career?",
      "How can I better manage stress related to work?",
      "What is blocking my career advancement?",
      "How can I align my career with my personal values?",
      "What new career paths should I consider exploring?"
    ],
    'quotes.growth': [
      "Should I do what I'm thinking now?",
      "Can I become rich?",
      "How can I become happy?",
      "A fateful choice awaits me within the next three months.",
      "Was that choice a mistake?",
      "The impression people have of me that I never expected.",
      "What should I focus on to improve myself?",
      "What should I do to grow?",
      "What is blocking my growth?",
      "How can I break free from my limitations?",
      "What habits should I cultivate for personal growth?",
      "How can I develop a more positive mindset?",
      "What steps can I take to enhance my self-confidence?",
      "How can I improve my relationships with others?",
      "What is my life's purpose or mission?",
      "How can I better manage stress and anxiety?",
      "What new skills should I learn to support my growth?",
      "How can I create a more balanced life?",
      "What opportunities for growth are coming my way?",
      "How can I overcome fear of failure?",
      "What does my inner self need to hear right now?",
      "Should I distance myself from that person I don't like?",
      "Will this problem be resolved?",
      "How can I repair my relationship with a friend I've had a falling out with?",
      "Should I pursue my dreams?",
      "Is this the luckiest time of the year?",
      "Will I thrive in a new environment?",
      "Happiness awaits you as you feel refreshed both physically and mentally.",
      "Who can I spend time with to feel energized?",
      "When that person wants to protect you.",
      "Lifestyle habits that will improve your financial luck?",
      "A romantic incident awaits me?",
      "Points to pay attention to over the next three months?",
      "An encounter that will come to you before the end of the year?",
      "How will I spend my last money of the year?"
    ],
    'quotes.healing': [
      "What can I do to improve my overall health right now?",
      "What message does the tarot have about my physical well-being?",
      "What steps can I take to support my mental health?",
      "How can I nurture my emotional healing?",
      "What spiritual practices can aid in my healing journey?",
      "What is blocking my healing process?",
      "How can I create a healing environment for myself?",
      "What role does forgiveness play in my healing?",
      "How can I better listen to my body's needs?",
      "What healing modalities should I explore?",
      "What is the root cause of my current health issues?",
      "How can I cultivate more self-love and compassion during my healing?",
      "What lifestyle changes can support my healing journey?",
      "How can I balance my mind, body, and spirit for optimal health?",
      "What lessons am I meant to learn through my healing process?",
      "How can I release past traumas that are hindering my healing?",
      "What support systems should I seek out for my healing?",
      "Is my current lifestyle supporting my body’s needs?",
      "How can I bring my mind, body, and spirit into better balance?",
      "What is blocking my healing or recovery process?",
      "What is the root cause of my current stress or anxiety?",
      "How can I find more peace and emotional stability?",
      "What changes do I need to make to support my mental health?",
      "What lesson am I being asked to learn through this emotional struggle?",
      "How can I release negative energy or self-sabotaging patterns affecting my health?",
      "What does my body need most right now to heal?",
      "What steps should I take to recover from illness or injury?",
      "How can I strengthen my immune system or vitality?",
      "What lifestyle habits are harming or helping my health?",
      "How can I best support my physical healing journey?",
      "What emotional wounds need my attention for healing?",
      "How can I cultivate more self-love and compassion during my healing process?",
      "What spiritual practices can aid in my overall healing and well-being?",
      "What is my body trying to tell me through my current symptoms?",
      "How can I align my energy to promote better health?",
      "What spiritual practices could improve my well-being?",
      "What karmic or emotional patterns might be affecting my health?",
      "What do I need to release to experience full healing and vitality?"
    ],
    'quotes.general': [
        "What is my true purpose in life?",
        "What direction should I take right now?",
        "What lessons am I meant to learn at this stage of my journey?",
        "What changes do I need to make to align with my higher self?",
        "What is blocking my personal growth or spiritual progress?",
        "What does the future hold for my love life?",
        "How can I attract a healthy, loving relationship?",
        "What lesson do I need to learn from my past relationships?",
        "What can I do to strengthen my current relationship?",
        "Is this person right for me or should I move on?",
        "What career path is most aligned with my talents and goals?",
        "What can I do to improve my financial situation?",
        "How can I find more fulfillment in my work?",
        "What opportunities are coming my way professionally?",
        "What skills should I develop to advance my career?",
        "What is the best way for me to achieve my goals?",
        "What habits or patterns are holding me back from success?",
        "How can I cultivate more confidence and self-belief?",
        "What steps can I take to improve my overall well-being?",
        "How can I create more balance in my life?",
        "What is the next step on my spiritual path?",
        "How can I deepen my connection to my intuition?",
        "What messages do my dreams have for me?",
        "What areas of my life need healing right now?",
        "What is the best way for me to practice self-care?",
        "How can I release negative energy and invite positivity into my life?",
        "What is the universe trying to tell me at this moment?",
        "What opportunities should I be open to right now?",
        "What challenges will help me grow the most?",
        "What strengths can I draw upon to overcome obstacles?",
        "What new beginnings are on the horizon for me?",
        "What do I need to let go of in order to move forward?",
        "What hidden talents or abilities do I possess?",
        "How can I better express myself and my creativity?",
        "What is my relationship with money and abundance?",
        "How can I improve my communication skills?",
        "What is the best way for me to nurture my relationships?",
        "What spiritual practices will benefit me the most right now?",
        "What is my soul trying to teach me through my current experiences?",
        "How can I align more closely with my life purpose?",
        "What is the most important thing for me to focus on right now?",
        "What is my biggest strength that I can leverage?",
        "What obstacles are holding me back from success?",
        "What energy surrounds me right now?",
        "What do I need to release to move forward?",
        "What is the universe trying to teach me through my current challenges?",
        "How can I better trust my intuition and inner wisdom?",
        "What do I need to focus on to create balance and happiness in my life?",
        "What is my true purpose in life?",
        "What direction should I take right now?",
        "What lessons am I meant to learn at this stage of my journey?",
        "What changes do I need to make to align with my higher self?",
        "What is blocking my personal growth or spiritual progress?",
        "What does the future hold for my love life?",
        "How can I attract a healthy, loving relationship?",
        "What lesson do I need to learn from my past relationships?",
        "What can I do to strengthen my current relationship?",
        "Is this person right for me or should I move on?",
        "What career path is most aligned with my talents and goals?",
        "What can I do to improve my financial situation?",
        "How can I find more fulfillment in my work?",
        "What opportunities are coming my way professionally?",
        "What skills should I develop to advance my career?",
        "What is the best way for me to achieve my goals?",
        "What habits or patterns are holding me back from success?",
        "How can I cultivate more confidence and self-belief?",
        "What steps can I take to improve my overall well-being?",
        "How can I create more balance in my life?",
        "What is the next step on my spiritual path?",
        "How can I deepen my connection to my intuition?",
        "What messages do my dreams have for me?",
        "What areas of my life need healing right now?",
        "What is the best way for me to practice self-care?",
        "How can I release negative energy and invite positivity into my life?",
        "What is the universe trying to tell me at this moment?",
        "What opportunities should I be open to right now?",
        "What challenges will help me grow the most?",
        "What strengths can I draw upon to overcome obstacles?",
        "What new beginnings are on the horizon for me?",
        "What do I need to let go of in order to move forward?",
        "What hidden talents or abilities do I possess?",
        "How can I better express myself and my creativity?",
        "What is my relationship with money and abundance?",
        "How can I improve my communication skills?",
        "What is the best way for me to nurture my relationships?",
        "What spiritual practices will benefit me the most right now?",
        "What is my soul trying to teach me through my current experiences?",
        "How can I align more closely with my life purpose?",
        "What is the most important thing for me to focus on right now?",
        "What is my biggest strength that I can leverage?",
        "What obstacles are holding me back from success?",
        "What energy surrounds me right now?",
        "What do I need to release to move forward?",
        "What is the universe trying to teach me through my current challenges?",
        "How can I better trust my intuition and inner wisdom?",
        "What do I need to focus on to create balance and happiness in my life?"
    ],
    
    // Card Options
    'cards.singleCard': 'Single Card',
    'cards.pastPresentFuture': 'Past, Present, Future',
    'cards.loveCross': 'Love Cross',
    'cards.relationshipSpread': 'Relationship Spread',
    'cards.chakraLove': 'Chakra Love',
    'cards.celticCross': 'Celtic Cross',
    'cards.zodiacLove': 'New Year Tarot Love Spread',
    
    // Card Descriptions
    'cards.singleCardDesc': 'Current state of your love life',
    'cards.pastPresentFutureDesc': 'Journey of your romantic life',
    'cards.loveCrossDesc': 'You & Partner, Challenge, Outcome',
    'cards.relationshipSpreadDesc': 'Comprehensive relationship analysis',
    'cards.chakraLoveDesc': 'Love energy through your chakras',
    'cards.celticCrossDesc': 'Deep dive into your love situation and future',
    'cards.zodiacLoveDesc': 'Month by month love forecast',
    
    // Career Spread
    'career.title': 'Career & Purpose Reading',
    'career.subtitle': 'Discover insights into your professional journey and life purpose',
    'career.chooseCareerSpread': 'Choose Your Career Tarot Spread',
    'career.selectCareerSituation': 'Select Your Career Situation',
    'career.askCareerQuestion': 'Ask Your Career Question',
    'career.drawCareerCard': 'Draw Your Career Tarot Card',
    'career.careerCardOfDay': 'Career Card of the Day',
    'career.startCareerReading': 'Start Career Tarot Reading',
    'career.careerCross': 'Career Cross',
    'career.careerCrossDesc': 'Skills, Challenges, Opportunities',
    'career.professionalPath': 'Professional Path',
    'career.professionalPathDesc': 'Comprehensive career analysis',
    'career.careerChakra': 'Career Chakra',
    'career.careerChakraDesc': 'Energy alignment with your work',
    'career.zodiacCareer': 'Zodiac Career',
    'career.zodiacCareerDesc': 'New Year & complete professional forecast',
    'career.reset': 'Reset',
    'career.singleCardDesc': 'Current state of your career',
    'career.pastPresentFutureDesc': 'Your 3-card career journey timeline',
    'career.celticCrossDesc': 'Deep dive into your career situation',
    'career.careerGuidance': 'Career Guidance',
    'career.currentJob': 'Current Job & Workplace',
    'career.jobSeeking': 'Job Seeking & New Opportunities',
    'career.entrepreneur': 'Entrepreneurship & Business',
    'career.currentJobDesc': 'Navigate challenges and opportunities in your current role',
    'career.jobSeekingDesc': 'Find guidance for your job search and new career paths',
    'career.entrepreneurDesc': 'Insights for starting or growing your business',
    'career.focusAreas': 'Focus areas:',
    'career.questionPlaceholder': 'What would you like to know about your career path? (Optional)',
    'career.identifyPassions': 'Identifying passions',
    'career.marketOpportunities': 'Market opportunities', 
    'career.manifestationTechniques': 'Manifestation techniques',
    'career.soulPurpose': 'Soul purpose',
    'career.naturalTalents': 'Natural talents',
    'career.serviceToOthers': 'Service to others',
    'career.developCareerPath': 'Develop your Career Path',
    'career.careerPathDesc': 'Navigate your professional direction and growth',
    'career.preparationStrategies': 'Preparation strategies',
    'career.firstImpressions': 'First impressions',
    'career.integrationTips': 'Integration tips',
    'career.strategicPlanning': 'Strategic planning',
    'career.skillDevelopment': 'Skill development',
    'career.longTermVision': 'Long-term vision',
    'career.workplaceCollaboration': 'Workplace Collaboration',
    'career.workplaceCollaborationDesc': 'Improve professional relationships and teamwork',
    'career.communicationSkills': 'Communication skills',
    'career.teamDynamics': 'Team dynamics',
    'career.leadershipPresence': 'Leadership presence',
    'career.careerType': 'Career Type',
    'career.selectCareerGuidance': 'Choose the type of career guidance you want',
    'career.selectNumberCards': 'Select the number of cards for your career reading',
    'career.focusIntention': 'Focus Your Intention',
    'career.centerMindQuestion': 'Take a moment to center your mind and focus on your specific career question',
    'career.startDrawingCards': 'Start Drawing Tarot Cards',
    'career.reset': 'Reset',

    // Career Spread Card Positions - New Job
    'career.spread.newJob.1.position1': 'New Job Readiness',
    'career.spread.newJob.3.position1': 'Previous Experience',
    'career.spread.newJob.3.position2': 'Current Preparation',
    'career.spread.newJob.3.position3': 'New Potential',
    'career.spread.newJob.5.position1': 'Your Skills',
    'career.spread.newJob.5.position2': 'Main Challenge',
    'career.spread.newJob.5.position3': 'Team Integration',
    'career.spread.newJob.5.position4': 'Growth Opportunity',
    'career.spread.newJob.5.position5': 'Job Outcome',
    'career.spread.newJob.6.position1': 'Current State',
    'career.spread.newJob.6.position2': 'Your Unique Value',
    'career.spread.newJob.6.position3': 'Skills You Need to Develop',
    'career.spread.newJob.6.position4': 'Potential Obstacles',
    'career.spread.newJob.6.position5': 'Optimal Timing',
    'career.spread.newJob.6.position6': 'Job Achievement',
    'career.spread.newJob.7.position1': 'Root - Job Security Foundation',
    'career.spread.newJob.7.position2': 'Sacral - Creative Contribution',
    'career.spread.newJob.7.position3': 'Solar Plexus - Professional Confidence',
    'career.spread.newJob.7.position4': 'Heart - Work Passion',
    'career.spread.newJob.7.position5': 'Throat - Communication Skills',
    'career.spread.newJob.7.position6': 'Third Eye - Career Vision',
    'career.spread.newJob.7.position7': 'Crown - Professional Purpose',
    'career.spread.newJob.10.position1': 'Current Job Situation',
    'career.spread.newJob.10.position2': 'Main Challenge',
    'career.spread.newJob.10.position3': 'Professional Past',
    'career.spread.newJob.10.position4': 'Career Future',
    'career.spread.newJob.10.position5': 'Desired Outcome',
    'career.spread.newJob.10.position6': 'Foundation',
    'career.spread.newJob.10.position7': 'Your Strategy',
    'career.spread.newJob.10.position8': 'Company Perspective',
    'career.spread.newJob.10.position9': 'Career Hopes & Fears',
    'career.spread.newJob.10.position10': 'Ultimate Job Outcome',
    'career.spread.newJob.12.position1': 'Jan',
    'career.spread.newJob.12.position2': 'Feb',
    'career.spread.newJob.12.position3': 'Mar',
    'career.spread.newJob.12.position4': 'Apr',
    'career.spread.newJob.12.position5': 'May',
    'career.spread.newJob.12.position6': 'Jun',
    'career.spread.newJob.12.position7': 'Jul',
    'career.spread.newJob.12.position8': 'Aug',
    'career.spread.newJob.12.position9': 'Sep',
    'career.spread.newJob.12.position10': 'Oct',
    'career.spread.newJob.12.position11': 'Nov',
    'career.spread.newJob.12.position12': 'Dec',

    // Career Spread Card Positions - Dream Job
    'career.spread.dreamJob.1.position1': 'Dream Career Energy',
    'career.spread.dreamJob.3.position1': 'Past Patterns',
    'career.spread.dreamJob.3.position2': 'Current Motivations',
    'career.spread.dreamJob.3.position3': 'Manifestation Path',
    'career.spread.dreamJob.5.position1': 'Your Passion',
    'career.spread.dreamJob.5.position2': 'Market Reality',
    'career.spread.dreamJob.5.position3': 'Skills Gap',
    'career.spread.dreamJob.5.position4': 'Strategic Action',
    'career.spread.dreamJob.5.position5': 'Career Realization',
    'career.spread.dreamJob.6.position1': 'Current Dissatisfaction',
    'career.spread.dreamJob.6.position2': 'Hidden Talents to Develop',
    'career.spread.dreamJob.6.position3': 'Dream Career Vision',
    'career.spread.dreamJob.6.position4': 'Barriers to Overcome',
    'career.spread.dreamJob.6.position5': 'Support Available',
    'career.spread.dreamJob.6.position6': 'Achievement Timeline',
    'career.spread.dreamJob.7.position1': 'Root - Financial Security in Dream Job',
    'career.spread.dreamJob.7.position2': 'Sacral - Creative Expression',
    'career.spread.dreamJob.7.position3': 'Solar Plexus - Personal Power',
    'career.spread.dreamJob.7.position4': 'Heart - Work Fulfillment',
    'career.spread.dreamJob.7.position5': 'Throat - Self-Expression',
    'career.spread.dreamJob.7.position6': 'Third Eye - Career Clarity',
    'career.spread.dreamJob.7.position7': 'Crown - Life Alignment',
    'career.spread.dreamJob.10.position1': 'Current Job Reality',
    'career.spread.dreamJob.10.position2': 'Internal Blocks',
    'career.spread.dreamJob.10.position3': 'Past',
    'career.spread.dreamJob.10.position4': 'Future',
    'career.spread.dreamJob.10.position5': 'Conscious Goals',
    'career.spread.dreamJob.10.position6': 'Subconscious Desires',
    'career.spread.dreamJob.10.position7': 'Your Approach',
    'career.spread.dreamJob.10.position8': 'Others\' Perspectives',
    'career.spread.dreamJob.10.position9': 'Dreams & Aspirations',
    'career.spread.dreamJob.10.position10': 'Ultimate Career Destiny',
    'career.spread.dreamJob.12.position1': 'Jan',
    'career.spread.dreamJob.12.position2': 'Feb',
    'career.spread.dreamJob.12.position3': 'Mar',
    'career.spread.dreamJob.12.position4': 'Apr',
    'career.spread.dreamJob.12.position5': 'May',
    'career.spread.dreamJob.12.position6': 'Jun',
    'career.spread.dreamJob.12.position7': 'Jul',
    'career.spread.dreamJob.12.position8': 'Aug',
    'career.spread.dreamJob.12.position9': 'Sep',
    'career.spread.dreamJob.12.position10': 'Oct',
    'career.spread.dreamJob.12.position11': 'Nov',
    'career.spread.dreamJob.12.position12': 'Dec',

    // Career Spread Card Positions - Life Purpose
    'career.spread.lifePurpose.1.position1': 'Soul\'s Mission',
    'career.spread.lifePurpose.3.position1': 'Past Career',
    'career.spread.lifePurpose.3.position2': 'Current Awakening',
    'career.spread.lifePurpose.3.position3': 'Manifestation Journey',
    'career.spread.lifePurpose.5.position1': 'Innate Soul',
    'career.spread.lifePurpose.5.position2': 'Life Lessons',
    'career.spread.lifePurpose.5.position3': 'Service Area Calling',
    'career.spread.lifePurpose.5.position4': 'Growth Path',
    'career.spread.lifePurpose.5.position5': 'Purpose Fulfillment',
    'career.spread.lifePurpose.6.position1': 'Current Purpose Confusion',
    'career.spread.lifePurpose.6.position2': 'Hidden Life Mission',
    'career.spread.lifePurpose.6.position3': 'True Calling Vision',
    'career.spread.lifePurpose.6.position4': 'Spiritual Blocks',
    'career.spread.lifePurpose.6.position5': 'Divine Support Available',
    'career.spread.lifePurpose.6.position6': 'Enlightened Career Path',
    'career.spread.lifePurpose.7.position1': 'Root - Life Foundation Purpose',
    'career.spread.lifePurpose.7.position2': 'Sacral - Creative Life Expression',
    'career.spread.lifePurpose.7.position3': 'Solar Plexus - Personal Will Alignment',
    'career.spread.lifePurpose.7.position4': 'Heart - Love-Based Service',
    'career.spread.lifePurpose.7.position5': 'Throat - Truth Expression',
    'career.spread.lifePurpose.7.position6': 'Third Eye - Spiritual Wisdom',
    'career.spread.lifePurpose.7.position7': 'Crown - Divine Connection',
    'career.spread.lifePurpose.10.position1': 'Current Life Situation',
    'career.spread.lifePurpose.10.position2': 'Soul Challenge',
    'career.spread.lifePurpose.10.position3': 'Past Life Karma',
    'career.spread.lifePurpose.10.position4': 'Soul Future',
    'career.spread.lifePurpose.10.position5': 'Higher Self Guidance',
    'career.spread.lifePurpose.10.position6': 'Recent Spiritual Lessons',
    'career.spread.lifePurpose.10.position7': 'Soul Path Strategy',
    'career.spread.lifePurpose.10.position8': 'World Service Need',
    'career.spread.lifePurpose.10.position9': 'Soul Aspirations',
    'career.spread.lifePurpose.10.position10': 'Cosmic Life Plan',
    'career.spread.lifePurpose.12.position1': 'Spiritual Cycle 1: Awakening',
    'career.spread.lifePurpose.12.position2': 'Spiritual Cycle 2: Learning',
    'career.spread.lifePurpose.12.position3': 'Spiritual Cycle 3: Developing',
    'career.spread.lifePurpose.12.position4': 'Spiritual Cycle 4: Serving',
    'career.spread.lifePurpose.12.position5': 'Spiritual Cycle 5: Mastering',
    'career.spread.lifePurpose.12.position6': 'Spiritual Cycle 6: Teaching',
    'career.spread.lifePurpose.12.position7': 'Soul Core Strength',
    'career.spread.lifePurpose.12.position8': 'Karmic Challenge to Heal',
    'career.spread.lifePurpose.12.position9': 'Divine Opportunity',
    'career.spread.lifePurpose.12.position10': 'Higher Guidance Message',
    'career.spread.lifePurpose.12.position11': 'Sacred Timing',
    'career.spread.lifePurpose.12.position12': 'Soul Destiny Fulfillment',

    // Career Spread Card Positions - Career Path
    'career.spread.careerPath.1.position1': 'Career Direction',
    'career.spread.careerPath.3.position1': 'Foundation Built',
    'career.spread.careerPath.3.position2': 'Current Crossroads',
    'career.spread.careerPath.3.position3': 'Optimal Path Forward',
    'career.spread.careerPath.5.position1': 'Core Strengths',
    'career.spread.careerPath.5.position2': 'Industry Trends',
    'career.spread.careerPath.5.position3': 'Skill Development',
    'career.spread.careerPath.5.position4': 'Network Building',
    'career.spread.careerPath.5.position5': 'Career Achievement',
    'career.spread.careerPath.6.position1': 'Current Position',
    'career.spread.careerPath.6.position2': 'Natural Talents to Leverage',
    'career.spread.careerPath.6.position3': 'Professional Vision Clarity',
    'career.spread.careerPath.6.position4': 'Development Areas Needed',
    'career.spread.careerPath.6.position5': 'Market Forces Influence',
    'career.spread.careerPath.6.position6': 'Long-term Growth Path',
    'career.spread.careerPath.7.position1': 'Root - Career Stability Foundation',
    'career.spread.careerPath.7.position2': 'Sacral - Professional Innovation',
    'career.spread.careerPath.7.position3': 'Solar Plexus - Career Ambition',
    'career.spread.careerPath.7.position4': 'Heart - Work-Life Fulfillment',
    'career.spread.careerPath.7.position5': 'Throat - Leadership Voice',
    'career.spread.careerPath.7.position6': 'Third Eye - Strategic Vision',
    'career.spread.careerPath.7.position7': 'Crown - Professional Legacy',
    'career.spread.careerPath.10.position1': 'Current Career Status',
    'career.spread.careerPath.10.position2': 'Professional Challenge',
    'career.spread.careerPath.10.position3': 'Career History',
    'career.spread.careerPath.10.position4': 'Potential Future',
    'career.spread.careerPath.10.position5': 'Desired Achievement',
    'career.spread.careerPath.10.position6': 'Experience Foundation',
    'career.spread.careerPath.10.position7': 'Your Strategic Approach',
    'career.spread.careerPath.10.position8': 'Industry Perspective',
    'career.spread.careerPath.10.position9': 'Career Hopes & Fears',
    'career.spread.careerPath.10.position10': 'Professional Destiny Path',
    'career.spread.careerPath.12.position1': 'Q1 Career Focus',
    'career.spread.careerPath.12.position2': 'Q2 Skill Development',
    'career.spread.careerPath.12.position3': 'Q3 Opportunity Pursuit',
    'career.spread.careerPath.12.position4': 'Q4 Achievement Goal',
    'career.spread.careerPath.12.position5': 'Year 2 Vision',
    'career.spread.careerPath.12.position6': 'Long-term Legacy Goal',
    'career.spread.careerPath.12.position7': 'Key Professional Strength',
    'career.spread.careerPath.12.position8': 'Major Challenge Navigation',
    'career.spread.careerPath.12.position9': 'Best Opportunity Window',
    'career.spread.careerPath.12.position10': 'Strategic Career Advice',
    'career.spread.careerPath.12.position11': 'Optimal Timing',
    'career.spread.careerPath.12.position12': 'Career Legacy Achievement',

    // Career Spread Card Positions - Workplace Collaboration
    'career.spread.workplace.1.position1': 'Team Dynamic Energy',
    'career.spread.workplace.3.position1': 'Past Experiences',
    'career.spread.workplace.3.position2': 'Current Dynamics',
    'career.spread.workplace.3.position3': 'Future Collaboration',
    'career.spread.workplace.5.position1': 'Your Communication Style',
    'career.spread.workplace.5.position2': 'Team Chemistry Potential',
    'career.spread.workplace.5.position3': 'Shared Goals Alignment',
    'career.spread.workplace.5.position4': 'Conflict Resolution Approach',
    'career.spread.workplace.5.position5': 'Collective Success Achievement',
    'career.spread.workplace.6.position1': 'Current Team Tension',
    'career.spread.workplace.6.position2': 'Your Unique Contribution',
    'career.spread.workplace.6.position3': 'Team Vision Clarity',
    'career.spread.workplace.6.position4': 'Communication Gaps',
    'career.spread.workplace.6.position5': 'Leadership Support',
    'career.spread.workplace.6.position6': 'Harmony Achievement Path',
    'career.spread.workplace.7.position1': 'Root - Team Security Foundation',
    'career.spread.workplace.7.position2': 'Sacral - Creative Team Synergy',
    'career.spread.workplace.7.position3': 'Solar Plexus - Shared Power Balance',
    'career.spread.workplace.7.position4': 'Heart - Mutual Respect',
    'career.spread.workplace.7.position5': 'Throat - Open Communication',
    'career.spread.workplace.7.position6': 'Third Eye - Collective Vision',
    'career.spread.workplace.7.position7': 'Crown - Unified Higher Purpose',
    'career.spread.workplace.10.position1': 'Current Team Situation',
    'career.spread.workplace.10.position2': 'Collaboration Challenge',
    'career.spread.workplace.10.position3': 'Team History Dynamics',
    'career.spread.workplace.10.position4': 'Future Team Potential',
    'career.spread.workplace.10.position5': 'Ideal Team Outcome',
    'career.spread.workplace.10.position6': 'Recent Team Issues',
    'career.spread.workplace.10.position7': 'Your Approach Strategy',
    'career.spread.workplace.10.position8': 'Others\' Perspectives',
    'career.spread.workplace.10.position9': 'Team Aspirations',
    'career.spread.workplace.10.position10': 'Collective Success Future',
    'career.spread.workplace.12.position1': 'Month 1: Relationship Building',
    'career.spread.workplace.12.position2': 'Month 2: Trust Development',
    'career.spread.workplace.12.position3': 'Month 3: Challenge Navigation',
    'career.spread.workplace.12.position4': 'Month 4: Growth Together',
    'career.spread.workplace.12.position5': 'Month 5: Success Celebration',
    'career.spread.workplace.12.position6': 'Month 6: Mastery Achievement',
    'career.spread.workplace.12.position7': 'Communication Core Strength',
    'career.spread.workplace.12.position8': 'Relationship Challenge',
    'career.spread.workplace.12.position9': 'Team Opportunity',
    'career.spread.workplace.12.position10': 'Leadership Guidance',
    'career.spread.workplace.12.position11': 'Collaboration Timing',
    'career.spread.workplace.12.position12': 'Team Legacy Building',
    
    // Payment
    'payment.title': 'Get TAROCA Explanation',
    'payment.price': 'US$2.49',
    'payment.description': 'Unlock detailed TAROCA insights for your reading',
    'payment.coupon': 'Have a coupon code? Click here',
    'payment.applyCoupon': 'Apply Coupon',
    'payment.processing': 'Processing payment...',
    'payment.success': 'Payment successful!',
    'payment.getExplanation': 'Get TAROCA Explanation',
    
    // Auth
    'auth.login': 'Sign In',
    'auth.register': 'Create Account', 
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.firstName': 'First Name',
    'auth.lastName': 'Last Name',
    'auth.welcome': 'Join us to start your tarot journey.',
    'auth.switchToLogin': 'Already have an account? Sign in',
    'auth.switchToRegister': 'Don\'t have an account? Sign up',
    'auth.forgotPassword': 'Forgot your password?',
    'auth.createAccount': 'Create Account',
    'auth.signIn': 'Sign In',
    'auth.resetPassword': 'Reset Password',
    'auth.newPassword': 'New Password',
    'auth.resetTitle': 'Reset Your Password',
    'auth.resetSubtitle': 'Enter your new password below',
    'auth.resetSuccess': 'Password reset successfully!',
    'auth.invalidToken': 'Invalid or expired reset token',
    'auth.passwordMismatch': 'Passwords do not match',
    'auth.backToLogin': 'Back to Sign In',
    'auth.subtitle': 'Sign in to Unlock Your Full Tarot Experience',
    'auth.registerSubtitle': 'Create your account to start your tarot journey. New registered users can get one free interpretation (Original Price US$2.49)!',
    'auth.emailPlaceholder': 'Enter your email',
    'auth.passwordPlaceholder': 'Enter your password',
    'auth.firstNamePlaceholder': 'Enter your first name',
    'auth.lastNamePlaceholder': 'Enter your last name',
    'auth.confirmPasswordPlaceholder': 'Confirm your password',
    'auth.signingIn': 'Signing In...',
    'auth.creatingAccount': 'Creating Account...',
    'auth.showPassword': 'Show password',
    'auth.hidePassword': 'Hide password',
    'auth.switchToRegisterText': 'Don\'t have an account? ',
    'auth.switchToLoginText': 'Already have an account? ',
    'auth.signUpLink': 'Sign up',
    'auth.signInLink': 'Sign in',
    'auth.uspDrawTarot': 'Draw Tarot Like the Real Thing',
    'auth.uspTarotLibrary': 'Your Complete Tarot Library',
    'auth.uspGuidedSpreads': 'Guided Spreads for Every Situation',
    'auth.uspPersonalDiary': 'Your Personal Tarot Diary',
    'auth.uspInsights': 'Insights That Move You Forward',
    'auth.validationFirstNameRequired': 'First name is required',
    'auth.validationLastNameRequired': 'Last name is required',
    'auth.validationEmailRequired': 'Email is required',
    'auth.validationEmailInvalid': 'Please enter a valid email address',
    'auth.validationPasswordRequired': 'Password is required',
    'auth.validationPasswordTooShort': 'Password must be at least 8 characters long',
    'auth.validationPasswordWeak': 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
    'auth.validationConfirmPasswordRequired': 'Please confirm your password',
    'auth.validationPasswordsNotMatch': 'Passwords do not match',
    'auth.forgotPasswordTitle': 'Forgot Password',
    'auth.forgotPasswordSubtitle': 'Enter your email address and we\'ll send you a reset link',
    'auth.sendResetLink': 'Send Reset Link',
    'auth.sendingResetLink': 'Sending...',
    'auth.checkEmailTitle': 'Check Your Email',
    'auth.checkEmailMessage': 'We\'ve sent a password reset link to',
    'auth.resetTokenLabel': 'Reset Token (for development)',
    'auth.copyToken': 'Copy Token',
    'auth.resetTokenCopied': 'Reset token copied to clipboard!',
    'auth.validationEmailRequired': 'Email is required',
    'auth.rememberPassword': 'Remember your password? ',
    
    // Cards and Readings
    'card.upright': 'Upright',
    'card.reversed': 'Reversed',
    'reading.question': 'What question would you like to ask?',
    'reading.questionPlaceholder': 'Enter your question here...',
    'reading.yourReading': 'Your Reading',
    'reading.saveReading': 'Save Reading',
    
    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.continue': 'Continue',
    'common.today': 'Today',
    'common.card': 'Card',

    // Tarot Cards
    // Major Arcana
    'tarot.fool.name': '0. The Fool',
    'tarot.fool.meaning': 'The Fool is the first card in the Major Arcana and represents the beginning of a new journey.',
    'tarot.fool.link': 'https://tarotreadingcard.online/tarot-card-meanings/0-the-fool-tarot-card-meanings-free-tarot-reading/',
    'tarot.magician.name': '1. The Magician',
    'tarot.magician.meaning': 'The Magician is a card of potential and manifestation, symbolizing the power to turn ideas into reality.',
    'tarot.magician.link': 'https://tarotreadingcard.online/tarot-card-meanings/1-the-magician-tarot-card-meanings-free-tarot-reading/',
    'tarot.highPriestess.name': '2. The High Priestess',
    'tarot.highPriestess.meaning': 'The High Priestess represents intuition, mystery, and the connection to the subconscious mind.',
    'tarot.highPriestess.link': 'https://tarotreadingcard.online/tarot-card-meanings/2-the-high-priestess-tarot-card-meanings-free-tarot-reading/',
    'tarot.empress.name': '3. The Empress',
    'tarot.empress.meaning': 'The Empress is a card of abundance, nurturing energy, and creativity.',
    'tarot.empress.link': 'https://tarotreadingcard.online/tarot-card-meanings/3-the-empress-tarot-card-meanings-free-tarot-reading/',
    'tarot.emperor.name': '4. The Emperor',
    'tarot.emperor.meaning': 'The Emperor is a card of authority, structure, and stability.',
    'tarot.emperor.link': 'https://tarotreadingcard.online/tarot-card-meanings/4-the-emperor-tarot-card-meanings-free-tarot-reading/',
    'tarot.hierophant.name': '5. The Hierophant',
    'tarot.hierophant.meaning': 'The Hierophant, also known as the High Priest, represents tradition, spirituality, and guidance.',
    'tarot.hierophant.link': 'https://tarotreadingcard.online/tarot-card-meanings/5-the-hierophant-tarot-card-meanings-free-tarot-reading/',
    'tarot.lovers.name': '6. The Lovers',
    'tarot.lovers.meaning': 'The Lovers card symbolizes union, harmony, and the power of choice.',
    'tarot.lovers.link': 'https://tarotreadingcard.online/tarot-card-meanings/6-the-lovers-tarot-card-meanings-free-tarot-reading/',
    'tarot.chariot.name': '7. The Chariot',
    'tarot.chariot.meaning': 'The Chariot represents determination, willpower, and victory through focus and control.',
    'tarot.chariot.link': 'https://tarotreadingcard.online/tarot-card-meanings/7-the-chariot-tarot-card-meanings-free-tarot-reading/',
    'tarot.strength.name': '8. Strength',
    'tarot.strength.meaning': 'The Strength card is a profound symbol of inner courage, self-mastery, and compassion.',
    'tarot.strength.link': 'https://tarotreadingcard.online/tarot-card-meanings/8-strength-tarot-card-meanings-free-tarot-reading/',
    'tarot.hermit.name': '9. The Hermit',
    'tarot.hermit.meaning': 'The Hermit is a card of introspection and inner guidance, symbolizing the journey inward to seek wisdom and understanding.',
    'tarot.hermit.link': 'https://tarotreadingcard.online/tarot-card-meanings/9-the-hermit-tarot-card-meanings-free-tarot-reading/',
    'tarot.wheelOfFortune.name': '10. Wheel of Fortune',
    'tarot.wheelOfFortune.meaning': 'The Wheel of Fortune is a card of cycles, fate, and the ever-changing nature of life, symbolizing the ups and downs we all experience.',
    'tarot.wheelOfFortune.link': 'https://tarotreadingcard.online/tarot-card-meanings/10-wheel-of-fortune-tarot-card-meanings-free-tarot-reading/',
    'tarot.justice.name': '11. Justice',
    'tarot.justice.meaning': 'Justice is a card that represents fairness, truth, and the pursuit of balance.',
    'tarot.justice.link': 'https://tarotreadingcard.online/tarot-card-meanings/11-justice-tarot-card-meanings-free-tarot-reading/',
    'tarot.hangedMan.name': '12. The Hanged Man',
    'tarot.hangedMan.meaning': 'The Hanged Man is a card of surrender, sacrifice, and new perspectives, representing the transformative power of letting go and seeing things from a different angle.',
    'tarot.hangedMan.link': 'https://tarotreadingcard.online/tarot-card-meanings/12-the-hanged-man-tarot-card-meanings-free-tarot-reading/',
    'tarot.death.name': '13. Death',
    'tarot.death.meaning': 'The Death card is a powerful symbol of transformation and rebirth, representing the cycles of life, endings, and new beginnings.',
    'tarot.death.link': 'https://tarotreadingcard.online/tarot-card-meanings/13-death-tarot-card-meanings-free-tarot-reading/',
    'tarot.temperance.name': '14. Temperance',
    'tarot.temperance.meaning': 'Temperance is a card of balance, harmony, and moderation, symbolizing the art of blending opposites to achieve a greater whole.',
    'tarot.temperance.link': 'https://tarotreadingcard.online/tarot-card-meanings/14-temperance-tarot-card-meanings-free-tarot-reading/',
    'tarot.devil.name': '15. The Devil',
    'tarot.devil.meaning': 'The Devil is a card that embodies temptation, bondage, and the darker aspects of human nature.',
    'tarot.devil.link': 'https://tarotreadingcard.online/tarot-card-meanings/15-the-devil-tarot-card-meanings-free-tarot-reading/',
    'tarot.tower.name': '16. The Tower',
    'tarot.tower.meaning': 'The Tower is a card of upheaval and revelation, symbolizing sudden change and the breaking down of old structures.',
    'tarot.tower.link': 'https://tarotreadingcard.online/tarot-card-meanings/16-the-tower-tarot-card-meanings-free-tarot-reading/',
    'tarot.star.name': '17. The Star',
    'tarot.star.meaning': 'The Star is a card of hope, inspiration, and serenity, symbolizing a sense of renewal and the promise of brighter days ahead.',
    'tarot.star.link': 'https://tarotreadingcard.online/tarot-card-meanings/17-the-star-tarot-card-meanings-free-tarot-reading/',
    'tarot.moon.name': '18. The Moon',
    'tarot.moon.meaning': 'The Moon is a card of intuition, illusion, and the subconscious, representing the journey through the unknown and the exploration of hidden aspects of the self.',
    'tarot.moon.link': 'https://tarotreadingcard.online/tarot-card-meanings/18-the-moon-tarot-card-meanings-free-tarot-reading/',
    'tarot.sun.name': '19. The Sun',
    'tarot.sun.meaning': 'The Sun is a card of joy, success, and vitality, embodying the warmth and illumination that comes from positive energy and clarity.',
    'tarot.sun.link': 'https://tarotreadingcard.online/tarot-card-meanings/19-the-sun-tarot-card-meanings-free-tarot-reading/',
    'tarot.judgement.name': '20. Judgement',
    'tarot.judgement.meaning': 'Judgement is a card of renewal, rebirth, and awakening, symbolizing a profound transformation and a call to evaluate one\'s life choices.',
    'tarot.judgement.link': 'https://tarotreadingcard.online/tarot-card-meanings/20-judgement-tarot-card-meanings-free-tarot-reading/',
    'tarot.world.name': '21. The World',
    'tarot.world.meaning': 'The World is a card of completion, fulfillment, and harmony, representing the culmination of a journey and the achievement of one\'s goals.',
    'tarot.world.link': 'https://tarotreadingcard.online/tarot-card-meanings/21-the-world-tarot-card-meanings-free-tarot-reading/',

    // Wands (Sample cards)
    'tarot.aceOfWands.name': 'Ace of Wands',
    'tarot.aceOfWands.meaning': 'The Ace of Wands is a card of inspiration, new beginnings, and creative potential, symbolizing the spark of enthusiasm that ignites action and innovation.',
    'tarot.aceOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/ace-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfWands.name': '2 of Wands',
    'tarot.twoOfWands.meaning': 'The 2 of Wands is a card of exploration and future planning, symbolizing the moment of choice and the contemplation of one\'s path ahead.',
    'tarot.twoOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/2-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfWands.name': '3 of Wands',
    'tarot.threeOfWands.meaning': 'The 3 of Wands is a card that represents exploration, expansion, and foresight, symbolizing the anticipation of future opportunities and the potential for growth.',
    'tarot.threeOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/3-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfWands.name': '4 of Wands',
    'tarot.fourOfWands.meaning': 'The 4 of Wands is a card that celebrates harmony, celebration, and the joy of community.',
    'tarot.fourOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/4-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfWands.name': '5 of Wands',
    'tarot.fiveOfWands.meaning': 'The 5 of Wands is a card that represents conflict, competition, and the challenges that arise when differing opinions clash.',
    'tarot.fiveOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/5-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfWands.name': '6 of Wands',
    'tarot.sixOfWands.meaning': 'The 6 of Wands is a card of victory, recognition, and achievement, symbolizing the triumph that comes from hard work and perseverance.',
    'tarot.sixOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/6-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfWands.name': '7 of Wands',
    'tarot.sevenOfWands.meaning': 'The 7 of Wands is a card of courage and perseverance, symbolizing the challenges that arise when standing one\'s ground and defending beliefs.',
    'tarot.sevenOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/7-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfWands.name': '8 of Wands',
    'tarot.eightOfWands.meaning': 'The 8 of Wands is a card of swift movement and rapid progress, representing the energy of momentum and the swift unfolding of events.',
    'tarot.eightOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/8-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfWands.name': '9 of Wands',
    'tarot.nineOfWands.meaning': 'The 9 of Wands is a card of resilience and perseverance, representing the strength to endure through challenges and the determination to protect one\'s hard-earned achievements.',
    'tarot.nineOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/9-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfWands.name': '10 of Wands',
    'tarot.tenOfWands.meaning': 'The 10 of Wands is a card that represents burden, responsibility, and the weight of obligations.',
    'tarot.tenOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/10-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfWands.name': 'Page of Wands',
    'tarot.pageOfWands.meaning': 'The Page of Wands is a card of inspiration and enthusiasm, embodying the spirit of adventure and the pursuit of new ideas.',
    'tarot.pageOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/page-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfWands.name': 'Knight of Wands',
    'tarot.knightOfWands.meaning': 'The Knight of Wands is a card of boldness, adventure, and dynamic energy, embodying the spirit of movement and exploration.',
    'tarot.knightOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/knight-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfWands.name': 'Queen of Wands',
    'tarot.queenOfWands.meaning': 'The Queen of Wands is a card of warmth, intuition, and vibrant energy, symbolizing the embodiment of confidence and charisma.',
    'tarot.queenOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/queen-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfWands.name': 'King of Wands',
    'tarot.kingOfWands.meaning': 'The King of Wands is a card of leadership, vision, and charisma, symbolizing the confident pursuit of goals and the ability to inspire others.',
    'tarot.kingOfWands.link': 'https://tarotreadingcard.online/tarot-card-meanings/king-of-wands-tarot-card-meanings-free-tarot-reading/',

    // Cups
    'tarot.aceOfCups.name': 'Ace of Cups',
    'tarot.aceOfCups.meaning': 'The Ace of Cups is a card of emotional fulfillment and new beginnings in love and relationships, symbolizing the overflowing potential of the heart.',
    'tarot.aceOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/ace-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfCups.name': '2 of Cups',
    'tarot.twoOfCups.meaning': 'The 2 of Cups is a card that embodies the essence of love, partnership, and emotional connection.',
    'tarot.twoOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/2-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfCups.name': '3 of Cups',
    'tarot.threeOfCups.meaning': 'The 3 of Cups is a card that celebrates community, friendship, and joyous reunions.',
    'tarot.threeOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/3-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfCups.name': '4 of Cups',
    'tarot.fourOfCups.meaning': 'The 4 of Cups is a card that represents contemplation, reevaluation, and the need for introspection.',
    'tarot.fourOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/4-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfCups.name': '5 of Cups',
    'tarot.fiveOfCups.meaning': 'The 5 of Cups is a card steeped in themes of loss, grief, and reflection, symbolizing the emotional turmoil that can follow disappointment.',
    'tarot.fiveOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/5-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfCups.name': '6 of Cups',
    'tarot.sixOfCups.meaning': 'The 6 of Cups is a card that evokes nostalgia, innocence, and the joy of childhood memories, representing a return to simpler times and the blessings of past experiences.',
    'tarot.sixOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/6-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfCups.name': '7 of Cups',
    'tarot.sevenOfCups.meaning': 'The 7 of Cups is a card of choices, dreams, and illusions, representing the myriad options and possibilities that life presents.',
    'tarot.sevenOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/7-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfCups.name': '8 of Cups',
    'tarot.eightOfCups.meaning': 'The 8 of Cups is a card that represents the journey of emotional growth and the pursuit of deeper fulfillment.',
    'tarot.eightOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/8-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfCups.name': '9 of Cups',
    'tarot.nineOfCups.meaning': 'The 9 of Cups is often referred to as the wish card, representing emotional fulfillment, satisfaction, and the realization of desires.',
    'tarot.nineOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/9-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfCups.name': '10 of Cups',
    'tarot.tenOfCups.meaning': 'The 10 of Cups is a card that embodies emotional fulfillment, happiness, and harmony in relationships.',
    'tarot.tenOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/10-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfCups.name': 'Page of Cups',
    'tarot.pageOfCups.meaning': 'The Page of Cups is a card of youthful creativity and emotional exploration, embodying the spirit of imagination and intuition.',
    'tarot.pageOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/page-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfCups.name': 'Knight of Cups',
    'tarot.knightOfCups.meaning': 'The Knight of Cups is a card of romance, intuition, and emotional exploration, representing the quest for deeper connections and the pursuit of dreams.',
    'tarot.knightOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/knight-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfCups.name': 'Queen of Cups',
    'tarot.queenOfCups.meaning': 'The Queen of Cups is a card of emotional depth, intuition, and nurturing energy, representing the compassionate and caring aspects of the feminine psyche.',
    'tarot.queenOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/queen-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfCups.name': 'King of Cups',
    'tarot.kingOfCups.meaning': 'The King of Cups is a card of emotional balance, compassion, and wisdom, representing the mastery of the heart and the ability to navigate complex feelings with grace.',
    'tarot.kingOfCups.link': 'https://tarotreadingcard.online/tarot-card-meanings/king-of-cups-tarot-card-meanings-free-tarot-reading/',

    // Swords
    'tarot.aceOfSwords.name': 'Ace of Swords',
    'tarot.aceOfSwords.meaning': 'The Ace of Swords is a card of clarity, truth, and intellectual power, representing the dawn of new ideas and the potential for breakthrough moments.',
    'tarot.aceOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/ace-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfSwords.name': '2 of Swords',
    'tarot.twoOfSwords.meaning': 'The 2 of Swords represents a state of indecision and inner conflict, symbolizing the struggle between two opposing choices or viewpoints.',
    'tarot.twoOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/2-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfSwords.name': '3 of Swords',
    'tarot.threeOfSwords.meaning': 'The 3 of Swords is a card that embodies emotional pain, heartbreak, and the complexities of human relationships.',
    'tarot.threeOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/3-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfSwords.name': '4 of Swords',
    'tarot.fourOfSwords.meaning': 'The 4 of Swords is a card of rest, recovery, and contemplation, representing the need for a pause in the midst of life\'s challenges.',
    'tarot.fourOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/4-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfSwords.name': '5 of Swords',
    'tarot.fiveOfSwords.meaning': 'The 5 of Swords is a card that embodies conflict, tension, and the complexities of human interactions.',
    'tarot.fiveOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/5-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfSwords.name': '6 of Swords',
    'tarot.sixOfSwords.meaning': 'The 6 of Swords is a card that symbolizes transition, movement, and the journey towards healing and clarity.',
    'tarot.sixOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/6-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfSwords.name': '7 of Swords',
    'tarot.sevenOfSwords.meaning': 'The 7 of Swords is a card that embodies strategy, cunning, and the art of deception.',
    'tarot.sevenOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/7-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfSwords.name': '8 of Swords',
    'tarot.eightOfSwords.meaning': 'The 8 of Swords is a card that embodies feelings of restriction, confusion, and entrapment.',
    'tarot.eightOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/8-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfSwords.name': '9 of Swords',
    'tarot.nineOfSwords.meaning': 'The 9 of Swords is a card deeply rooted in anxiety, fear, and the weight of overwhelming thoughts.',
    'tarot.nineOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/9-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfSwords.name': '10 of Swords',
    'tarot.tenOfSwords.meaning': 'The 10 of Swords is a card that represents endings, betrayal, and the finality of a difficult situation.',
    'tarot.tenOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/10-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfSwords.name': 'Page of Swords',
    'tarot.pageOfSwords.meaning': 'The Page of Swords is a card of curiosity, intellect, and youthful energy, symbolizing the pursuit of knowledge and truth.',
    'tarot.pageOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/page-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfSwords.name': 'Knight of Swords',
    'tarot.knightOfSwords.meaning': 'The Knight of Swords is a card that embodies intellect, action, and the pursuit of truth, often representing a swift and decisive approach to challenges.',
    'tarot.knightOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/knight-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfSwords.name': 'Queen of Swords',
    'tarot.queenOfSwords.meaning': 'The Queen of Swords is a card of intellect, clarity, and independence, representing the strength found in wisdom and the ability to discern truth from illusion.',
    'tarot.queenOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/queen-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfSwords.name': 'King of Swords',
    'tarot.kingOfSwords.meaning': 'The King of Swords is a card of intellect, authority, and clear communication, embodying the qualities of a wise leader and strategist.',
    'tarot.kingOfSwords.link': 'https://tarotreadingcard.online/tarot-card-meanings/king-of-swords-tarot-card-meanings-free-tarot-reading/',

    // Pentacles
    'tarot.aceOfPentacles.name': 'Ace of Pentacles',
    'tarot.aceOfPentacles.meaning': 'The Ace of Pentacles is a card of new beginnings and opportunities, representing the potential for wealth, prosperity, and material success.',
    'tarot.aceOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/ace-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfPentacles.name': '2 of Pentacles',
    'tarot.twoOfPentacles.meaning': 'The 2 of Pentacles represents balance, adaptability, and the juggling of multiple responsibilities.',
    'tarot.twoOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/2-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfPentacles.name': '3 of Pentacles',
    'tarot.threeOfPentacles.meaning': 'The 3 of Pentacles is a card that represents collaboration, skill, and the value of teamwork in the pursuit of common goals.',
    'tarot.threeOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/3-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfPentacles.name': '4 of Pentacles',
    'tarot.fourOfPentacles.meaning': 'The 4 of Pentacles is a card that represents stability, security, and the desire to hold on tightly to what one has.',
    'tarot.fourOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/4-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfPentacles.name': '5 of Pentacles',
    'tarot.fiveOfPentacles.meaning': 'The 5 of Pentacles is a card that speaks to themes of hardship, struggle, and the experience of feeling left out in the cold.',
    'tarot.fiveOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/5-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfPentacles.name': '6 of Pentacles',
    'tarot.sixOfPentacles.meaning': 'The 6 of Pentacles is a card that embodies the themes of generosity, balance, and the cyclical nature of giving and receiving.',
    'tarot.sixOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/6-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfPentacles.name': '7 of Pentacles',
    'tarot.sevenOfPentacles.meaning': 'The 7 of Pentacles is a card that embodies patience, assessment, and the evaluation of progress.',
    'tarot.sevenOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/7-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfPentacles.name': '8 of Pentacles',
    'tarot.eightOfPentacles.meaning': 'The 8 of Pentacles is a card that signifies dedication, craftsmanship, and the pursuit of mastery in one\'s work.',
    'tarot.eightOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/8-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfPentacles.name': '9 of Pentacles',
    'tarot.nineOfPentacles.meaning': 'The 9 of Pentacles is a card that embodies self-sufficiency, luxury, and the rewards of hard work.',
    'tarot.nineOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/9-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfPentacles.name': '10 of Pentacles',
    'tarot.tenOfPentacles.meaning': 'The 10 of Pentacles is a card that embodies legacy, stability, and the culmination of wealth and success.',
    'tarot.tenOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/10-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfPentacles.name': 'Page of Pentacles',
    'tarot.pageOfPentacles.meaning': 'The Page of Pentacles is a card that embodies the spirit of ambition, diligence, and the pursuit of knowledge, particularly in matters of the material world.',
    'tarot.pageOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/page-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfPentacles.name': 'Knight of Pentacles',
    'tarot.knightOfPentacles.meaning': 'The Knight of Pentacles is a card that embodies diligence, responsibility, and a strong work ethic.',
    'tarot.knightOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/knight-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfPentacles.name': 'Queen of Pentacles',
    'tarot.queenOfPentacles.meaning': 'The Queen of Pentacles is a card of nurturing, practicality, and abundance, embodying the qualities of a grounded and resourceful individual.',
    'tarot.queenOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/queen-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfPentacles.name': 'King of Pentacles',
    'tarot.kingOfPentacles.meaning': 'The King of Pentacles is a card that embodies stability, abundance, and material success, representing the culmination of hard work and a deep understanding of the physical world.',
    'tarot.kingOfPentacles.link': 'https://tarotreadingcard.online/tarot-card-meanings/king-of-pentacles-tarot-card-meanings-free-tarot-reading/',

    // Reversed Meanings - Major Arcana
    'tarot.fool.reversedMeaning': 'When The Fool appears reversed, it often signifies hesitation, recklessness, or a lack of preparation in the face of new beginnings.',
    'tarot.magician.reversedMeaning': 'When The Magician appears reversed, it often signifies blocked potential, misuse of power, or lack of focus.',
    'tarot.highPriestess.reversedMeaning': 'When The High Priestess appears reversed, it signifies a disconnection from intuition, hidden truths being revealed, or difficulty accessing inner wisdom.',
    'tarot.empress.reversedMeaning': 'When The Empress appears reversed, it signifies creative blocks, neglect, or an imbalance in nurturing energy.',
    'tarot.emperor.reversedMeaning': 'When The Emperor appears reversed, it signifies issues with authority, control, or structure.',
    'tarot.hierophant.reversedMeaning': 'When The Hierophant appears reversed, it signifies rebellion, nonconformity, or a rejection of traditional values and systems.',
    'tarot.lovers.reversedMeaning': 'When The Lovers appears reversed, it signifies disharmony, misalignment, or conflict in relationships or within yourself.',
    'tarot.chariot.reversedMeaning': 'When The Chariot appears reversed, it signifies a lack of control, direction, or focus.',
    'tarot.strength.reversedMeaning': 'When Strength appears reversed, it represents inner weakness, self-doubt, or the misuse of personal power.',
    'tarot.hermit.reversedMeaning': 'The Hermit reversed brings forth a sense of confusion, isolation, or refusing to seek inner guidance.',
    'tarot.wheelOfFortune.reversedMeaning': 'The Wheel of Fortune reversed suggests feeling stuck in cycles of misfortune and lack of control over circumstances.',
    'tarot.justice.reversedMeaning': 'Justice reversed represents unfairness, lack of accountability, or avoiding the consequences of one\'s actions.',
    'tarot.hangedMan.reversedMeaning': 'The Hanged Man reversed indicates resistance to change, impatience, or refusing to see different perspectives.',
    'tarot.death.reversedMeaning': 'Death reversed suggests resistance to change, stagnation, or fear of necessary endings and transformations.',
    'tarot.temperance.reversedMeaning': 'Temperance reversed represents imbalance, excess, or lack of moderation and harmony.',
    'tarot.devil.reversedMeaning': 'The Devil reversed signifies breaking free from bondage, overcoming addictions, or liberation from limiting beliefs.',
    'tarot.tower.reversedMeaning': 'The Tower reversed indicates avoiding necessary change, internal upheaval, or resistance to transformation.',
    'tarot.star.reversedMeaning': 'The Star reversed reflects loss of hope, lack of faith, or disconnection from spiritual guidance.',
    'tarot.moon.reversedMeaning': 'The Moon reversed brings clarity, releasing illusions, or overcoming confusion and fear.',
    'tarot.sun.reversedMeaning': 'The Sun reversed suggests temporary setbacks, lack of enthusiasm, or difficulty seeing the positive side.',
    'tarot.judgement.reversedMeaning': 'Judgement reversed represents avoiding self-reflection, harsh self-criticism, or refusing to learn from past mistakes.',
    'tarot.world.reversedMeaning': 'The World reversed indicates incomplete goals, lack of closure, or seeking external validation for inner fulfillment.',

    // Reversed Meanings - Wands
    'tarot.aceOfWands.reversedMeaning': 'Ace of Wands reversed suggests creative blocks, lack of motivation, or delayed new beginnings.',
    'tarot.twoOfWands.reversedMeaning': 'Two of Wands reversed indicates poor planning, lack of personal power, or fear of the unknown.',
    'tarot.threeOfWands.reversedMeaning': 'Three of Wands reversed suggests delays in plans, lack of foresight, or obstacles in expansion.',
    'tarot.fourOfWands.reversedMeaning': 'Four of Wands reversed indicates disrupted celebrations, lack of harmony, or unstable foundations.',
    'tarot.fiveOfWands.reversedMeaning': 'Five of Wands reversed suggests conflict avoidance, inner conflict, or resolution of disputes.',
    'tarot.sixOfWands.reversedMeaning': 'Six of Wands reversed indicates lack of recognition, private achievement, or fallen ego.',
    'tarot.sevenOfWands.reversedMeaning': 'Seven of Wands reversed suggests giving up, overwhelm, or lack of confidence in defending your position.',
    'tarot.eightOfWands.reversedMeaning': 'Eight of Wands reversed indicates delays, frustration, or lack of progress and momentum.',
    'tarot.nineOfWands.reversedMeaning': 'Nine of Wands reversed suggests paranoia, stubbornness, or refusing help when needed.',
    'tarot.tenOfWands.reversedMeaning': 'Ten of Wands reversed indicates delegating burdens, releasing responsibilities, or finding easier ways.',
    'tarot.pageOfWands.reversedMeaning': 'Page of Wands reversed suggests lack of direction, impatience, or unfocused creative energy.',
    'tarot.knightOfWands.reversedMeaning': 'Knight of Wands reversed indicates recklessness, impatience, or destructive impulsiveness.',
    'tarot.queenOfWands.reversedMeaning': 'Queen of Wands reversed suggests jealousy, insecurity, or using manipulation to control others.',
    'tarot.kingOfWands.reversedMeaning': 'King of Wands reversed indicates arrogance, controlling behavior, or misuse of leadership power.',

    // Reversed Meanings - Cups
    'tarot.aceOfCups.reversedMeaning': 'Ace of Cups reversed signifies emotional blocks, repressed feelings, or difficulty expressing emotions.',
    'tarot.twoOfCups.reversedMeaning': 'Two of Cups reversed indicates relationship problems, disconnection, or imbalanced partnerships.',
    'tarot.threeOfCups.reversedMeaning': 'Three of Cups reversed suggests social isolation, gossip, or superficial friendships.',
    'tarot.fourOfCups.reversedMeaning': 'Four of Cups reversed indicates missed opportunities, regret, or finally seeing new possibilities.',
    'tarot.fiveOfCups.reversedMeaning': 'Five of Cups reversed suggests recovery from loss, forgiveness, or moving on from disappointment.',
    'tarot.sixOfCups.reversedMeaning': 'Six of Cups reversed indicates living in the past, being stuck in nostalgia, or childhood issues.',
    'tarot.sevenOfCups.reversedMeaning': 'Seven of Cups reversed suggests clarity of vision, making realistic choices, or overcoming illusions.',
    'tarot.eightOfCups.reversedMeaning': 'Eight of Cups reversed indicates fear of change, staying in unsatisfying situations, or avoiding growth.',
    'tarot.nineOfCups.reversedMeaning': 'Nine of Cups reversed suggests inner dissatisfaction, materialism, or unrealistic expectations.',
    'tarot.tenOfCups.reversedMeaning': 'Ten of Cups reversed indicates family problems, broken relationships, or lack of harmony at home.',
    'tarot.pageOfCups.reversedMeaning': 'Page of Cups reversed suggests emotional immaturity, moodiness, or creative blocks.',
    'tarot.knightOfCups.reversedMeaning': 'Knight of Cups reversed indicates moodiness, manipulation, or unrealistic romantic ideals.',
    'tarot.queenOfCups.reversedMeaning': 'Queen of Cups reversed suggests emotional instability, codependency, or being overly sensitive.',
    'tarot.kingOfCups.reversedMeaning': 'King of Cups reversed indicates emotional manipulation, moodiness, or suppressed emotions.',

    // Reversed Meanings - Swords
    'tarot.aceOfSwords.reversedMeaning': 'Ace of Swords reversed suggests mental confusion, lack of clarity, or misuse of intellectual power.',
    'tarot.twoOfSwords.reversedMeaning': 'Two of Swords reversed indicates decision-making, information overload, or avoiding difficult choices.',
    'tarot.threeOfSwords.reversedMeaning': 'Three of Swords reversed suggests healing from heartbreak, forgiveness, or releasing emotional pain.',
    'tarot.fourOfSwords.reversedMeaning': 'Four of Swords reversed indicates restlessness, insomnia, or avoiding necessary rest and reflection.',
    'tarot.fiveOfSwords.reversedMeaning': 'Five of Swords reversed suggests reconciliation, making amends, or learning from past conflicts.',
    'tarot.sixOfSwords.reversedMeaning': 'Six of Swords reversed indicates being stuck in the past, rough transitions, or resistance to change.',
    'tarot.sevenOfSwords.reversedMeaning': 'Seven of Swords reversed suggests coming clean, confessing, or taking responsibility for deceptions.',
    'tarot.eightOfSwords.reversedMeaning': 'Eight of Swords reversed indicates self-liberation, breaking free from mental restrictions, or finding solutions.',
    'tarot.nineOfSwords.reversedMeaning': 'Nine of Swords reversed suggests recovery from anxiety, hope returning, or overcoming nightmares.',
    'tarot.tenOfSwords.reversedMeaning': 'Ten of Swords reversed indicates recovery, regeneration, or the worst being over.',
    'tarot.pageOfSwords.reversedMeaning': 'Page of Swords reversed suggests all talk no action, haste, or unfocused mental energy.',
    'tarot.knightOfSwords.reversedMeaning': 'Knight of Swords reversed indicates reckless behavior, impatience, or lack of direction.',
    'tarot.queenOfSwords.reversedMeaning': 'Queen of Swords reversed suggests cold-heartedness, bitterness, or harsh judgment.',
    'tarot.kingOfSwords.reversedMeaning': 'King of Swords reversed indicates abuse of power, manipulation, or tyrannical behavior.',

    // Reversed Meanings - Pentacles
    'tarot.aceOfPentacles.reversedMeaning': 'Ace of Pentacles reversed suggests missed opportunities, poor financial decisions, or lack of planning.',
    'tarot.twoOfPentacles.reversedMeaning': 'Two of Pentacles reversed indicates lack of balance, poor time management, or financial instability.',
    'tarot.threeOfPentacles.reversedMeaning': 'Three of Pentacles reversed suggests lack of teamwork, poor collaboration, or working alone.',
    'tarot.fourOfPentacles.reversedMeaning': 'Four of Pentacles reversed indicates overspending, generosity, or releasing control over material possessions.',
    'tarot.fiveOfPentacles.reversedMeaning': 'Five of Pentacles reversed suggests recovery from hardship, accepting help, or improving financial situation.',
    'tarot.sixOfPentacles.reversedMeaning': 'Six of Pentacles reversed indicates selfishness, strings attached, or unequal giving and receiving.',
    'tarot.sevenOfPentacles.reversedMeaning': 'Seven of Pentacles reversed suggests impatience, lack of reward, or questioning your investments.',
    'tarot.eightOfPentacles.reversedMeaning': 'Eight of Pentacles reversed indicates lack of focus, poor craftsmanship, or seeking shortcuts.',
    'tarot.nineOfPentacles.reversedMeaning': 'Nine of Pentacles reversed suggests financial dependence, lack of self-discipline, or superficial wealth.',
    'tarot.tenOfPentacles.reversedMeaning': 'Ten of Pentacles reversed indicates family disputes, financial loss, or lack of long-term security.',
    'tarot.pageOfPentacles.reversedMeaning': 'Page of Pentacles reversed suggests lack of progress, procrastination, or unrealistic financial goals.',
    'tarot.knightOfPentacles.reversedMeaning': 'Knight of Pentacles reversed indicates laziness, obsessiveness, or being stuck in routine.',
    'tarot.queenOfPentacles.reversedMeaning': 'Queen of Pentacles reversed suggests financial insecurity, neglecting self-care, or being overly materialistic.',
    'tarot.kingOfPentacles.reversedMeaning': 'King of Pentacles reversed indicates greed, corruption, or obsession with material wealth.',

    // Positions
    'tarot.position.upright': 'upright',
    'tarot.position.reversed': 'reversed'
  },
  
  ja: {
    // Language Switcher
    'lang.current': '言語',
    
    // Navigation  
    'nav.home': 'ホーム',
    'nav.meanings': '意味',
    'nav.spreads': 'スプレッド', 
    'nav.diary': 'マイ日記',
    'nav.about': 'について',
    'nav.signin': 'サインイン',
    'nav.signup': 'サインアップ',
    
    // User Menu
    'user.profileSettings': 'プロフィール設定',
    'user.signOut': 'サインアウト',
    
    // Profile Page
    'profile.title': 'プロフィール設定',
    'profile.subtitle': '個人情報とアカウント設定を管理',
    'profile.loading': '読み込み中...',
    'profile.tabProfile': 'プロフィール情報',
    'profile.tabPassword': 'パスワード変更',
    'profile.personalInfo': '個人情報',
    'profile.firstName': '名前',
    'profile.lastName': '姓',
    'profile.firstNamePlaceholder': '名前を入力してください',
    'profile.lastNamePlaceholder': '姓を入力してください',
    'profile.emailAddress': 'メールアドレス',
    'profile.emailCannotChange': 'メールアドレスは変更できません',
    'profile.saveChanges': '変更を保存',
    'profile.saving': '保存中...',
    'profile.changePasswordTitle': 'パスワード変更',
    'profile.currentPassword': '現在のパスワード',
    'profile.newPassword': '新しいパスワード',
    'profile.confirmPassword': '新しいパスワード（確認）',
    'profile.currentPasswordPlaceholder': '現在のパスワードを入力',
    'profile.newPasswordPlaceholder': '新しいパスワードを入力',
    'profile.confirmPasswordPlaceholder': '新しいパスワードを再入力',
    'profile.passwordRequirements': 'パスワードは8文字以上で、大文字、小文字、数字、特殊文字を含む必要があります',
    'profile.passwordsDoNotMatch': 'パスワードが一致しません',
    'profile.changePasswordButton': 'パスワードを変更',
    'profile.changing': '変更中...',
    'profile.profileUpdated': 'プロフィールが正常に更新されました！',
    'profile.passwordChanged': 'パスワードが正常に変更されました！',
    
    // Homepage
    'home.title': 'TAROCA - 無料タロット占い',
    'home.subtitle': '人生の課題を成長の機会に変える',
    'home.drawCard': 'カードを引く',
    'home.dailyCard': '今日のタロットカード',
    'home.categoriesTitle': 'タロット無料占いとスプレッドの分類',
    'home.categoriesDescription': 'あなたの現在のニーズと質問に共鳴するカテゴリーを選択してください',
    'home.sampleQuestions': 'サンプル質問：',
    'home.getStarted': '始める',
    
    // Categories
    'category.love': '恋愛タロット・人間関係',
    'category.career': '仕事・目的', 
    'category.growth': '個人的成長',
    'category.health': '健康',
    'category.loveDesc': '心と繋がりの問題を探る',
    'category.careerDesc': 'あなたの職業的な旅路への導き',
    'category.growthDesc': 'あなたの精神的進化への洞察',
    'category.healthDesc': '身体、心、魂の健康',
    
    // Category Questions
    'questions.love1': '恋愛関係を改善するには？',
    'questions.love2': '今日の私にとって愛とは何を意味するか？',
    'questions.love3': '自分自身をもっと愛するには？',
    'questions.career1': '私の魂に合うキャリアパスは？',
    'questions.career2': '仕事でもっと充実感を見つけるには？',
    'questions.career3': 'どのようなスキルを伸ばすべきか？',
    'questions.growth1': '私が学ぶべき教訓は何か？',
    'questions.growth2': '最高の自分になるには？',
    'questions.growth3': '解放すべき制限的な信念は何か？',
    'questions.health1': '体の知恵にアクセスするには？',
    'questions.health2': '古い傷を癒すには？',
    'questions.health3': '真の平和をもたらすものは何か？',
    
    // Inspirational Questions
    'inspire.question1': '今日あなたの魂が聞く必要があることは何ですか？',
    'inspire.question2': '今週受け入れるべきエネルギーは何ですか？',
    'inspire.question3': '宇宙があなたに教えようとしている教訓は何ですか？',
    'inspire.question4': '今日活用できる隠れた強さは何ですか？',
    'inspire.question5': '前進するために手放す必要があるものは何ですか？',
    'inspire.question6': 'あなたの注意を待っている機会は何ですか？',
    'inspire.question7': 'あなたが既に持っている知恵は何ですか？',
    'inspire.question8': 'あなたの一日を変える新しい視点は何ですか？',
    'inspire.question9': 'あなたの心が伝えたいメッセージは何ですか？',
    'inspire.question10': 'あなたの人生に既に存在する魔法は何ですか？',
    
    // General Reading Section
    'home.notSureCategory': 'どのカテゴリーが適しているかわからない？',
    'home.generalReadingDesc': '柔軟な一般的な占いスプレッドでパーソナライズされたガイダンスを受けましょう',
    'home.createOwnReading': 'あなた独自の占いを作る',
    
    // Growth Section
    'home.whyTaroca': 'なぜTAROCAなのか？',
    'home.growthTitle': 'リアルに感じ、深く学び、成長を助けるタロット体験',
    'home.growthDesc': 'TAROCAは本格的なタロット儀式の魔法と、ガイド付きの洞察と個人的な反省を組み合わせています — すべての占いがあなたの旅路の一歩となるように。',
    
    // Features
    'home.featureCategory1': '単なる偶然ではなく、直感で選ぶ',
    'home.featureTitle1': '本物の儀式のように引く',
    'home.featureDesc1': 'タロットが本来あるべき姿で体験してください — 神秘的な円やファン状に配置されたカードが、あなたの手による選択を待っています。各引きは本物のタロットテーブルに座っているかのように本格的に感じられます。',
    'home.featureCategory2': '単なる意味以上 — 真の理解',
    'home.featureTitle2': '深く学び探求する',
    'home.featureDesc2': '正位置と逆位置の意味、象徴的な洞察、恋愛・キャリア・自己成長のための事前構築されたスプレッドを含む完全なタロットカードライブラリにアクセスしてください。答えを求めている場合でも、ただ学んでいる場合でも、TAROCAが明確にガイドします。',
    'home.featureCategory3': '占い結果を人生の教訓に変える',
    'home.featureTitle3': '反省し、保存し、成長する',
    'home.featureDesc3': 'あなたが引くすべてのスプレッドは、個人的なタロット日記に保存できます。ガイド付きのプロンプトで反省し、繰り返し出るカードを追跡し、あなたの引きに合わせた建設的な提案を確認 — パターン、教訓、進歩を時間をかけて見ることができます。',
    
    // Diary Section
    'home.diaryTitle': 'あなたのタロット日記',
    'home.diaryDesc': '個人的なタロット日記でスピリチュアルな旅を記録しましょう。占い結果を保存し、洞察を振り返り、直感が時間をかけて成長するのを見守りましょう。',
    'home.diaryFeature1': '占い結果を保存・整理',
    'home.diaryFeature2': 'パターンと洞察を追跡',
    'home.diaryFeature3': 'スピリチュアルな成長をモニター',
    'home.viewDiary': '日記を見る',
    
    // Diary Page
    'diary.title': 'マイタロット日記',
    'diary.subtitle': 'あなた個人のタロット占い結果と洞察のコレクション',
    'diary.signInRequired': 'サインインが必要です',
    'diary.signInMessage': 'タロット日記にアクセスするにはサインインしてください',
    'diary.signIn': 'サインイン',
    'diary.loading': 'タロット日記を読み込み中...',
    'diary.totalReadings': '占い回数',
    'diary.favoriteSpread': 'お気に入りのスプレッド',
    'diary.filterAll': '全てのスプレッド',
    'diary.filterLove': '恋愛',
    'diary.filterCareer': '仕事運',
    'diary.filterPersonal': '自己成長',
    'diary.filterMental': '心の癒し',
    'diary.filterSingle': '今日のタロットカード',
    'diary.noReadings': 'まだ占い結果がありません',
    'diary.emptyMessage': 'タロット日記が空です。タロットスプレッドを完了して保存し、コレクションを始めましょう！',
    'diary.importReading': '占い結果をインポート',
    'diary.deleteConfirm': 'この占い結果を削除してもよろしいですか？',
    'diary.deleteReading': '占い結果を削除',
    'diary.generateAI': 'TAROCA 洞察を生成',
    'diary.generatingAI': 'TAROCA 占い...',
    'diary.aiInsight': 'TAROCAの洞察:',
    'diary.question': '啓示:',
    'diary.notes': 'ノート:',
    'diary.cardsDrawn': '枚のカードを引いた',
    'diary.untitledReading': '無題の占い',
    'diary.moreCards': '枚以上',
    'diary.previous': '前へ',
    'diary.next': '次へ',
    'diary.page': 'ページ',
    'diary.of': '/',
    'diary.refreshing': '更新中...',
    
    // Reading Detail Page
    'readingDetail.loading': '占い結果を読み込み中...',
    'readingDetail.notFound': '占い結果が見つかりません',
    'readingDetail.backToDiary': '日記に戻る',
    'readingDetail.editNotes': 'ノートを編集',
    'readingDetail.save': '保存',
    'readingDetail.cancel': 'キャンセル',
    'readingDetail.defaultTitle': 'タロット占い詳細',
    'readingDetail.questionAsked': '質問した内容:',
    'readingDetail.yourNotes': 'あなたのノート:',
    'readingDetail.questionPlaceholder': 'どのような質問をしましたか？',
    'readingDetail.notesPlaceholder': 'あなたの考え、解釈、洞察を追加してください...',
    'readingDetail.askTarocaTitle': 'TAROCAにより深い洞察を求める',
    'readingDetail.askTarocaDesc': 'すべてのカードを意味のあるストーリーと実行可能な次のステップに結びつける個人的なガイダンスを得られます。',
    'readingDetail.benefit1': 'スプレッド全体の詳細な解釈',
    'readingDetail.benefit2': 'あなたの質問に合わせた実践的なガイダンス',
    'readingDetail.benefit3': 'カードの関係性とパターンの洞察',
    'readingDetail.benefit4': '次のステップへの実行可能なアドバイス',
    'readingDetail.askTarocaBtn': 'TAROCAに聞く - ¥500',
    'readingDetail.tarocaRevealing': 'TAROCAが明らかにしています...',
    'readingDetail.tarocaExplanation': '✨ TAROCAの解説',
    'readingDetail.aiError': 'エラー:',
    'readingDetail.learnMore': '📖 このカードについて詳しく学ぶ',
    
    // Spreads
    'spread.love': '恋愛占い',
    'spread.career': '仕事運占い',
    'spread.personal': '自己成長占い', 
    'spread.mental': '心の癒し占い',
    'spread.general': '一般的な占い',
    'spread.chooseCards': 'カード数を選択',
    'spread.askQuestion': '質問をしてください（任意）',
    'spread.questionPlaceholder': 'あなたの恋愛について何を知りたいですか？',
    'spread.startReading': 'タロット占いを始める',
    'spread.selectType': 'あなたの状況を選択',
    'spread.welcomePersonal': '無料の成長タロット占い',
    'spread.welcomeHealing': '無料の体調タロット占い',
    'spread.welcomeGeneral': '一般・無料のタロット占い',
    'spread.welcomeLove': '無料の恋愛タロット占い',
    'spread.welcomeCareer': '無料の仕事タロット占い',

    // Footer
    'footer.spreadsHeading': 'タロットスプレッド',
    'footer.learnHeading': 'タロットを学ぶ',
    'footer.companyHeading': 'TAROCA',
    'footer.privacy': 'プライバシーポリシー',
    'footer.terms': '利用規約',
    'footer.disclaimer': 'タロット占いは内省と気づきのためのものです。医療・法律・財務上の助言ではありません。',
    'footer.copyright': '© TAROCA. All rights reserved.',

    // 404 page
    'notFound.title': 'ページが見つかりませんでした',
    'notFound.body': 'お探しのページは移動したか、存在しない可能性があります。新しい占いを始めてみましょう。',
    'notFound.cta': 'タロットスプレッドに戻る',

    // Image alt text
    'alt.demoScreen': 'タロット占いとその解釈を表示しているTAROCAアプリの画面',
    'alt.generalReading': '自分だけのカスタムタロットスプレッドを作成',
    'alt.iconRitual': '毎日のタロットの習慣を表すアイコン',
    'alt.iconInsight': 'タロット占いから得られる気づきを表すアイコン',
    'alt.iconGrowth': 'タロットによる自己成長を表すアイコン',

    // Spread Drawing Page
    'spreadDrawing.customSpread': 'カスタムスプレッド',
    'spreadDrawing.drawingCard': 'カードを引いています...',
    'spreadDrawing.drawCard': 'カードを引く',
    'spreadDrawing.drawAllCards': 'TAROCAが引く',
    'spreadDrawing.drawingCards': 'カードを引いています...',
    'spreadDrawing.of': 'の',
    'spreadDrawing.for': 'のために:',
    'spreadDrawing.newReading': '新しいリーディング',
    'spreadDrawing.saveToDiary': '日記に保存',
    'spreadDrawing.saving': '保存中...',
    'spreadDrawing.tryAgain': '再試行',
    'spreadDrawing.signIn': 'サインイン',
    'spreadDrawing.signUpFree': '無料でサインアップ',
    'spreadDrawing.unlockExperience': '完全なタロット体験をアンロック',
    'spreadDrawing.signInToUnlock': '新規登録したユーザーは無料で1回の通訳を受けることができます(価格: ¥500)！タロットの旅の完全な力をアンロックするにはサインインしてください：',
    'spreadDrawing.signInMessage': 'サインインしてリーディングを保存し、TAROCA の洞察をアンロックしてください!',
    'spreadDrawing.personalDiary': '📖 個人日記',
    'spreadDrawing.personalDiaryDesc': 'リーディングを保存して追跡し、精神的成長のパターンを発見してください',
    'spreadDrawing.progressTracking': '📊 進捗追跡',
    'spreadDrawing.progressTrackingDesc': 'リーディング履歴と精神的旅の統計を表示します',
    'spreadDrawing.joinUsers': 'TAROCA と共に精神的な道を発見する何千人ものユーザーに参加してください',
    'spreadDrawing.failedToSave': 'リーディングの保存に失敗しました。再試行してください。',
    'spreadDrawing.paymentSuccess': '支払いが成功しました！「日記に保存」ボタンを使用してリーディングを手動で保存してください。',
    'spreadDrawing.cardPosition': 'カード',
    'spreadDrawing.personalNotes': '個人的なメモ',
    'spreadDrawing.notesPlaceholder': 'この占いについてあなたの感想や考察を書いてください...',
    'spreadDrawing.findLesson': 'あなたの占いから教訓を見つけ、スプレッドを行動できる明確なガイダンスに変えてください。新規登録したユーザーは無料で1回の通訳を受けることができます！',
    'spreadDrawing.getTarocaWisdom': 'TAROCAの知恵を得る - ¥500',
    'spreadDrawing.tarocaRevealing': 'TAROCAが明かしています...',
    'spreadDrawing.interpretingSpread': 'あなたのスプレッドを解釈し、ガイダンスを準備しています',
    'spreadDrawing.spreadOverview': 'あなたのスプレッド概要',
    'spreadDrawing.learnMoreCard': '📖 このカードについて詳しく学ぶ',
    'spreadDrawing.yourQuestion': 'あなたの質問',
    'spreadDrawing.cardMeaning': '意味:',
    'spreadDrawing.tarocaExplanationTitle': '🔮 TAROCAの解説',

    // Payment Modal
    'payment.unlockWisdom': 'TAROCAの智恵を解放',
    'payment.personalizedInsights': 'あなたのタロット占いにパーソナライズされた洞察を得る',
    'payment.whatYouGet': 'あなたが得るもの：',
    'payment.tarocaInterpretation': 'あなたのスプレッドのTAROCAの解釈',
    'payment.personalizedGuidance': 'あなたの質問に基づくパーソナライズされたガイダンス',
    'payment.actionableInsights': 'あなたの状況に対する実行可能な洞察',
    'payment.savedToDiary': '今後の参考のためにあなたの日記に保存',
    'payment.couponApplied': 'クーポンが適用されました：',
    'payment.save': '保存',
    'payment.haveCoupon': 'クーポンコードをお持ちですか？こちらをクリック',
    'payment.couponCode': 'クーポンコード',
    'payment.enterCoupon': 'クーポンコードを入力',
    'payment.applyCoupon': 'クーポンを適用',
    'payment.applying': '適用中...',
    'payment.cancel': 'キャンセル',
    'payment.securePayment': 'Stripeによる安全な決済',
    'payment.initializing': '安全な決済を初期化中...',
    'payment.processing': '処理中...',
    'payment.pay': '支払い',
    'payment.thankYou': 'お支払いありがとうございます！🎉',
    'payment.paymentSuccessful': '支払い成功！✨ 含まれるもの：',
    'payment.readingSaved': 'あなたの占いが日記に保存されました',
    'payment.accessExplanations': 'TAROCAの解説へのアクセス',
    'payment.nextSteps': '💫 次のステップ：',
    'payment.waitForExplanation': '少々お待ちください。TAROCAの解説があなたの独特な状況に合わせた深いスピリチュアルな洞察を提供します。',
    'payment.viewAllReadings': 'すべての占いを見る',
    'payment.tarocaRevealing': 'TAROCAが明かしています...',
    'payment.viewThisReading': 'この占いを見る',

    // Daily Card Positions
    'dailyCard.position': '今日の運勢',
    'dailyCard.healingPosition': '今日の癒しのガイダンス',
    'dailyCard.lovePosition': '今日の恋愛運',
    'dailyCard.careerPosition': '今日の仕事運',
    'dailyCard.growthPosition': '今日の成長',
    
    // Daily Card Spread Names
    'dailyCard.spreadName': '今日のカード',
    'dailyCard.growthSpreadName': '今日の成長カード',

    // Love Spread
    'love.title': '恋愛・人間関係占い',
    'love.subtitle': 'あなたの恋愛生活と人間関係についての洞察を発見',
    'love.singles': '片思い タロット',
    'love.couple': 'カップル向け',
    'love.singlesDesc': 'ソウルメイトを引き寄せ、見つけるためのガイダンス',
    'love.coupleDesc': '関係を強化し、理解を深める',
    'love.chooseLoveSpread': '恋愛スプレッドを選択',
    'love.selectCardCount': 'あなたの恋愛占いのためのカード枚数を選択してください',
    'love.selectSituation': '恋愛状況を選択',
    'love.askLoveQuestion': '恋愛について質問する',
    'love.drawLoveCard': '恋愛タロット占いを始める',
    'love.loveCardOfDay': '今日の恋愛カード',
    'love.selectRelationship': '関係タイプを選択',
    'love.startLoveReading': '恋愛タロット占いを開始',
    'love.focusAreas': '重点エリア：',
    'love.questionPlaceholder': 'あなたの人間関係について何を知りたいですか？（任意）',
    'love.selfLovePrep': '復縁可能性タロット',
    'love.attractPartner': '恋愛運、次の恋人タロット',
    'love.removeLoveBlocks': '恋愛のブロックを取り除く',
    'love.communication': 'コミュニケーション',
    'love.intimacy': 'タロット結婚相手',
    'love.relationshipGrowth': '相手の気持ち',
    'love.family': '家族タロット',
    'love.familyDesc': '家族関係を修復し調和させる',
    'love.familyDynamics': 'あの人の家庭',
    'love.healingOldWounds': '家族の気持ち',
    'love.creatingHarmony': '調和を生み出す',
    'love.business': 'ビジネス関係のために',
    'love.businessDesc': 'プロフェッショナルなパートナーシップとビジネスのつながり',
    'love.networking': 'ネットワーキング',
    'love.partnerships': '職場恋愛タロット',
    'love.professionalBounds': 'プロフェッショナルの境界',
    'love.reset': '初期化',
    'love.yourLoveGuidance': 'あなたの恋愛ガイダンス',

    // Love Spread Card Positions - Singles (Japanese)
    'loveSpread.singles.1.loveReadiness': '恋愛運',
    'loveSpread.singles.3.pastLessons': '過去',
    'loveSpread.singles.3.currentEnergy': '現在',
    'loveSpread.singles.3.futureLove': '未来',
    'loveSpread.singles.5.yourDesire': 'あなたの願望',
    'loveSpread.singles.5.whatAttractsLove': '愛を引き寄せるもの',
    'loveSpread.singles.5.loveBlocks': '恋愛の障害',
    'loveSpread.singles.5.actionToTake': '取るべき行動',
    'loveSpread.singles.5.loveManifestation': '恋愛の現実化',
    'loveSpread.singles.6.selfLoveStatus': '自己愛の状態',
    'loveSpread.singles.6.whatYouSeek': 'あなたが求めるもの',
    'loveSpread.singles.6.whatYouOffer': 'あなたが提供するもの',
    'loveSpread.singles.6.currentObstacles': '現在の障害',
    'loveSpread.singles.6.divineTiming': '神聖なタイミング',
    'loveSpread.singles.6.loveOutcome': '恋愛の結果',
    'loveSpread.singles.7.rootSecurity': 'ルート - 恋愛の安全性',
    'loveSpread.singles.7.sacralDesires': 'セイクラル - ロマンチックな欲求',
    'loveSpread.singles.7.solarConfidence': 'ソーラープレクサス - 自信',
    'loveSpread.singles.7.heartCapacity': 'ハート - 愛の能力',
    'loveSpread.singles.7.throatExpression': 'スロート - 愛の表現',
    'loveSpread.singles.7.thirdEyeIntuition': 'サードアイ - 恋愛の直感',
    'loveSpread.singles.7.crownConnection': 'クラウン - 魂のつながり',
    "loveSpread.singles.10.loveSituation": "恋愛状況",
    "loveSpread.singles.10.challengeCross": "チャレンジ",
    "loveSpread.singles.10.distantPast": "過去",
    "loveSpread.singles.10.recentPast": "未来",
    "loveSpread.singles.10.possibleOutcome": "望ましい結果",
    "loveSpread.singles.10.nearFuture": "基盤",
    "loveSpread.singles.10.yourApproach": "自分自身",
    "loveSpread.singles.10.externalInfluences": "外部の影響",
    "loveSpread.singles.10.hopesFears": "希望と恐れ",
    "loveSpread.singles.10.finalOutcome": "最終結果",
    'loveSpread.singles.12.janNewLoveEnergy': '1月 - 新しい恋愛エネルギー',
    'loveSpread.singles.12.febSelfLove': '2月 - 自己愛',
    'loveSpread.singles.12.marTakingAction': '3月 - 行動を起こす',
    'loveSpread.singles.12.aprBuildingFoundation': '4月 - 基盤を築く',
    'loveSpread.singles.12.mayAdventure': '5月 - 恋愛の冒険',
    'loveSpread.singles.12.junHarmony': '6月 - 調和',
    'loveSpread.singles.12.julReflection': '7月 - 反省',
    'loveSpread.singles.12.augStrength': '8月 - 強さ',
    'loveSpread.singles.12.sepCompletion': '9月 - 完成',
    'loveSpread.singles.12.octTransformation': '10月 - 変容',
    'loveSpread.singles.12.novInnerWork': '11月 - 内なる作業',
    'loveSpread.singles.12.decLoveManifestation': '12月 - 恋愛の現実化',

    // Love Spread Card Positions - Couple (Japanese)  
    'loveSpread.couple.1.relationshipEnergy': '関係のエネルギー',
    'loveSpread.couple.3.relationshipPast': '過去',
    'loveSpread.couple.3.currentDynamic': '現在',
    'loveSpread.couple.3.futureTogether': '未来',
    'loveSpread.couple.5.youInRelationship': '関係でのあなた',
    'loveSpread.couple.5.yourPartner': 'あなたのパートナー',
    'loveSpread.couple.5.relationshipBond': '関係の絆',
    'loveSpread.couple.5.challengeToAddress': '対処すべき課題',
    'loveSpread.couple.5.relationshipFuture': '関係の未来',
    'loveSpread.couple.6.yourFeelings': 'あなたの感情',
    'loveSpread.couple.6.partnersFeelings': 'パートナーの感情',
    'loveSpread.couple.6.relationshipFoundation': '関係の基盤',
    'loveSpread.couple.6.currentChallenge': '現在の課題',
    'loveSpread.couple.6.whatsNeeded': '必要なこと',
    'loveSpread.couple.6.relationshipPath': '関係の道',
    'loveSpread.couple.7.rootSecurity': 'ルート - 一緒の安全性',
    'loveSpread.couple.7.sacralIntimacy': 'セイクラル - 親密さと情熱',
    'loveSpread.couple.7.solarConfidence': 'ソーラープレクサス - カップルとしての自信',
    'loveSpread.couple.7.heartLove': 'ハート - 愛と思いやり',
    'loveSpread.couple.7.throatCommunication': 'スロート - コミュニケーション',
    'loveSpread.couple.7.thirdEyeVision': 'サードアイ - 共有ビジョン',
    'loveSpread.couple.7.crownBond': 'クラウン - 精神的な絆',
    "loveSpread.couple.10.relationshipStatus": "関係の状況",
    "loveSpread.couple.10.challengeCross": "チャレンジ",
    "loveSpread.couple.10.foundation": "過去",
    "loveSpread.couple.10.recentPast": "未来",
    "loveSpread.couple.10.possibleOutcome": "望ましい結果",
    "loveSpread.couple.10.nearFuture": "基盤",
    "loveSpread.couple.10.yourRole": "自分自身",
    "loveSpread.couple.10.externalInfluences": "外部の影響",
    "loveSpread.couple.10.hopesFears": "希望と恐れ",
    "loveSpread.couple.10.relationshipFuture": "関係の未来",
    'loveSpread.couple.12.janNewBeginnings': '1月',
    'loveSpread.couple.12.febGrowthTogether': '2月',
    'loveSpread.couple.12.marTakingAction': '3月',
    'loveSpread.couple.12.aprStability': '4月',
    'loveSpread.couple.12.mayAdventure': '5月',
    'loveSpread.couple.12.junHarmony': '6月',
    'loveSpread.couple.12.julReflection': '7月',
    'loveSpread.couple.12.augStrength': '8月',
    'loveSpread.couple.12.sepCompletion': '9月',
    'loveSpread.couple.12.octChange': '10月',
    'loveSpread.couple.12.novDeepWork': '11月',
    'loveSpread.couple.12.decCelebration': '12月',

    // Love Spread Card Positions - Family (Japanese)
    'loveSpread.family.1.familyEnergy': '家族のエネルギー',
    'loveSpread.family.3.familyPast': '過去',
    'loveSpread.family.3.currentDynamic': '現在',
    'loveSpread.family.3.familyFuture': '未来',
    'loveSpread.family.5.yourRole': 'あなたの役割',
    'loveSpread.family.5.familyFoundation': '家族の基盤',
    'loveSpread.family.5.currentChallenge': '現在の課題',
    'loveSpread.family.5.healingNeeded': '必要なヒーリング',
    'loveSpread.family.5.familyHarmony': '家族の調和',
    'loveSpread.family.6.yourFeelings': 'あなたの感情',
    'loveSpread.family.6.familysNeeds': '家族のニーズ',
    'loveSpread.family.6.familyStrengths': '家族の強み',
    'loveSpread.family.6.currentIssues': '現在の問題',
    'loveSpread.family.6.healingPath': 'ヒーリングの道',
    'loveSpread.family.6.familyFuture': '家族の未来',
    'loveSpread.family.7.rootSecurity': 'ルート - 家族の安全性',
    'loveSpread.family.7.sacralCreativity': 'セイクラル - 家族の創造性',
    'loveSpread.family.7.solarPower': 'ソーラープレクサス - 家族の力',
    'loveSpread.family.7.heartLove': 'ハート - 家族の愛',
    'loveSpread.family.7.throatCommunication': 'スロート - 家族のコミュニケーション',
    'loveSpread.family.7.thirdEyeWisdom': 'サードアイ - 家族の知恵',
    'loveSpread.family.7.crownSpirit': 'クラウン - 家族の精神',
    'loveSpread.family.10.familySituation': '家族の状況',
    'loveSpread.family.10.challengeCross': 'チャレンジ',
    'loveSpread.family.10.familyFoundation': '最近の過去',
    'loveSpread.family.10.recentPast': '近い未来',
    'loveSpread.family.10.possibleOutcome': '可能な結果',
    'loveSpread.family.10.nearFuture': '家族の基盤',
    'loveSpread.family.10.yourRole': 'あなたの役割',
    'loveSpread.family.10.externalInfluences': '外部の影響',
    'loveSpread.family.10.familyHopesFears': '家族の希望と恐れ',
    'loveSpread.family.10.familyFuture': '家族の未来',
    'loveSpread.family.12.janNewFamilyStart': '1月',
    'loveSpread.family.12.febFamilyGrowth': '2月',
    'loveSpread.family.12.marFamilyAction': '3月',
    'loveSpread.family.12.aprFamilyStability': '4月',
    'loveSpread.family.12.mayFamilyFun': '5月',
    'loveSpread.family.12.junFamilyHarmony': '6月',
    'loveSpread.family.12.julFamilyReflection': '7月',
    'loveSpread.family.12.augFamilyStrength': '8月',
    'loveSpread.family.12.sepFamilyCompletion': '9月',
    'loveSpread.family.12.octFamilyChange': '10月',
    'loveSpread.family.12.novFamilyHealing': '11月',
    'loveSpread.family.12.decFamilyJoy': '12月',

    // Love Spread Card Positions - Business (Japanese)
    'loveSpread.business.1.businessRelationship': 'ビジネス関係',
    'loveSpread.business.3.professionalPast': '過去',
    'loveSpread.business.3.currentDynamic': '現在',
    'loveSpread.business.3.professionalFuture': '未来',
    'loveSpread.business.5.yourProfessionalRole': 'あなたのプロフェッショナルな役割',
    'loveSpread.business.5.partnerColleague': 'パートナー/同僚',
    'loveSpread.business.5.businessFoundation': 'ビジネスの基盤',
    'loveSpread.business.5.challengeToAddress': '対処すべき課題',
    'loveSpread.business.5.professionalOutcome': 'プロフェッショナルな結果',
    'loveSpread.business.6.yourProfessionalFeelings': 'あなたのプロフェッショナルな感情',
    'loveSpread.business.6.theirPerspective': '彼らの視点',
    'loveSpread.business.6.businessFoundation': 'ビジネスの基盤',
    'loveSpread.business.6.currentChallenge': '現在の課題',
    'loveSpread.business.6.strategyNeeded': '必要な戦略',
    'loveSpread.business.6.businessFuture': 'ビジネスの未来',
    'loveSpread.business.7.rootSecurity': 'ルート - ビジネスの安全性',
    'loveSpread.business.7.sacralCollaboration': 'セイクラル - 創造的コラボレーション',
    'loveSpread.business.7.solarPower': 'ソーラープレクサス - プロフェッショナルな力',
    'loveSpread.business.7.heartRespect': 'ハート - 相互尊重',
    'loveSpread.business.7.throatCommunication': 'スロート - ビジネスコミュニケーション',
    'loveSpread.business.7.thirdEyeVision': 'サードアイ - ビジネスビジョン',
    'loveSpread.business.7.crownPurpose': 'クラウン - より高い目的',
    'loveSpread.business.10.businessSituation': 'ビジネス状況',
    'loveSpread.business.10.challengeCross': 'チャレンジ',
    'loveSpread.business.10.businessFoundation': '過去',
    'loveSpread.business.10.recentPast': '未来',
    'loveSpread.business.10.possibleOutcome': '可能な結果',
    'loveSpread.business.10.nearFuture': 'ビジネスの基盤',
    'loveSpread.business.10.yourApproach': 'あなたのアプローチ',
    'loveSpread.business.10.externalInfluences': '外部の影響',
    'loveSpread.business.10.businessHopesFears': 'ビジネスの希望と恐れ',
    'loveSpread.business.10.professionalFuture': 'プロフェッショナルな未来',
    'loveSpread.business.12.janNewBusinessEnergy': '1月',
    'loveSpread.business.12.febProfessionalGrowth': '2月',
    'loveSpread.business.12.marBusinessAction': '3月',
    'loveSpread.business.12.aprStableFoundation': '4月',
    'loveSpread.business.12.mayBusinessAdventure': '5月',
    'loveSpread.business.12.junProfessionalHarmony': '6月',
    'loveSpread.business.12.julBusinessReflection': '7月',
    'loveSpread.business.12.augBusinessStrength': '8月',
    'loveSpread.business.12.sepProjectCompletion': '9月',
    'loveSpread.business.12.octBusinessTransformation': '10月',
    'loveSpread.business.12.novStrategicPlanning': '11月',
    'loveSpread.business.12.decBusinessSuccess': '12月',

    // Personal Growth
    // Personal Growth
    'growth.chooseGrowthSpread': 'あなたの個人成長スプレッドを選択',
    'growth.selectCardCount': '個人成長リーディングのカード枚数を選択してください',
    'growth.growthTypeTitle': '成長タイプ',
    'growth.selectGrowthType': 'ガイダンスを受けたい個人成長のタイプを選択してください',
    'growth.focusIntention': '意図を集中させる',
    'growth.intentionDesc': '個人成長のどの側面についてガイダンスを求めますか？',
    'growth.focusAreas': '重点分野：',
    'growth.questionPlaceholder': 'あなたは自分について何を発展させたい、または理解したいですか？（任意）',
    'growth.startDrawing': 'カードを引き始める',
    'growth.cards': 'カード',
    'growth.singleInsight': '一枚引きの洞察',
    'growth.singleInsightDesc': '現在の成長の機会または学び',
    'growth.growthTriangle': '成長のトライアングル',
    'growth.growthTriangleDesc': '過去の学び、現在の状態、未来の可能性',
    'growth.personalDevelopmentCross': '個人発達のクロス',
    'growth.personalDevelopmentCrossDesc': '核心の自己、課題、強み、ガイダンス、結果',
    'growth.lifeAspectsSpread': '人生の側面スプレッド',
    'growth.lifeAspectsSpreadDesc': '心、体、精神、人間関係、キャリア、目的',
    'growth.chakraGrowth': 'チャクラ成長スプレッド',
    'growth.chakraGrowthDesc': 'エネルギーセンターを通した個人成長',
    'growth.lifeJourneyCelticCross': '人生の旅ケルト十字',
    'growth.lifeJourneyCelticCrossDesc': '包括的な個人発達リーディング',
    'growth.yearOfGrowth': '成長の一年',
    'growth.yearOfGrowthDesc': '個人成長のための月ごとのガイダンス',
    'growth.lifeJourney': '人生の旅',
    'growth.lifeJourneyDesc': '人生の大きな転機を乗り越え、自分の道を見つける',
    'growth.lifePurpose': '人生の目的',
    'growth.majorDecisions': '重要な決断',
    'growth.lifeDirection': '人生の方向性',
    'growth.personalMission': '個人の使命',
    'growth.heroJourney': '英雄の旅',
    'growth.heroJourneyDesc': '試練を受け入れ、変容を遂げる',
    'growth.overcomingObstacles': '障害を乗り越える',
    'growth.innerStrength': '内なる強さ',
    'growth.personalTrials': '個人的な試練',
    'growth.transformation': '変容',
    'growth.spiritualGrowth': '精神的成長',
    'growth.spiritualGrowthDesc': '神聖な存在や高次の自己とのつながりを深める',
    'growth.spiritualAwakening': 'スピリチュアルな覚醒',
    'growth.higherConsciousness': '高次の意識',
    'growth.divineConnection': '神聖なつながり',
    'growth.soulPurpose': '魂の目的',
    'growth.transformationDesc': '変化を受け入れ、自己変容を遂げる',
    'growth.personalChange': '個人的な変化',
    'growth.breakingPatterns': 'パターンの解放',
    'growth.newBeginnings': '新しい始まり',
    'growth.lifeRenewal': '人生の再生',
    'growth.selfReflection': '自己内省',
    'growth.selfReflectionDesc': '深い内省と自己理解',
    'growth.selfAwareness': '自己認識',
    'growth.innerWisdom': '内なる知恵',
    'growth.personalInsights': '個人的な洞察',
    'growth.selfDiscovery': '自己発見',
    'growth.selfImprovement': '自己改善',
    'growth.selfImprovementDesc': 'スキル、習慣、個人的能力を高める',
    'growth.skillDevelopment': 'スキル開発',
    'growth.habitFormation': '習慣形成',
    'growth.personalGoals': '個人の目標',
    'growth.performanceGrowth': 'パフォーマンスの向上',
    'growth.selfDefeating': '自己否定パターン',
    'growth.selfDefeatingDesc': '制限的な思考や行動パターンを特定し克服する',
    'growth.breakingLimitations': '限界を打ち破る',
    'growth.selfSabotagePatterns': '自己妨害のパターン',
    'growth.negativeBeliefs': '否定的な信念',
    'growth.personalBarriers': '個人的な障壁',
    'growth.drawGrowthCard': 'あなたの成長カードを引く',
    'growth.reset': '初期化',


    // Growth spread position translations (Japanese)
    'growth.spread.lifeJourney.1.position1': '現在の成長の焦点',
    'growth.spread.lifeJourney.3.position1': '過去の学び',
    'growth.spread.lifeJourney.3.position2': '現在の状態',
    'growth.spread.lifeJourney.3.position3': '成長の可能性',
    'growth.spread.lifeJourney.5.position1': 'あなたの核心の自己',
    'growth.spread.lifeJourney.5.position2': '現在の課題',
    'growth.spread.lifeJourney.5.position3': '隠れた強み',
    'growth.spread.lifeJourney.5.position4': '必要なガイダンス',
    'growth.spread.lifeJourney.5.position5': '成長の結果',

    'growth.spread.heroJourney.1.position1': '英雄の呼びかけ',
    'growth.spread.heroJourney.3.position1': '試練',
    'growth.spread.heroJourney.3.position2': '内なる強さ',
    'growth.spread.heroJourney.3.position3': '勝利への道',
    'growth.spread.heroJourney.5.position1': '現在の試練',
    'growth.spread.heroJourney.5.position2': '直面すべき恐れ',
    'growth.spread.heroJourney.5.position3': '仲間からの支援',
    'growth.spread.heroJourney.5.position4': '得られた知恵',
    'growth.spread.heroJourney.5.position5': '変容した自己',

    'growth.spread.spiritualGrowth.1.position1': 'スピリチュアルな焦点',
    'growth.spread.spiritualGrowth.3.position1': '魂の呼びかけ',
    'growth.spread.spiritualGrowth.3.position2': '神聖なつながり',
    'growth.spread.spiritualGrowth.3.position3': '悟りへの道',
    'growth.spread.spiritualGrowth.5.position1': 'スピリチュアルなブロック',
    'growth.spread.spiritualGrowth.5.position2': 'ハイヤーセルフ',
    'growth.spread.spiritualGrowth.5.position3': '神聖な導き',
    'growth.spread.spiritualGrowth.5.position4': '聖なる実践',
    'growth.spread.spiritualGrowth.5.position5': '覚醒した状態',

    'growth.spread.transformation.1.position1': '変化の焦点',
    'growth.spread.transformation.3.position1': '終わらせるべきこと',
    'growth.spread.transformation.3.position2': '変容のプロセス',
    'growth.spread.transformation.3.position3': '新しい始まり',
    'growth.spread.transformation.5.position1': '古いパターン',
    'growth.spread.transformation.5.position2': '解放のプロセス',
    'growth.spread.transformation.5.position3': '変化の触媒エネルギー',
    'growth.spread.transformation.5.position4': '統合のステップ',
    'growth.spread.transformation.5.position5': '再生した自己',

    'growth.spread.selfReflection.1.position1': '内なる真実',
    'growth.spread.selfReflection.3.position1': '隠れた側面',
    'growth.spread.selfReflection.3.position2': '自己認識',
    'growth.spread.selfReflection.3.position3': '内なる知恵',
    'growth.spread.selfReflection.5.position1': 'シャドウセルフ',
    'growth.spread.selfReflection.5.position2': '意識的な心',
    'growth.spread.selfReflection.5.position3': '潜在意識の真実',
    'growth.spread.selfReflection.5.position4': '魂のメッセージ',
    'growth.spread.selfReflection.5.position5': '統合された自己',

    'growth.spread.selfImprovement.1.position1': '成長の焦点',
    'growth.spread.selfImprovement.3.position1': '現在のスキル',
    'growth.spread.selfImprovement.3.position2': '成長エリア',
    'growth.spread.selfImprovement.3.position3': 'マスタリーパス',
    'growth.spread.selfImprovement.5.position1': '強さの基盤',
    'growth.spread.selfImprovement.5.position2': 'スキルギャップ',
    'growth.spread.selfImprovement.5.position3': '学習方法',
    'growth.spread.selfImprovement.5.position4': '必要な練習',
    'growth.spread.selfImprovement.5.position5': '達成した目標',

    'growth.spread.selfDefeating.1.position1': '制限的なパターン',
    'growth.spread.selfDefeating.3.position1': 'セルフサボタージュ',
    'growth.spread.selfDefeating.3.position2': '根本原因',
    'growth.spread.selfDefeating.3.position3': '解放の道',
    'growth.spread.selfDefeating.5.position1': '制限的な信念',
    'growth.spread.selfDefeating.5.position2': '恐れのパターン',
    'growth.spread.selfDefeating.5.position3': '内なる強さ',
    'growth.spread.selfDefeating.5.position4': '癒しの行動',
    'growth.spread.selfDefeating.5.position5': 'エンパワーメントされた自己',
    'growth.spread.selfDefeating.7.position1': 'ルートチャクラ - 安定と基盤',
    'growth.spread.selfDefeating.7.position2': 'サクラルチャクラ - 創造性と感情',
    'growth.spread.selfDefeating.7.position3': 'ソーラープレクサスチャクラ - 個人の力',
    'growth.spread.selfDefeating.7.position4': 'ハートチャクラ - 愛と慈しみ',
    'growth.spread.selfDefeating.7.position5': 'スロートチャクラ - 自己表現',
    'growth.spread.selfDefeating.7.position6': 'サードアイチャクラ - 直感と知恵',
    'growth.spread.selfDefeating.7.position7': 'クラウンチャクラ - 精神的つながり',

    'growth.spread.base.6.position1': '精神的成長',
    'growth.spread.base.6.position2': '身体的健康',
    'growth.spread.base.6.position3': 'スピリチュアルな発達',
    'growth.spread.base.6.position4': '人間関係の成長',
    'growth.spread.base.6.position5': 'キャリアの進化',
    'growth.spread.base.6.position6': '人生の目的',

    'growth.spread.base.7.position1': 'ルートチャクラ - 安定と基盤',
    'growth.spread.base.7.position2': 'サクラルチャクラ - 創造性と感情',
    'growth.spread.base.7.position3': 'ソーラープレクサスチャクラ - 個人の力',
    'growth.spread.base.7.position4': 'ハートチャクラ - 愛と慈しみ',
    'growth.spread.base.7.position5': 'スロートチャクラ - 自己表現',
    'growth.spread.base.7.position6': 'サードアイチャクラ - 直感と知恵',
    'growth.spread.base.7.position7': 'クラウンチャクラ - 精神的つながり',

    'growth.spread.base.10.position1': '現在の状況',
    'growth.spread.base.10.position2': '成長の課題',
    'growth.spread.base.10.position3': '過去の影響',
    'growth.spread.base.10.position4': '近い未来',
    'growth.spread.base.10.position5': '可能な成長の道',
    'growth.spread.base.10.position6': 'あなたの基盤',
    'growth.spread.base.10.position7': 'あなたのアプローチ',
    'growth.spread.base.10.position8': '外部からの影響',
    'growth.spread.base.10.position9': '内なる希望と恐れ',
    'growth.spread.base.10.position10': '究極の成長可能性',

    'growth.spread.base.12.position1': '1月 - 新たな始まり',
    'growth.spread.base.12.position2': '2月 - 自己内省',
    'growth.spread.base.12.position3': '3月 - 行動を起こす',
    'growth.spread.base.12.position4': '4月 - 安定を築く',
    'growth.spread.base.12.position5': '5月 - 変化を受け入れる',
    'growth.spread.base.12.position6': '6月 - バランスを見つける',
    'growth.spread.base.12.position7': '7月 - 内なる知恵',
    'growth.spread.base.12.position8': '8月 - 個人の強さ',
    'growth.spread.base.12.position9': '9月 - 完成と収穫',
    'growth.spread.base.12.position10': '10月 - 変容',
    'growth.spread.base.12.position11': '11月 - 内面的な探求',
    'growth.spread.base.12.position12': '12月 - 統合と祝福',

    // Mental Healing
    'healing.chooseHealingSpread': 'あなたのヒーリングスプレッドを選択',
    'healing.selectCardCount': 'あなたのヒーリング占いのためのカード枚数を選択してください',
    'healing.healingTypeTitle': 'ヒーリングタイプ',
    'healing.selectHealingType': 'ガイダンスが必要なヒーリングのタイプを選択してください',
    'healing.focusHealingIntention': 'ヒーリングの意図を集中させる',
    'healing.healingIntentionDesc': 'あなたのヒーリングジャーニーのどの特定の側面についてガイダンスをお求めですか？',
    'healing.healingPlaceholder': '何を癒したり、変化させたいですか？（任意）',
    'healing.drawHealingCard': 'あなたのヒーリングカードを引く',
    'healing.startDrawing': 'ドローイングを開始',
    'healing.cards': 'カード',
    'healing.focusAreas': '重点分野:',

    // Healing spread position translations
    // Self Healing
    'healing.spread.selfHealing.1.position1': '現在の癒しの焦点',
    'healing.spread.selfHealing.3.position1': '内なる傷',
    'healing.spread.selfHealing.3.position2': '自己慈愛の実践',
    'healing.spread.selfHealing.3.position3': '癒しの統合',
    'healing.spread.selfHealing.5.position1': '核心の傷',
    'healing.spread.selfHealing.5.position2': '自己批判',
    'healing.spread.selfHealing.5.position3': '内なる強さ',
    'healing.spread.selfHealing.5.position4': 'セルフケアの行動',
    'healing.spread.selfHealing.5.position5': '癒された自己',

    // Energy Healing
    'healing.spread.energyHealing.1.position1': 'エネルギーの焦点',
    'healing.spread.energyHealing.3.position1': 'エネルギーのブロック',
    'healing.spread.energyHealing.3.position2': '現在のエネルギー状態',
    'healing.spread.energyHealing.3.position3': 'エネルギー浄化の道',
    'healing.spread.energyHealing.5.position1': 'エネルギーの消耗',
    'healing.spread.energyHealing.5.position2': 'ブロックされたチャクラ',
    'healing.spread.energyHealing.5.position3': '自然なエネルギー',
    'healing.spread.energyHealing.5.position4': 'エネルギーワークの実践',
    'healing.spread.energyHealing.5.position5': 'バランスの取れたエネルギー',

    // Soulmate Healing
    'healing.spread.soulmateHealing.1.position1': 'ソウルコネクションの焦点',
    'healing.spread.soulmateHealing.3.position1': '魂の傷',
    'healing.spread.soulmateHealing.3.position2': '現在のつながり',
    'healing.spread.soulmateHealing.3.position3': '神聖な愛の道',
    'healing.spread.soulmateHealing.5.position1': '過去世のパターン',
    'healing.spread.soulmateHealing.5.position2': '現在の課題',
    'healing.spread.soulmateHealing.5.position3': '魂の強さ',
    'healing.spread.soulmateHealing.5.position4': '癒しの行動',
    'healing.spread.soulmateHealing.5.position5': '魂の統合',


    // Pregnancy
    'healing.spread.pregnancy.1.position1': '妊娠の焦点',
    'healing.spread.pregnancy.3.position1': '受胎のブロック',
    'healing.spread.pregnancy.3.position2': '現在の状態',
    'healing.spread.pregnancy.3.position3': '受胎への道',
    'healing.spread.pregnancy.5.position1': '身体の準備状態',
    'healing.spread.pregnancy.5.position2': '感情的なブロック',
    'healing.spread.pregnancy.5.position3': '自然な受胎力',
    'healing.spread.pregnancy.5.position4': '必要なサポート',
    'healing.spread.pregnancy.5.position5': '新しい生命のエネルギー',

    // Physical Healing
    'healing.spread.physicalHealing.1.position1': '身体への焦点',
    'healing.spread.physicalHealing.3.position1': '身体の不調和',
    'healing.spread.physicalHealing.3.position2': '身体の知恵',
    'healing.spread.physicalHealing.3.position3': '癒しのプロセス',
    'healing.spread.physicalHealing.5.position1': '根本原因',
    'healing.spread.physicalHealing.5.position2': '身体の抵抗',
    'healing.spread.physicalHealing.5.position3': '自然治癒力',
    'healing.spread.physicalHealing.5.position4': 'ライフスタイルの変化',
    'healing.spread.physicalHealing.5.position5': '回復した健康',

    // Rejection Healing
    'healing.spread.rejectionHealing.1.position1': '自信の焦点',
    'healing.spread.rejectionHealing.3.position1': '拒絶の傷',
    'healing.spread.rejectionHealing.3.position2': '現在の自己価値',
    'healing.spread.rejectionHealing.3.position3': '自信への道',
    'healing.spread.rejectionHealing.5.position1': '核心的な拒絶経験',
    'healing.spread.rejectionHealing.5.position2': '自己不信',
    'healing.spread.rejectionHealing.5.position3': '内なる価値',
    'healing.spread.rejectionHealing.5.position4': '癒しの実践',
    'healing.spread.rejectionHealing.5.position5': '新たな自信',


    // Base healing spread positions
    'healing.spread.base.6.position1': '精神的な癒し',
    'healing.spread.base.6.position2': '身体的な癒し',
    'healing.spread.base.6.position3': '感情的な癒し',
    'healing.spread.base.6.position4': 'スピリチュアルな癒し',
    'healing.spread.base.6.position5': '過去の癒し',
    'healing.spread.base.6.position6': '未来の完全性',

    'healing.spread.base.7.position1': 'ルートチャクラ - 安全と安心',
    'healing.spread.base.7.position2': 'サクラルチャクラ - 感情と創造性',
    'healing.spread.base.7.position3': 'ソーラープレクサスチャクラ - 個人の力',
    'healing.spread.base.7.position4': 'ハートチャクラ - 愛と慈悲',
    'healing.spread.base.7.position5': 'スロートチャクラ - 表現と真実',
    'healing.spread.base.7.position6': 'サードアイチャクラ - 直感と明晰さ',
    'healing.spread.base.7.position7': 'クラウンチャクラ - 精神的なつながり',

    'healing.spread.base.10.position1': '現在の状況',
    'healing.spread.base.10.position2': '主な課題',
    'healing.spread.base.10.position3': '過去の影響',
    'healing.spread.base.10.position4': '近い未来',
    'healing.spread.base.10.position5': '癒しの可能性',
    'healing.spread.base.10.position6': '基盤',
    'healing.spread.base.10.position7': 'あなたの取り組み方',
    'healing.spread.base.10.position8': '外部からのサポート',
    'healing.spread.base.10.position9': '内なる知恵',
    'healing.spread.base.10.position10': '完全な癒し',

    'healing.spread.base.12.position1': '1月 - 新しい癒しのサイクル',
    'healing.spread.base.12.position2': '2月 - 感情の解放',
    'healing.spread.base.12.position3': '3月 - 行動を起こす',
    'healing.spread.base.12.position4': '4月 - 基盤を築く',
    'healing.spread.base.12.position5': '5月 - 成長と変化',
    'healing.spread.base.12.position6': '6月 - バランスと調和',
    'healing.spread.base.12.position7': '7月 - 内なる反映',
    'healing.spread.base.12.position8': '8月 - 個人的な強さ',
    'healing.spread.base.12.position9': '9月 - 統合',
    'healing.spread.base.12.position10': '10月 - 変容',
    'healing.spread.base.12.position11': '11月 - 深い癒し',
    'healing.spread.base.12.position12': '12月 - 更新と祝福',
    'healing.singleHealingFocus': '単一の癒しの焦点',
    'healing.singleHealingFocusDesc': '癒しに集中すべき一つの重要な領域',
    'healing.healingTriangle': 'ヒーリング・トライアングル',
    'healing.healingTriangleDesc': '根本原因、現在の状態、癒しの道',
    'healing.healingCross': 'ヒーリング・クロス',
    'healing.healingCrossDesc': '包括的な癒しのガイダンス',
    'healing.holisticHealing': 'ホリスティック・ヒーリング',
    'healing.holisticHealingDesc': '心、身体、精神、ハート、過去、未来の癒し',
    'healing.chakraHealing': 'チャクラ・ヒーリング',
    'healing.chakraHealingDesc': 'エネルギーセンターを通じた癒し',
    'healing.deepHealingJourney': '深い癒しの旅',
    'healing.deepHealingJourneyDesc': '完全な癒しと変容のスプレッド',
    'healing.yearOfHealing': '癒しの一年',
    'healing.yearOfHealingDesc': '月ごとの癒しの指針と進展',
    'healing.selfHealing': '自己ヒーリング',
    'healing.selfHealingDesc': '内面の探求と個人的な癒しの旅',
    'healing.selfCompassion': '自己への思いやり',
    'healing.innerChildWork': 'インナーチャイルドの癒し',
    'healing.personalBoundaries': '個人の境界線',
    'healing.selfForgiveness': '自己許し',
    'healing.energyHealing': 'エネルギー・ヒーリング',
    'healing.energyHealingDesc': 'エネルギーフィールドの浄化とバランスを整える',
    'healing.chakraAlignment': 'チャクラの調整',
    'healing.auraCleansing': 'オーラの浄化',
    'healing.energyBlocks': 'エネルギーのブロック',
    'healing.spiritualProtection': 'スピリチュアルな保護',
    'healing.soulmateHealing': 'ソウルメイト・ヒーリング',
    'healing.soulmateHealingDesc': '深い魂のつながりやツインレイの旅の癒し',
    'healing.soulContracts': '魂の契約',
    'healing.twinFlameHealing': 'ツインフレームの癒し',
    'healing.karmicRelationships': 'カルマ的な関係',
    'healing.divineLove': '神聖な愛',
    'healing.pregnancyFertility': '妊娠と受胎',
    'healing.pregnancyFertilityDesc': '受胎・妊娠・母性への癒しのサポート',
    'healing.fertilityJourney': '受胎の旅',
    'healing.pregnancySupport': '妊娠サポート',
    'healing.maternalHealing': '母性の癒し',
    'healing.newLifeEnergy': '新しい生命のエネルギー',
    'healing.physicalHealing': '身体の癒し',
    'healing.physicalHealingDesc': '身体の自然治癒力を支える',
    'healing.bodyWisdom': '身体の知恵',
    'healing.healingEnergy': '癒しのエネルギー',
    'healing.vitalityRestoration': '活力の回復',
    'healing.physicalStrength': '身体の強さ',
    'healing.rejectionHealing': '拒絶からの癒し',
    'healing.rejectionHealingDesc': '拒絶の傷を知恵と強さへと変える',
    'healing.selfWorthHealing': '自己価値の癒し',
    'healing.rejectionRecovery': '拒絶からの回復',
    'healing.confidenceRebuilding': '自信の再構築',
    'healing.trustRestoration': '信頼の回復',
    'healing.reset': '初期化',


    // General Spread
    'general.generalReading': '総合占い',
    'general.chooseSpreadSize': 'スプレッドのサイズを選択してください',
    'general.selectCardCount': '占いに使用するカードの枚数を選択してください',
    'general.singleCard': '1枚引き',
    'general.singleCardDesc': 'あなたの質問に対する簡潔なガイダンス',
    'general.pastPresentFuture': '過去・現在・未来',
    'general.pastPresentFutureDesc': 'あなたの状況を時系列で読み解くスプレッド',
    'general.fiveCardCross': '5枚クロススプレッド',
    'general.fiveCardCrossDesc': '状況、課題、過去、未来、結果を示すスプレッド',
    'general.hexagramSpread': 'ヘキサグラムスプレッド',
    'general.hexagramSpreadDesc': 'あなたの道に対する包括的なガイダンス',
    'general.chakraAlignment': 'チャクラバランス',
    'general.chakraAlignmentDesc': '人生のあらゆる側面における調和を整える',
    'general.celticCross': 'ケルト十字スプレッド',
    'general.celticCrossDesc': 'あなたの状況と未来を深く探るスプレッド',
    'general.yearAhead': '1年の運勢',
    'general.yearAheadDesc': '1年を通した月ごとのガイダンス',
    'general.pastInfluences': '過去の影響',
    'general.presentSituation': '現在の状況',
    'general.futurePotential': '未来の可能性',
    'general.currentSituation': '現状',
    'general.challengeCross': '課題／クロス',
    'general.pastFoundation': '過去の基盤',
    'general.futurePossibility': '未来の展望',
    'general.finalOutcome': '最終結果',
    'general.generalGuidanceCard': '総合ガイダンスカード',
    'general.focusIntention': '意図を集中させる',
    'general.centerMindQuestion': '心を落ち着け、あなたの質問に意識を集中させてください',
    'general.drawGuidanceCard': 'ガイダンスカードを引く',
    'general.drawCards': 'カードを引く',
    'general.reset': '初期化',
    'general.questionPlaceholder': '何を知りたいですか？（例：「転職すべき？」「健康を改善するには？」「今月集中すべきことは？」など）※任意',

    // General spread position translations (Japanese placeholders)
    'general.spread.1.position1': 'ガイダンス',
    'general.spread.1.position2': '現状',
    'general.spread.1.position3': '未来の可能性',

    'general.spread.3.position1': '過去の影響',
    'general.spread.3.position2': '現在の状況',
    'general.spread.3.position3': '未来の可能性',

    'general.spread.5.position1': '現状',
    'general.spread.5.position2': '課題／クロス',
    'general.spread.5.position3': '過去の基盤',
    'general.spread.5.position4': '未来の展望',
    'general.spread.5.position5': '最終結果',

    'general.spread.6.position1': 'あなたの内なる自己',
    'general.spread.6.position2': 'あなたの現在の道',
    'general.spread.6.position3': '隠れた影響',
    'general.spread.6.position4': 'あなたの強み',
    'general.spread.6.position5': '外部要因',
    'general.spread.6.position6': '前進へのガイダンス',

    'general.spread.7.position1': '根 - 基盤',
    'general.spread.7.position2': '仙骨 - 創造性',
    'general.spread.7.position3': '太陽神経叢 - 個人の力',
    'general.spread.7.position4': 'ハート - 感情',
    'general.spread.7.position5': '喉 - コミュニケーション',
    'general.spread.7.position6': '第三の目 - 直感',
    'general.spread.7.position7': 'クラウン - 精神的なガイダンス',

    'general.spread.10.position1': '現状',
    'general.spread.10.position2': '課題',
    'general.spread.10.position3': '過去の影響',
    'general.spread.10.position4': '近い未来',
    'general.spread.10.position5': '可能な結果',
    'general.spread.10.position6': '基盤',
    'general.spread.10.position7': 'あなたのアプローチ',
    'general.spread.10.position8': '外部要因',
    'general.spread.10.position9': '希望と恐れ',
    'general.spread.10.position10': '最終結果',

    'general.spread.12.position1': '1月',
    'general.spread.12.position2': '2月',
    'general.spread.12.position3': '3月',
    'general.spread.12.position4': '4月',
    'general.spread.12.position5': '5月',
    'general.spread.12.position6': '6月',
    'general.spread.12.position7': '7月',
    'general.spread.12.position8': '8月',
    'general.spread.12.position9': '9月',
    'general.spread.12.position10': '10月',
    'general.spread.12.position11': '11月',
    'general.spread.12.position12': '12月',

    // Inspirational Quotes
    'quotes.love': [
      "私の「運命の相手」はどんな人？",
      "あの人は今、私のことをどう思っていますか？",
      "今、押すべき？ 引くべき？ 恋の駆け引きを教えて！",
      "私に恋してる人は？",
      "パートナーの浮気願望は!?",
      "あの人が私のカラダを求めてくるのはなぜ？",
      "私に次の恋が訪れるのはいつ？",
      "やっぱり気になるあの人と復活することはできますか？",
      "既読なのに返事がないのはなぜ？",
      "あの人は会いたいと思っていますか？",
      "私にモテ期は訪れるの？",
      "あの人は運命の人ですか？",
      "今、あの人にメッセージ送ってもいい？",
      "今告白したらどうなりますか？",
      "パートナーがいるあの人、好きになってもいい？",
      "私に好きな人はできますか？",
      "好き？ 迷惑？ あの人の本心",
      "あの人は今、私に隠し事をしてる？",
      "恋の終幕と私に訪れる次の恋",
      "あの人の第一印象は本物？",
      "パートナーを許したほうがいいですか？",
      "あの人は私に気がありますか？",
      "パートナーの愛を信じてもいいですか？",
      "私に恋人ができないのはなぜ？",
      "次の恋に進むべき？",
      "あの人との体の相性は？",
      "別れたパートナーは、新しい恋に踏み出している？"
    ],
    'quotes.career': [
      "私の天職って何？",
      "転職するなら今？",
      "今の仕事は私に向いてる？",
      "今後の収入はどうなる？",
      "転職？ 独立？ 副業？ 私にとってベストな働き方とは？",
      "手な上司 or 部下に、どう対処すればいい？",
      "職場での私の評価",
      "仕事での失敗、乗り越えられますか？",
      "私のキャリアアップのために必要なことは？",
      "今の仕事、続けるべき？",
      "私の仕事運、上がる？",
      "今の仕事で成功するには？",
      "私の適職は？",
      "私の仕事のストレスを減らすには？",
      "私に向いている働き方は？",
      "今の仕事で昇進できる？",
      "私のビジネスが成功するために必要なことは？",
      "新しいスキルを学ぶべき？",
      "私の仕事での強みは？",
      "私の仕事での弱みは？",
      "今の仕事で成果を出すには？",
      "私にとって理想的な職場環境は？",
      "私がリーダーシップを発揮できる分野は？",
      "私がチームで成功するために必要なことは？",
      "私の仕事での人間関係を改善するには？",
      "私が仕事で達成できる最大の目標は？",
      "今年、最大の仕事運？",
      "新しい仕事でうまくやっていける？",
      "私のキャリアにおける次のステップは？"
    ],
    'quotes.growth': [
      "今考えていることを実行すべき？",
      "お金持ちになれますか？",
      "私が幸せになる方法",
      "3ヶ月以内に訪れる運命の選択",
      "あの選択は間違い？",
      "あなたが思いもしない周囲からの印象",
      "苦手なあの人、今は距離を置くべき？",
      "この悩み、解決する？",
      "仲違いしてしまった友人との関係を修復するには？",
      "夢を追いかけるべき？",
      "新しい趣味を始めるべき？",
      "私が本当にやりたいことは？",
      "あの人と和解できる？",
      "私の人生のテーマ",
      "新しいスキルを学ぶべき？",
      "私にとって大切な価値観とは？",
      "私が恐れていること、その根源は？",
      "私の直感は正しい？",
      "自分をもっと愛するには？",
      "私が今年達成できる最大の目標は？",
      "今年、最大の幸運期？",
      "新しい環境でうまくやっていける？",
      "身も心もスッキリとしたあなたに訪れる幸せ",
      "誰と一緒に過ごせば元気になれますか？",
      "あの人があなたを守りたいと思うとき",
      "金運を上げる生活習慣？",
      "私を待ち受ける恋の事件？",
      "3ヵ月間、注意すべきポイント？",
      "年内、あなたに訪れる出会い？",
      "今年最後のお金の使い道？"
    ],
    'quotes.healing': [
      "今、健康状態を改善するために何ができるでしょうか？",
      "タロットは私の身体の健康についてどんなメッセージを伝えているでしょうか？",
      "心の健康を維持するためにどのようなステップを踏めばいいでしょうか？",
      "感情的な癒しをどのように育むことができるでしょうか？",
      "私の癒しの旅を助けるスピリチュアルな実践にはどのようなものがありますか？",
      "私の癒しのプロセスを妨げているものは何でしょうか？",
      "どうすれば自分自身のための癒しの環境を作ることができるでしょうか？",
      "許しは私の癒しにおいてどのような役割を果たすでしょうか？",
      "どうすれば自分の体のニーズにもっと耳を傾けることができるでしょうか？",
      "どのような癒しの方法を探求すべきでしょうか？",
      "現在の健康問題の根本原因は何でしょうか？",
      "癒しの過程で、どのように自己愛と思いやりを育むことができるでしょうか？",
      "どのようなライフスタイルの変化が私の癒しの旅をサポートしてくれるでしょうか？",
      "最適な健康のために、心と体と精神のバランスをどのように取ることができるでしょうか？",
      "癒しのプロセスを通して、どのような教訓を学ぶべきでしょうか？",
      "癒しを妨げている過去のトラウマをどのように解放できるでしょうか？",
      "どのようにして自分のエネルギーを浄化し、バランスを取ることができるでしょうか？",
      "私の心と体の健康を改善するために、どのようなサポートシステムを探すべきでしょうか？",
      "どのようなサポートシステムを探すべきでしょうか？",
      "現在のライフスタイルは、身体のニーズを満たしているだろうか？",
      "心と身体、そして魂のバランスをより良くするにはどうすればいいだろうか？",
      "私の癒しや回復のプロセスを妨げているものは何だろうか？",
      "どのようにして自分自身のための癒しの環境を作り出せるだろうか？",
      "許しは私の癒しにおいてどのような役割を果たしているだろうか？",
      "現在のストレスや不安の根本原因は何だろうか？",
      "どうすればもっと平穏と感情の安定を得られるだろうか？",
      "心の健康を維持するために、どのような変化を起こす必要があるだろうか？",
      "この感情的な葛藤を通して、どのような教訓を学ぶように求められているだろうか？",
      "健康に影響を与えるネガティブなエネルギーや自己破壊的なパターンをどのように解放できるだろうか？",
      "今、私の身体は何を最も必要としているだろうか？",
      "病気や怪我から回復するために、どのようなステップを踏むべきだろうか？",
      "免疫システムや活力をどのように強化できるだろうか？",
      "どのようなライフスタイルの習慣が、私の健康を害している、あるいは健康に役立っているだろうか？",
      "身体の癒しの旅をどのように最善にサポートできるだろうか？",
      "癒しのために、どのような感情的な傷に注意を払う必要があるだろうか？",
      "癒しの過程で、どのように自己愛と思いやりを育むことができるだろうか？",
      "どのようなスピリチュアルな実践が、私の全体的な癒しとウェルビーイングをサポートしてくれるだろうか？",
      "どのようにして自分のエネルギーを浄化し、バランスを取ることができるだろうか？",
      "私の心と体の健康を改善するために、どのようなサポートシステムを探すべきだろうか？",
      "どのようにして自分の体のニーズにもっと耳を傾けることができるだろうか？",
      "どのような癒しの方法を探求すべきだろうか？",
      "今の健康状態を改善するために、どのような具体的なステップを踏むべきだろうか？",
      "タロットは私の身体の健康について、どんなメッセージを伝えているだろうか？",
      "感情的な癒しを育むために、どのような実践を取り入れるべきだろうか？",
      "私の癒しの旅を助けるスピリチュアルな実践には、どのようなものがあるだろうか？",
      "私の癒しのプロセスを妨げているものは何だろうか？",
      "完全な癒しと活力を体験するために、どのようなライフスタイルの変化が必要だろうか？",
      "最適な健康のために、心と体と精神のバランスをどのように取ることができるだろうか？",
      "癒しのプロセスを通して、どのような教訓を学ぶように求められているだろうか？",
      "癒しを妨げている過去のトラウマを、どのように解放できるだろうか？",
      "どのようにして自分のエネルギーを浄化し、バランスを取ることができるだろうか？",
      "私の心と体の健康を改善するために、どのようなサポートシステムを探すべきだろうか？",
      "今の症状を通して、私の体は私に何を伝えようとしているのか？",
      "より良い健康を促進するために、どのようにエネルギーを調整できるのか？",
      "どのようなスピリチュアルな実践が私のウェルビーイングを向上させるのか？",
      "どのようなカルマや感情のパターンが私の健康に影響を与えているのか？",
      "完全な癒しと活力を体験するために、何を手放す必要があるのか​​？"
    ],
    'quotes.general': [
      "私の人生の真の目的は何だろう？",
      "今、私はどの方向へ進むべきだろうか？",
      "この人生の旅の段階で、私はどんな教訓を学ぶべきだろうか？",
      "ハイヤーセルフと調和するために、どのような変化を起こす必要があるだろうか？",
      "私の個人的な成長や精神的な進歩を妨げているものは何だろうか？",
      "私の恋愛の未来はどうなるだろうか？",
      "どうすれば健全で愛情深い関係を引き寄せることができるだろうか？",
      "過去の人間関係から学ぶべき教訓は何だろうか？",
      "現在の関係を強めるにはどうすればいいだろうか？",
      "過去の恋愛からどんな教訓を学ぶべきだろうか？",
      "今の関係を強めるために何ができるだろうか？",
      "この人は私にふさわしい人だろうか、それとも別れるべきだろうか？",
      "私の才能と目標に最も合致するキャリアパスは何か？",
      "経済状況を改善するにはどうすればいいだろうか？",
      "仕事でより充実感を得るにはどうすればいいだろうか？",
      "仕事でどんなチャンスが巡ってくるだろうか？",
      "キャリアアップのためにどんなスキルを身につけるべきだろうか？",
      "目標を達成するための最善の方法は何だろうか？",
      "成功を妨げている習慣やパターンは何だろうか？",
      "どうすればもっと自信と自己信念を高めることができるだろうか？",
      "全体的な幸福を向上させるにはどうすればいいだろうか？",
      "人生のバランスをもっと取るにはどうすればいいだろうか？",
      "私の精神的な次のステップは何だろうか？",
      "どうすれば直感とのつながりを深められるだろうか？",
      "夢は私にどんなメッセージを伝えているだろうか？",
      "今、人生のどの部分に癒しが必要なのだろうか？",
      "セルフケアを実践する最良の方法は何だろうか？",
      "ネガティブなエネルギーを解放し、ポジティブなエネルギーを取り入れるにはどうすればいいだろうか？",
      "宇宙は今、私に何を伝えようとしているだろうか？",
      "今、どんな機会に心を開くべきだろうか？",
      "どんな挑戦が、私を最も成長させてくれるだろうか？",
      "どんな強みを活かせば、障害を乗り越えることができるだろうか？",
      "どんな新しい始まりが、私の目の前に広がっているだろうか？",
      "前進するために、何を手放すべきだろうか？",
      "私にはどんな隠れた才能や能力があるだろうか？",
      "自分自身と創造性をより良く表現するにはどうすればいいだろうか？",
      "全体的な健康状態を改善するために、どのようなステップを踏めばいいでしょうか？",
      "人生のバランスをもっと整えるにはどうすればいいでしょうか？",
      "私のスピリチュアルな道のりの次のステップは何でしょうか？",
      "直感とのつながりを深めるにはどうすればいいでしょうか？",
      "夢は私にどんなメッセージを伝えているのでしょうか？",
      "今、人生のどの部分に癒しが必要なのでしょうか？",
      "セルフケアを実践する最良の方法は何でしょうか？",
      "ネガティブなエネルギーを解放し、ポジティブなエネルギーを取り入れるにはどうすればいいでしょうか？",
      "宇宙は今、私に何を伝えようとしているのでしょうか？",
      "今、どんな機会に心を開くべきでしょうか？",
      "どんな挑戦が、私を最も成長させてくれるでしょうか？",
      "どんな強みを活かせば、障害を乗り越えることができるでしょうか？",
      "どんな新しい始まりが、私の目の前に広がっているでしょうか？",
      "前進するために、何を手放すべきでしょうか？",
      "私にはどんな隠れた才能や能力があるでしょうか？",
      "自分自身と創造性をより良く表現するにはどうすればいいでしょうか？",
      "全体的な健康状態を改善するために、どのようなステップを踏めばいいでしょうか？",
      "人生のバランスをもっと整えるにはどうすればいいでしょうか？",
      "私のスピリチュアルな道のりの次のステップは何でしょうか？",
      "直感とのつながりを深めるにはどうすればいいでしょうか？",
      "私とお金と豊かさとは何か？",
      "コミュニケーション能力をどのように向上させればいいのか？",
      "人間関係を育む最善の方法は何なのか？",
      "今、どんなスピリチュアルな実践が私に最も役立つのか？",
      "私の魂は、現在の経験を通して私に何を教えようとしているのか？",
      "私の人生における真の目的は何なのか？",
      "今、私はどの方向に進むべきなのか？",
      "この人生の段階で、私はどんな教訓を学ぶべきなのか？",
      "どのような変化を起こす必要があるのか​​？",
      "どうすればハイヤーセルフと調和できるのか？",
      "私の個人的な成長や精神的な進歩を妨げているものは何なのか？",
      "私の恋愛の未来はどうなるのか？",
      "健全で愛情深い関係を築くにはどうすればいいのか？",
      "過去の人間関係から学ぶべき教訓は何なのか？",
      "現在の関係を強めるにはどうすればいいのか？",
      "この人は私にふさわしい人なのか？ それとも、別れるべきなのか？",
      "私の才能と目標に最も合ったキャリアパスは何なのか？",
      "経済状況を改善するにはどうすればいいのか？",
      "仕事でより充実した人生を送るにはどうすればいいのか？",
      "仕事でどんなチャンスが巡ってくるのか？",
      "キャリアアップのためにどんなスキルを身につけるべきなのか？",
      "目標を達成するための最善の方法は何なのか？",
      "成功を妨げている習慣やパターンは何なのか？",
      "自信と自己肯定感を高めるにはどうすればいいのか？",
      "全体的な幸福を向上させるにはどうすればいいのか？",
      "人生のバランスをもっと取るにはどうすればいいのか？",
      "私の精神的な次のステップは何なのか？",
      "どうすれば直感とのつながりを深められるのか？",
      "夢は私にどんなメッセージを伝えているのか？",
      "今、人生のどの部分に癒しが必要なのか？",
      "セルフケアを実践する最良の方法は何なのか？",
      "どうすればネガティブなエネルギーを解放し、ポジティブなエネルギーを人生に取り込めるのか？",
      "宇宙は今、私に何を伝えようとしているのか？",
      "今、どんな機会に心を開くべきなのか？",
      "どんな挑戦が私を最も成長させてくれるのか？",
      "どんな強みを活かせば障害を乗り越えられるのか？",
      "どんな新たな始まりが私の前に待っているのか？",
      "前進するために何を手放すべきなのか？",
      "私にはどんな隠れた才能や能力があるのか？",
      "自分自身と創造性をより良く表現するにはどうすればいいのか？",
      "全体的な健康状態を改善するために、どのようなステップを踏めばいいのか？",
      "人生のバランスをもっと整えるにはどうすればいいのか？",
      "どうすれば人生の目的にもっと近づけるのか？",
      "今、私が最も集中すべきことは何なのか？",
      "私のスピリチュアルな道のりの次のステップは何なのか？",
      "私が活用できる最大の強みは何なのか？",
      "私の人生における最大の障害は何なのか？",
      "どうすれば直感とのつながりを深められるのか？",
      "夢は私にどんなメッセージを伝えているのか？",
      "今、人生のどの部分に癒しが必要なのか？",
      "セルフケアを実践する最良の方法は何なのか？",
      "どうすればネガティブなエネルギーを解放し、ポジティブなエネルギーを人生に取り込めるのか？",
      "成功を阻んでいる障害は何なのか？",
      "今、私を取り巻くエネルギーは何なのか？",
      "私の人生における最大のチャンスは何なのか？",
      "私の人生における真の目的は何なのか？",
      "今、私はどの方向に進むべきなのか？",
      "この人生の段階で、私はどんな教訓を学ぶべきなのか？",
      "どのような変化を起こす必要があるのか​​？",
      "どうすればハイヤーセルフと調和できるのか？",
      "私の個人的な成長や精神的な進歩を妨げているものは何なのか？"
    ],
    
    // Card Options
    'cards.singleCard': 'シングルカード',
    'cards.pastPresentFuture': '過去・現在・未来',
    'cards.loveCross': 'ラブクロス',
    'cards.relationshipSpread': '関係スプレッド',
    'cards.chakraLove': 'チャクラ恋愛',
    'cards.celticCross': 'ケルト十字',
    'cards.zodiacLove': '星座恋愛',
    
    // Card Descriptions
    'cards.singleCardDesc': 'あなたの恋愛の現状',
    'cards.pastPresentFutureDesc': 'あなたの恋愛の旅路',
    'cards.loveCrossDesc': 'あなたとパートナー、課題、結果',
    'cards.relationshipSpreadDesc': '包括的な関係分析',
    'cards.chakraLoveDesc': 'チャクラを通した恋愛エネルギー',
    'cards.celticCrossDesc': '恋愛状況と未来の深い洞察',
    'cards.zodiacLoveDesc': '完全な恋愛予測',
    
    // Career Spread
    'career.title': 'キャリア・目的占い',
    'career.subtitle': 'あなたの職業的な旅と人生の目的についての洞察を発見',
    'career.chooseCareerSpread': 'キャリアスプレッドを選択',
    'career.selectCareerSituation': 'キャリア状況を選択',
    'career.askCareerQuestion': 'キャリアについて質問する',
    'career.drawCareerCard': 'キャリアカードを引く',
    'career.careerCardOfDay': '今日のキャリアカード',
    'career.startCareerReading': 'キャリアタロット占いを開始',
    'career.careerCross': 'キャリアクロス',
    'career.careerCrossDesc': 'スキル、課題、機会',
    'career.professionalPath': 'プロフェッショナルパス',
    'career.professionalPathDesc': '包括的なキャリア分析',
    'career.careerChakra': 'キャリアチャクラ',
    'career.careerChakraDesc': '仕事とのエネルギーの調和',
    'career.zodiacCareer': '星座キャリア',
    'career.zodiacCareerDesc': '完全なプロフェッショナル予測',
    'career.reset': '初期化',
    'career.singleCardDesc': '現在のキャリアの状態',
    'career.pastPresentFutureDesc': 'あなたのキャリアの旅のタイムライン',
    'career.celticCrossDesc': 'キャリア状況の深い洞察',
    'career.currentJob': '現在の仕事・職場',
    'career.jobSeeking': '求職・新しい機会',
    'career.entrepreneur': '起業・ビジネス',
    'career.currentJobDesc': '現在の役割での課題と機会をナビゲート',
    'career.jobSeekingDesc': '求職と新しいキャリアパスのガイダンス',
    'career.entrepreneurDesc': 'ビジネスの開始や成長についての洞察',
    'career.focusAreas': '重点エリア：',
    'career.questionPlaceholder': 'あなたのキャリアパスについて何を知りたいですか？（任意）',
    'career.identifyPassions': '情熱の特定',
    'career.marketOpportunities': '市場機会', 
    'career.manifestationTechniques': '現実化のテクニック',
    'career.soulPurpose': '魂の目的',
    'career.naturalTalents': '自然な才能',
    'career.serviceToOthers': '他者への奉仕',
    'career.developCareerPath': 'キャリアパスを開発',
    'career.careerPathDesc': 'あなたの専門的な方向性と成長をナビゲート',
    'career.preparationStrategies': '準備の戦略',
    'career.firstImpressions': '第一印象',
    'career.integrationTips': '職場への適応のヒント',
    'career.strategicPlanning': '戦略的計画',
    'career.skillDevelopment': 'スキル開発',
    'career.longTermVision': '長期的なビジョン',
    'career.workplaceCollaboration': '職場での協力',
    'career.workplaceCollaborationDesc': '職場での人間関係とチームワークを改善する',
    'career.communicationSkills': 'コミュニケーションスキル',
    'career.teamDynamics': 'チームのダイナミクス',
    'career.leadershipPresence': 'リーダーシップの存在感',
    'career.careerType': 'キャリアの種類',
    'career.selectCareerGuidance': '希望するキャリアガイダンスの種類を選択してください',
    'career.selectNumberCards': 'タロットカードの枚数を選択してください',
    'career.focusIntention': '意図を集中させる',
    'career.centerMindQuestion': '心を落ち着かせ、具体的なキャリアに関する質問に集中してください',
    'career.startDrawingCards': 'カードを引き始める',
    'career.reset': '初期化',
    'career.careerGuidance': 'キャリアガイダンス',


    // Career Spread Card Positions - New Job
    'career.spread.newJob.1.position1': '仕事運',
    'career.spread.newJob.3.position1': '前職の経験',
    'career.spread.newJob.3.position2': '現在の準備',
    'career.spread.newJob.3.position3': '新しい可能性',
    'career.spread.newJob.5.position1': 'あなたのスキル',
    'career.spread.newJob.5.position2': '主な課題',
    'career.spread.newJob.5.position3': 'チーム統合',
    'career.spread.newJob.5.position4': '成長の機会',
    'career.spread.newJob.5.position5': '仕事の成果',
    'career.spread.newJob.6.position1': '現在の状態',
    'career.spread.newJob.6.position2': 'あなたのユニークな価値',
    'career.spread.newJob.6.position3': '開発が必要なスキル',
    'career.spread.newJob.6.position4': '潜在的な障害',
    'career.spread.newJob.6.position5': '最適なタイミング',
    'career.spread.newJob.6.position6': '仕事の達成',
    'career.spread.newJob.7.position1': '根 - 仕事の安定性の基盤',
    'career.spread.newJob.7.position2': '仙骨 - 創造的貢献',
    'career.spread.newJob.7.position3': '太陽神経叢 - プロフェッショナルな自信',
    'career.spread.newJob.7.position4': 'ハート - 仕事への情熱',
    'career.spread.newJob.7.position5': '喉 - コミュニケーションスキル',
    'career.spread.newJob.7.position6': '第三の目 - キャリアビジョン',
    'career.spread.newJob.7.position7': 'クラウン - プロフェッショナルな目的',
    'career.spread.newJob.10.position1': '現在の状況',
    'career.spread.newJob.10.position2': '主な課題',
    'career.spread.newJob.10.position3': '過去',
    'career.spread.newJob.10.position4': '未来',
    'career.spread.newJob.10.position5': '潜在能力',
    'career.spread.newJob.10.position6': '基盤',
    'career.spread.newJob.10.position7': 'あなたの戦略',
    'career.spread.newJob.10.position8': '会社の視点',
    'career.spread.newJob.10.position9': 'キャリアの希望と恐れ',
    'career.spread.newJob.10.position10': '究極の仕事の成果',
    'career.spread.newJob.12.position1': '月 1-2: 強いスタート',
    'career.spread.newJob.12.position2': '月 3-4: 関係構築',
    'career.spread.newJob.12.position3': '月 5-6: 自己証明',
    'career.spread.newJob.12.position4': '月 7-8: 主導権を取る',
    'career.spread.newJob.12.position5': '月 9-10: 認識を得る',
    'career.spread.newJob.12.position6': '月 11-12: 専門性の確立',
    'career.spread.newJob.12.position7': 'コアプロフェッショナル強み',
    'career.spread.newJob.12.position8': 'ナビゲートすべき主要な課題',
    'career.spread.newJob.12.position9': '最高の成長機会',
    'career.spread.newJob.12.position10': '戦略的キャリアアドバイス',
    'career.spread.newJob.12.position11': '最適なキャリアタイミング',
    'career.spread.newJob.12.position12': '長期的成功ビジョン',

    // Career Spread Card Positions - Dream Job
    'career.spread.dreamJob.1.position1': '夢のキャリアエネルギー',
    'career.spread.dreamJob.3.position1': '過去のパターン',
    'career.spread.dreamJob.3.position2': '現在の動機',
    'career.spread.dreamJob.3.position3': '具現化の道',
    'career.spread.dreamJob.5.position1': 'あなたの情熱',
    'career.spread.dreamJob.5.position2': '市場の現実',
    'career.spread.dreamJob.5.position3': 'スキルギャップ',
    'career.spread.dreamJob.5.position4': '戦略的アクション',
    'career.spread.dreamJob.5.position5': 'キャリアの実現',
    'career.spread.dreamJob.6.position1': '現在の不満',
    'career.spread.dreamJob.6.position2': '開発すべき隠れた才能',
    'career.spread.dreamJob.6.position3': '夢のキャリアビジョン',
    'career.spread.dreamJob.6.position4': '克服すべき障害',
    'career.spread.dreamJob.6.position5': '利用可能なサポート',
    'career.spread.dreamJob.6.position6': '達成タイムライン',
    'career.spread.dreamJob.7.position1': '根 - 夢の仕事における経済的安定',
    'career.spread.dreamJob.7.position2': '仙骨 - 創造的表現',
    'career.spread.dreamJob.7.position3': '太陽神経叢 - 個人的な力',
    'career.spread.dreamJob.7.position4': 'ハート - 仕事の充実',
    'career.spread.dreamJob.7.position5': '喉 - 自己表現',
    'career.spread.dreamJob.7.position6': '第三の目 - キャリアの明確さ',
    'career.spread.dreamJob.7.position7': 'クラウン - 人生の調和',
    'career.spread.dreamJob.10.position1': '現在の現実',
    'career.spread.dreamJob.10.position2': '内面的なブロック',
    'career.spread.dreamJob.10.position3': '過去',
    'career.spread.dreamJob.10.position4': '未來',
    'career.spread.dreamJob.10.position5': '意識的な目標',
    'career.spread.dreamJob.10.position6': '最近のキャリアイベント',
    'career.spread.dreamJob.10.position7': 'あなたの具現化アプローチ',
    'career.spread.dreamJob.10.position8': '他者の視点',
    'career.spread.dreamJob.10.position9': '夢と願望',
    'career.spread.dreamJob.10.position10': '究極のキャリアの運命',
    'career.spread.dreamJob.12.position1': '月 1-2: ビジョンの明確化',
    'career.spread.dreamJob.12.position2': '月 3-4: スキル構築',
    'career.spread.dreamJob.12.position3': '月 5-6: ネットワーク拡大',
    'career.spread.dreamJob.12.position4': '月 7-8: 機会創出',
    'career.spread.dreamJob.12.position5': '月 9-10: 飛躍',
    'career.spread.dreamJob.12.position6': '月 11-12: 夢の実現',
    'career.spread.dreamJob.12.position7': 'コアキャリア強み',
    'career.spread.dreamJob.12.position8': '直面すべき主な課題',
    'career.spread.dreamJob.12.position9': '最高の機会ウィンドウ',
    'career.spread.dreamJob.12.position10': '神聖なキャリアガイダンス',
    'career.spread.dreamJob.12.position11': '完璧なタイミング',
    'career.spread.dreamJob.12.position12': '究極の夢の成功',

    // Career Spread Card Positions - Life Purpose
    'career.spread.lifePurpose.1.position1': '魂の使命',
    'career.spread.lifePurpose.3.position1': '過去のキャリア',
    'career.spread.lifePurpose.3.position2': '現在の目覚め',
    'career.spread.lifePurpose.3.position3': '実現への旅',
    'career.spread.lifePurpose.5.position1': '本来の魂',
    'career.spread.lifePurpose.5.position2': '人生の学び',
    'career.spread.lifePurpose.5.position3': '奉仕の領域・呼びかけ',
    'career.spread.lifePurpose.5.position4': '成長の道',
    'career.spread.lifePurpose.5.position5': '目的の達成',
    'career.spread.lifePurpose.6.position1': '現状の目的への混乱',
    'career.spread.lifePurpose.6.position2': '隠された人生の使命',
    'career.spread.lifePurpose.6.position3': '真の天職のビジョン',
    'career.spread.lifePurpose.6.position4': '精神的ブロック',
    'career.spread.lifePurpose.6.position5': '受け取れる神聖なサポート',
    'career.spread.lifePurpose.6.position6': '悟りのキャリアパス',
    'career.spread.lifePurpose.7.position1': 'ルートチャクラ - 人生の基盤と目的',
    'career.spread.lifePurpose.7.position2': 'サクラルチャクラ - 創造的な人生表現',
    'career.spread.lifePurpose.7.position3': 'ソーラープレクサス - 意志と自己の調和',
    'career.spread.lifePurpose.7.position4': 'ハートチャクラ - 愛に基づく奉仕',
    'career.spread.lifePurpose.7.position5': 'スロートチャクラ - 真実の表現',
    'career.spread.lifePurpose.7.position6': 'サードアイチャクラ - 精神的な叡智',
    'career.spread.lifePurpose.7.position7': 'クラウンチャクラ - 神聖なつながり',
    'career.spread.lifePurpose.10.position1': '現在の状況',
    'career.spread.lifePurpose.10.position2': '魂の課題',
    'career.spread.lifePurpose.10.position3': '過去生のカルマ',
    'career.spread.lifePurpose.10.position4': '魂の未来',
    'career.spread.lifePurpose.10.position5': 'ハイヤーセルフからの導き',
    'career.spread.lifePurpose.10.position6': '最近の精神的な学び',
    'career.spread.lifePurpose.10.position7': '魂の道の戦略',
    'career.spread.lifePurpose.10.position8': '世界への奉仕の必要性',
    'career.spread.lifePurpose.10.position9': '魂の願望',
    'career.spread.lifePurpose.10.position10': '宇宙的な人生計画',
    'career.spread.lifePurpose.12.position1': 'スピリチュアルサイクル1:目覚め',
    'career.spread.lifePurpose.12.position2': 'スピリチュアルサイクル2:学び',
    'career.spread.lifePurpose.12.position3': 'スピリチュアルサイクル3:成長',
    'career.spread.lifePurpose.12.position4': 'スピリチュアルサイクル4:奉仕',
    'career.spread.lifePurpose.12.position5': 'スピリチュアルサイクル5:熟達',
    'career.spread.lifePurpose.12.position6': 'スピリチュアルサイクル6:教え',
    'career.spread.lifePurpose.12.position7': '魂の中核的な強さ',
    'career.spread.lifePurpose.12.position8': '癒すべきカルマの課題',
    'career.spread.lifePurpose.12.position9': '神聖なチャンス',
    'career.spread.lifePurpose.12.position10': '高次の導きからのメッセージ',
    'career.spread.lifePurpose.12.position11': '神聖なタイミング',
    'career.spread.lifePurpose.12.position12': '魂の運命の成就',


    // Career Spread Card Positions - Career Path
    'career.spread.careerPath.1.position1': 'キャリアの方向性',
    'career.spread.careerPath.3.position1': '基盤の構築',
    'career.spread.careerPath.3.position2': '現在の岐路',
    'career.spread.careerPath.3.position3': '最適な進むべき道',
    'career.spread.careerPath.5.position1': 'コアの強み',
    'career.spread.careerPath.5.position2': '業界のトレンド',
    'career.spread.careerPath.5.position3': 'スキルの開発',
    'career.spread.careerPath.5.position4': 'ネットワーク構築',
    'career.spread.careerPath.5.position5': 'キャリアの達成',
    'career.spread.careerPath.6.position1': '現在のポジション',
    'career.spread.careerPath.6.position2': '活用すべき自然な才能',
    'career.spread.careerPath.6.position3': 'プロフェッショナルビジョンの明確化',
    'career.spread.careerPath.6.position4': '必要な開発領域',
    'career.spread.careerPath.6.position5': '市場の力の影響',
    'career.spread.careerPath.6.position6': '長期的な成長の道',
    'career.spread.careerPath.7.position1': 'ルート - キャリアの安定性の基盤',
    'career.spread.careerPath.7.position2': 'サクラル - プロフェッショナルの革新',
    'career.spread.careerPath.7.position3': 'ソーラープレクサス - キャリアの野心',
    'career.spread.careerPath.7.position4': 'ハート - ワークライフの充実',
    'career.spread.careerPath.7.position5': 'スロート - リーダーシップの声',
    'career.spread.careerPath.7.position6': 'サードアイ - 戦略的ビジョン',
    'career.spread.careerPath.7.position7': 'クラウン - プロフェッショナルの遺産',
    'career.spread.careerPath.10.position1': '現在の状況',
    'career.spread.careerPath.10.position2': 'プロフェッショナルの課題',
    'career.spread.careerPath.10.position3': '経験の基盤',
    'career.spread.careerPath.10.position4': '成長の可能性の未来',
    'career.spread.careerPath.10.position5': '最高のキャリアの達成',
    'career.spread.careerPath.10.position6': '最近のキャリアの変化',
    'career.spread.careerPath.10.position7': 'あなたの戦略的アプローチ',
    'career.spread.careerPath.10.position8': '業界の視点',
    'career.spread.careerPath.10.position9': 'キャリアの希望と恐れ',
    'career.spread.careerPath.10.position10': 'プロフェッショナルの運命の道',
    'career.spread.careerPath.12.position1': 'Q1 キャリアの焦点',
    'career.spread.careerPath.12.position2': 'Q2 スキル開発',
    'career.spread.careerPath.12.position3': 'Q3 機会追求',
    'career.spread.careerPath.12.position4': 'Q4 達成目標',
    'career.spread.careerPath.12.position5': '2年目のビジョン',
    'career.spread.careerPath.12.position6': '長期的な遺産目標',
    'career.spread.careerPath.12.position7': '重要なプロフェッショナルの強み',
    'career.spread.careerPath.12.position8': '主要な課題のナビゲーション',
    'career.spread.careerPath.12.position9': '最高の機会のウィンドウ',
    'career.spread.careerPath.12.position10': '戦略的なキャリアアドバイス',
    'career.spread.careerPath.12.position11': '最適なタイミング',
    'career.spread.careerPath.12.position12': 'キャリアの遺産の達成',

    // Career Spread Card Positions - Workplace Collaboration
    'career.spread.workplace.1.position1': 'チームのダイナミックエネルギー',
    'career.spread.workplace.3.position1': '過去の経験',
    'career.spread.workplace.3.position2': '現在のチームの状況',
    'career.spread.workplace.3.position3': '今後の協力関係',
    'career.spread.workplace.5.position1': 'あなたのコミュニケーションスタイル',
    'career.spread.workplace.5.position2': 'チームの相性と潜在能力',
    'career.spread.workplace.5.position3': '共有目標の一致',
    'career.spread.workplace.5.position4': '対立解決のアプローチ',
    'career.spread.workplace.5.position5': '共同成功の達成',
    'career.spread.workplace.6.position1': '現在のチーム内の緊張',
    'career.spread.workplace.6.position2': 'あなたの独自の貢献',
    'career.spread.workplace.6.position3': 'チームビジョンの明確さ',
    'career.spread.workplace.6.position4': 'コミュニケーションのギャップ',
    'career.spread.workplace.6.position5': 'リーダーシップからの支援',
    'career.spread.workplace.6.position6': '調和を達成する道',
    'career.spread.workplace.7.position1': 'ルートチャクラ - チームの安定基盤',
    'career.spread.workplace.7.position2': 'サクラルチャクラ - 創造的なチームの相乗効果',
    'career.spread.workplace.7.position3': 'ソーラープレクサス - 共有する力のバランス',
    'career.spread.workplace.7.position4': 'ハートチャクラ - 相互の敬意',
    'career.spread.workplace.7.position5': 'スロートチャクラ - オープンなコミュニケーション',
    'career.spread.workplace.7.position6': 'サードアイチャクラ - チーム全体のビジョン',
    'career.spread.workplace.7.position7': 'クラウンチャクラ - 統一された高次の目的',
    'career.spread.workplace.10.position1': '現在の状況',
    'career.spread.workplace.10.position2': '協働の課題',
    'career.spread.workplace.10.position3': '過去の流れ',
    'career.spread.workplace.10.position4': '将来の可能性',
    'career.spread.workplace.10.position5': '理想的なチームの結果',
    'career.spread.workplace.10.position6': '最近のチーム課題',
    'career.spread.workplace.10.position7': 'あなたの取り組み方・戦略',
    'career.spread.workplace.10.position8': '他者の視点',
    'career.spread.workplace.10.position9': 'チームの目標と願望',
    'career.spread.workplace.10.position10': 'チーム全体の成功の未来',
    'career.spread.workplace.12.position1': '1ヶ月目:間関係の構築',
    'career.spread.workplace.12.position2': '2ヶ月目:信頼の育成',
    'career.spread.workplace.12.position3': '3ヶ月目:課題への対応',
    'career.spread.workplace.12.position4': '4ヶ月目:共に成長する',
    'career.spread.workplace.12.position5': '5ヶ月目:成功の祝福',
    'career.spread.workplace.12.position6': '6ヶ月目:熟達の達成',
    'career.spread.workplace.12.position7': 'コミュニケーションの中核的な強み',
    'career.spread.workplace.12.position8': '人間関係の課題',
    'career.spread.workplace.12.position9': 'チームとしてのチャンス',
    'career.spread.workplace.12.position10': 'リーダーシップからの導き',
    'career.spread.workplace.12.position11': '協働のタイミング',
    'career.spread.workplace.12.position12': '未来への基盤',
    
    // Payment
    'payment.title': 'TAROCA 解説を取得',
    'payment.price': '¥500',
    'payment.description': 'あなたの占い結果の詳細な TAROCA 洞察を解放する',
    'payment.coupon': 'クーポンコードをお持ちですか？こちらをクリック',
    'payment.applyCoupon': 'クーポンを適用',
    'payment.processing': '支払い処理中...',
    'payment.success': '支払い成功！',
    'payment.getExplanation': 'TAROCA解説を取得',
    
    // Auth
    'auth.login': 'サインイン',
    'auth.register': 'アカウント作成',
    'auth.email': 'メールアドレス', 
    'auth.password': 'パスワード',
    'auth.confirmPassword': 'パスワード確認',
    'auth.firstName': '名前',
    'auth.lastName': '苗字',
    'auth.welcome': 'タロットの旅を始めましょう。',
    'auth.switchToLogin': 'アカウントをお持ちですか？サインイン',
    'auth.switchToRegister': 'アカウントをお持ちでないですか？サインアップ',
    'auth.forgotPassword': 'パスワードをお忘れですか？',
    'auth.createAccount': 'アカウント作成',
    'auth.signIn': 'サインイン',
    'auth.resetPassword': 'パスワードリセット',
    'auth.newPassword': '新しいパスワード',
    'auth.resetTitle': 'パスワードをリセット',
    'auth.resetSubtitle': '以下に新しいパスワードを入力してください',
    'auth.resetSuccess': 'パスワードのリセットが完了しました！',
    'auth.invalidToken': '無効または期限切れのリセットトークン',
    'auth.passwordMismatch': 'パスワードが一致しません',
    'auth.backToLogin': 'サインインに戻る',
    'auth.subtitle': 'タロットの完全な体験にアクセスするにはログインしてください',
    'auth.registerSubtitle': 'アカウントを作成してタロットの旅を始めましょう。新規登録したユーザーは、1 つの通訳を無料で入手できます (価格 ¥500)。',
    'auth.emailPlaceholder': 'メールアドレスを入力',
    'auth.passwordPlaceholder': 'パスワードを入力',
    'auth.firstNamePlaceholder': '名前を入力',
    'auth.lastNamePlaceholder': '苗字を入力',
    'auth.confirmPasswordPlaceholder': 'パスワードを確認',
    'auth.signingIn': 'サインイン中...',
    'auth.creatingAccount': 'アカウント作成中...',
    'auth.showPassword': 'パスワードを表示',
    'auth.hidePassword': 'パスワードを隠す',
    'auth.switchToRegisterText': 'アカウントをお持ちでないですか？ ',
    'auth.switchToLoginText': 'アカウントをお持ちですか？ ',
    'auth.signUpLink': 'サインアップ',
    'auth.signInLink': 'サインイン',
    'auth.uspDrawTarot': '本物のようなタロットカード引き',
    'auth.uspTarotLibrary': 'あなた専用のタロットライブラリ',
    'auth.uspGuidedSpreads': 'あらゆる状況に対応したガイド付きスプレッド',
    'auth.uspPersonalDiary': 'あなただけのタロット日記',
    'auth.uspInsights': 'あなたを前進させる洞察',
    'auth.validationFirstNameRequired': '名前が必要です',
    'auth.validationLastNameRequired': '苗字が必要です',
    'auth.validationEmailRequired': 'メールアドレスが必要です',
    'auth.validationEmailInvalid': '有効なメールアドレスを入力してください',
    'auth.validationPasswordRequired': 'パスワードが必要です',
    'auth.validationPasswordTooShort': 'パスワードは8文字以上である必要があります',
    'auth.validationPasswordWeak': 'パスワードには大文字、小文字、数字を含める必要があります',
    'auth.validationConfirmPasswordRequired': 'パスワードを確認してください',
    'auth.validationPasswordsNotMatch': 'パスワードが一致しません',
    'auth.forgotPasswordTitle': 'パスワードを忘れた場合',
    'auth.forgotPasswordSubtitle': 'メールアドレスを入力してください。リセットリンクをお送りします',
    'auth.sendResetLink': 'リセットリンクを送信',
    'auth.sendingResetLink': '送信中...',
    'auth.checkEmailTitle': 'メールをご確認ください',
    'auth.checkEmailMessage': 'パスワードリセットリンクを以下のメールアドレスに送信しました',
    'auth.resetTokenLabel': 'リセットトークン（開発用）',
    'auth.copyToken': 'トークンをコピー',
    'auth.resetTokenCopied': 'リセットトークンをクリップボードにコピーしました！',
    'auth.validationEmailRequired': 'メールアドレスが必要です',
    'auth.rememberPassword': 'パスワードを思い出しましたか？ ',
    
    // Cards and Readings
    'card.upright': '正位置',
    'card.reversed': '逆位置',
    'reading.question': 'どんな質問をしたいですか？',
    'reading.questionPlaceholder': 'ここに質問を入力してください...',
    'reading.yourReading': 'あなたの占い結果',
    'reading.saveReading': '占い結果を保存',
    
    // Common
    'common.loading': '読み込み中...',
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.close': '閉じる',
    'common.continue': '続ける',
    'common.today': '今日',
    'common.card': 'カード',

    // Tarot Cards
    // Major Arcana
    'tarot.fool.name': '0. 愚者',
    'tarot.fool.meaning': '愚者は大アルカナの最初のカードで、新しい旅の始まりを表します。',
    'tarot.fool.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/0-the-fool-tarot-card-meanings-free-tarot-reading/',
    'tarot.magician.name': '1. 魔術師',
    'tarot.magician.meaning': '魔術師は潜在能力と実現化のカードで、アイデアを現実に変える力を象徴しています。',
    'tarot.magician.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/1-the-magician-tarot-card-meanings-free-tarot-reading/',
    'tarot.highPriestess.name': '2. 女教皇',
    'tarot.highPriestess.meaning': '女教皇は直感、神秘、潜在意識との繋がりを表します。',
    'tarot.highPriestess.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/2-the-high-priestess-tarot-card-meanings-free-tarot-reading/',
    'tarot.empress.name': '3. 女帝',
    'tarot.empress.meaning': '女帝は豊かさ、育成のエネルギー、創造性のカードです。',
    'tarot.empress.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/3-the-empress-tarot-card-meanings-free-tarot-reading/',
    'tarot.emperor.name': '4. 皇帝',
    'tarot.emperor.meaning': '皇帝は権威、構造、安定のカードです。',
    'tarot.emperor.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/4-the-emperor-tarot-card-meanings-free-tarot-reading/',
    'tarot.hierophant.name': '5. 法王',
    'tarot.hierophant.meaning': '法王は、大神官としても知られ、伝統、霊性、導きを表します。',
    'tarot.hierophant.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/5-the-hierophant-tarot-card-meanings-free-tarot-reading/',
    'tarot.lovers.name': '6. 恋人',
    'tarot.lovers.meaning': '恋人のカードは結合、調和、選択の力を象徴します。',
    'tarot.lovers.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/6-the-lovers-tarot-card-meanings-free-tarot-reading/',
    'tarot.chariot.name': '7. 戦車',
    'tarot.chariot.meaning': '戦車は決意、意志力、集中と制御による勝利を表します。',
    'tarot.chariot.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/7-the-chariot-tarot-card-meanings-free-tarot-reading/',
    'tarot.strength.name': '8. 力',
    'tarot.strength.meaning': '力のカードは内なる勇気、自己制御、慈悲の深いシンボルです。',
    'tarot.strength.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/8-strength-tarot-card-meanings-free-tarot-reading/',
    'tarot.hermit.name': '9. 隠者',
    'tarot.hermit.meaning': '隠者は内省と内なる導きのカードで、知恵と理解を求める内なる旅を象徴します。',
    'tarot.hermit.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/9-the-hermit-tarot-card-meanings-free-tarot-reading/',
    'tarot.wheelOfFortune.name': '10. 運命の輪',
    'tarot.wheelOfFortune.meaning': '運命の輪は循環、運命、人生の絶え間ない変化のカードで、私たちが経験する浮き沈みを象徴します。',
    'tarot.wheelOfFortune.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/10-the-wheel-of-fortune-tarot-card-meanings-free-tarot-reading/',
    'tarot.justice.name': '11. 正義',
    'tarot.justice.meaning': '正義は公正、真実、バランスの追求を表すカードです。',
    'tarot.justice.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/11-justice-tarot-card-meanings-free-tarot-reading/',
    'tarot.hangedMan.name': '12. 吊られた男',
    'tarot.hangedMan.meaning': '吊られた男は降伏、犠牲、新しい視点のカードで、手放すことと異なる角度から物事を見ることの変容力を表します。',
    'tarot.hangedMan.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/12-the-hanged-man-tarot-card-meanings-free-tarot-reading/',
    'tarot.death.name': '13. 死神',
    'tarot.death.meaning': '死神のカードは変化と再生の強力なシンボルで、人生の循環、終わりと新しい始まりを表します。',
    'tarot.death.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/13-death-tarot-card-meanings-free-tarot-reading/',
    'tarot.temperance.name': '14. 節制',
    'tarot.temperance.meaning': '節制はバランス、調和、節度のカードで、より大きな全体を達成するために対立するものを混合する技術を象徴します。',
    'tarot.temperance.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/14-temperance-tarot-card-meanings-free-tarot-reading/',
    'tarot.devil.name': '15. 悪魔',
    'tarot.devil.meaning': '悪魔は誘惑、束縛、人間性の暗い側面を体現するカードです。',
    'tarot.devil.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/15-the-devil-tarot-card-meanings-free-tarot-reading/',
    'tarot.tower.name': '16. 塔',
    'tarot.tower.meaning': '塔は激変と啓示のカードで、突然の変化と古い構造の崩壊を象徴します。',
    'tarot.tower.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/16-the-tower-tarot-card-meanings-free-tarot-reading/',
    'tarot.star.name': '17. 星',
    'tarot.star.meaning': '星は希望、インスピレーション、平静のカードで、再生の感覚とより明るい日々への約束を象徴します。',
    'tarot.star.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/17-the-star-tarot-card-meanings-free-tarot-reading/',
    'tarot.moon.name': '18. 月',
    'tarot.moon.meaning': '月は直感、幻想、潜在意識のカードで、未知なるものへの旅と自己の隠れた側面の探求を表します。',
    'tarot.moon.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/18-the-moon-tarot-card-meanings-free-tarot-reading/',
    'tarot.sun.name': '19. 太陽',
    'tarot.sun.meaning': '太陽は喜び、成功、活力のカードで、ポジティブなエネルギーと明晰さから来る温かさと光を体現します。',
    'tarot.sun.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/19-the-sun-tarot-card-meanings-free-tarot-reading/',
    'tarot.judgement.name': '20. 審判',
    'tarot.judgement.meaning': '審判は再生、復活、覚醒のカードで、深い変容と人生の選択を評価する呼びかけを象徴します。',
    'tarot.judgement.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/20-judgement-tarot-card-meanings-free-tarot-reading/',
    'tarot.world.name': '21. 世界',
    'tarot.world.meaning': '世界は完成、充実、調和のカードで、旅の終了と目標の達成を表します。',
    'tarot.world.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/21-the-world-tarot-card-meanings-free-tarot-reading/',

    // Wands Suite
    'tarot.aceOfWands.name': 'ワンドのエース',
    'tarot.aceOfWands.meaning': 'ワンドのエースはインスピレーション、新しい始まり、創造的な可能性のカードで、行動と革新を起こす情熱の火花を象徴します。',
    'tarot.aceOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/ace-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfWands.name': 'ワンドの2',
    'tarot.twoOfWands.meaning': 'ワンドの2は探求と未来計画のカードで、選択の瞬間と自分の前進する道への熟考を象徴します。',
    'tarot.twoOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/2-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfWands.name': 'ワンドの3',
    'tarot.threeOfWands.meaning': 'ワンドの3は拡張と遠見のカードで、未来への希望と長期的な成功を象徴します。',
    'tarot.threeOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/3-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfWands.name': 'ワンドの4',
    'tarot.fourOfWands.meaning': 'ワンドの4は祝祭と安定のカードで、調和と共同体の幸せを象徴します。',
    'tarot.fourOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/4-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfWands.name': 'ワンドの5',
    'tarot.fiveOfWands.meaning': 'ワンドの5は競争と闘争のカードで、挑戦と意見の相違を象徴します。',
    'tarot.fiveOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/5-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfWands.name': 'ワンドの6',
    'tarot.sixOfWands.meaning': 'ワンドの6は勝利と成功のカードで、認識と達成を象徴します。',
    'tarot.sixOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/6-of-wands-tarot-card-meanings-free-tarot-reading/', 
    'tarot.sevenOfWands.name': 'ワンドの7',
    'tarot.sevenOfWands.meaning': 'ワンドの7は防御と勇気のカードで、挑戦に立ち向かう決意を象徴します。',
    'tarot.sevenOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/7-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfWands.name': 'ワンドの8',
    'tarot.eightOfWands.meaning': 'ワンドの8は迅速な動きと進歩のカードで、エネルギーと勢いを象徴します。',
    'tarot.eightOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/8-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfWands.name': 'ワンドの9',
    'tarot.nineOfWands.meaning': 'ワンドの9は忍耐と決意のカードで、困難に耐える強さを象徴します。',
    'tarot.nineOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/9-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfWands.name': 'ワンドの10',
    'tarot.tenOfWands.meaning': 'ワンドの10は重荷と責任のカードで、成功への重い代償を象徴します。',
    'tarot.tenOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/10-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfWands.name': 'ワンドのペイジ',
    'tarot.pageOfWands.meaning': 'ワンドのペイジは冒険と探求のカードで、新しい情熱と学習への意欲を象徴します。',
    'tarot.pageOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/page-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfWands.name': 'ワンドのナイト',
    'tarot.knightOfWands.meaning': 'ワンドのナイトは行動と衝動のカードで、勇敢さと冒険心を象徴します。',
    'tarot.knightOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/knight-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfWands.name': 'ワンドのクイーン',
    'tarot.queenOfWands.meaning': 'ワンドのクイーンは自信と魅力のカードで、リーダーシップと温かい心を象徴します。',
    'tarot.queenOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/queen-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfWands.name': 'ワンドのキング',
    'tarot.kingOfWands.meaning': 'ワンドのキングは統率力と野心のカードで、ビジョンと達成を象徴します。',
    'tarot.kingOfWands.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/king-of-wands-tarot-card-meanings-free-tarot-reading/',

    // Cups Suite
    'tarot.aceOfCups.name': 'カップのエース',
    'tarot.aceOfCups.meaning': 'カップのエースは新しい愛と感情の始まりのカードで、心の開放と霊的な充実を象徴します。',
    'tarot.aceOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/ace-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfCups.name': 'カップの2',
    'tarot.twoOfCups.meaning': 'カップの2は愛と調和のカードで、魂の繋がりとパートナーシップを象徴します。',
    'tarot.twoOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/2-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfCups.name': 'カップの3',
    'tarot.threeOfCups.meaning': 'カップの3は友情と祝祭のカードで、共同体と喜びの分かち合いを象徴します。',
    'tarot.threeOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/3-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfCups.name': 'カップの4',
    'tarot.fourOfCups.meaning': 'カップの4は無関心と内省のカードで、現状への不満と新しい機会の見落としを象徴します。',
    'tarot.fourOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/4-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfCups.name': 'カップの5',
    'tarot.fiveOfCups.meaning': 'カップの5は悲しみと失望のカードで、喪失の悲しみと回復への希望を象徴します。',
    'tarot.fiveOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/5-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfCups.name': 'カップの6',
    'tarot.sixOfCups.meaning': 'カップの6は懐かしさと純真のカードで、過去の記憶と子供時代の無邪気さを象徴します。',
    'tarot.sixOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/6-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfCups.name': 'カップの7',
    'tarot.sevenOfCups.meaning': 'カップの7は幻想と選択のカードで、多くの選択肢と現実逃避の誘惑を象徴します。',
    'tarot.sevenOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/7-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfCups.name': 'カップの8',
    'tarot.eightOfCups.meaning': 'カップの8は放棄と探求のカードで、現状からの離脱と新しい道への旅立ちを象徴します。',
    'tarot.eightOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/8-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfCups.name': 'カップの9',
    'tarot.nineOfCups.meaning': 'カップの9は満足と願いの成就のカードで、感情的な充実と幸福を象徴します。',
    'tarot.nineOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/9-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfCups.name': 'カップの10',
    'tarot.tenOfCups.meaning': 'カップの10は家族の調和と完全な幸福のカードで、愛に満ちた関係と感情的な充実を象徴します。',
    'tarot.tenOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/10-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfCups.name': 'カップのペイジ',
    'tarot.pageOfCups.meaning': 'カップのペイジは感受性と創造性のカードで、新しい感情と芸術的なインスピレーションを象徴します。',
    'tarot.pageOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/page-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfCups.name': 'カップのナイト',
    'tarot.knightOfCups.meaning': 'カップのナイトはロマンスと理想主義のカードで、感情的な追求と騎士道精神を象徴します。',
    'tarot.knightOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/knight-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfCups.name': 'カップのクイーン',
    'tarot.queenOfCups.meaning': 'カップのクイーンは直感と共感のカードで、感情的な知恵と養育の愛を象徴します。',
    'tarot.queenOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/queen-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfCups.name': 'カップのキング',
    'tarot.kingOfCups.meaning': 'カップのキングは感情的な成熟とバランスのカードで、慈悲と感情的な統制を象徴します。',
    'tarot.kingOfCups.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/king-of-cups-tarot-card-meanings-free-tarot-reading/',

    // Swords Suite
    'tarot.aceOfSwords.name': 'ソードのエース',
    'tarot.aceOfSwords.meaning': 'ソードのエースは新しいアイデアと精神的明晰性のカードで、真理と知的突破を象徴します。',
    'tarot.aceOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/ace-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfSwords.name': 'ソードの2',
    'tarot.twoOfSwords.meaning': 'ソードの2は難しい選択と停滞のカードで、決断の困難と内的な葛藤を象徴します。',
    'tarot.twoOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/2-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfSwords.name': 'ソードの3',
    'tarot.threeOfSwords.meaning': 'ソードの3は心痛と悲しみのカードで、感情的な傷と別れの痛みを象徴します。',
    'tarot.threeOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/3-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfSwords.name': 'ソードの4',
    'tarot.fourOfSwords.meaning': 'ソードの4は休息と瞑想のカードで、平穏と精神的な回復を象徴します。',
    'tarot.fourOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/4-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfSwords.name': 'ソードの5',
    'tarot.fiveOfSwords.meaning': 'ソードの5は対立と敗北のカードで、勝利の空虚さと人間関係の損失を象徴します。',
    'tarot.fiveOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/5-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfSwords.name': 'ソードの6',
    'tarot.sixOfSwords.meaning': 'ソードの6は移行と回復のカードで、困難からの脱出と癒やしへの旅を象徴します。',
    'tarot.sixOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/6-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfSwords.name': 'ソードの7',
    'tarot.sevenOfSwords.meaning': 'ソードの7は欺瞒と戦略のカードで、単独行動と狡猾な計画を象徴します。',
    'tarot.sevenOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/7-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfSwords.name': 'ソードの8',
    'tarot.eightOfSwords.meaning': 'ソードの8は束縛と制限のカードで、自己制限と被害者意識を象徴します。',
    'tarot.eightOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/8-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfSwords.name': 'ソードの9',
    'tarot.nineOfSwords.meaning': 'ソードの9は不安と悪夢のカードで、精神的苦痛と恐怖を象徴します。',
    'tarot.nineOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/9-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfSwords.name': 'ソードの10',
    'tarot.tenOfSwords.meaning': 'ソードの10は終結と再生のカードで、完全な終了と新しい始まりを象徴します。',
    'tarot.tenOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/10-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfSwords.name': 'ソードのペイジ',
    'tarot.pageOfSwords.meaning': 'ソードのペイジは好奇心と警戒のカードで、新しいアイデアと慎重な観察を象徴します。',
    'tarot.pageOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/page-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfSwords.name': 'ソードのナイト',
    'tarot.knightOfSwords.meaning': 'ソードのナイトは行動と決断のカードで、素早い思考と衝動的な行動を象徴します。',
    'tarot.knightOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/knight-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfSwords.name': 'ソードのクイーン',
    'tarot.queenOfSwords.meaning': 'ソードのクイーンは知的独立と明晰性のカードで、鋭い判断力と誠実さを象徴します。',
    'tarot.queenOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/queen-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfSwords.name': 'ソードのキング',
    'tarot.kingOfSwords.meaning': 'ソードのキングは知的権威と公正のカードで、論理的思考と道徳的リーダーシップを象徴します。',
    'tarot.kingOfSwords.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/king-of-swords-tarot-card-meanings-free-tarot-reading/',

    // Pentacles Suite
    'tarot.aceOfPentacles.name': 'ペンタクルのエース',
    'tarot.aceOfPentacles.meaning': 'ペンタクルのエースは新しい機会と物質的な始まりのカードで、繁栄と実現の可能性を象徴します。',
    'tarot.aceOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/ace-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfPentacles.name': 'ペンタクルの2',
    'tarot.twoOfPentacles.meaning': 'ペンタクルの2はバランスと適応のカードで、変化への柔軟性と優先順位の管理を象徴します。',
    'tarot.twoOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/2-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfPentacles.name': 'ペンタクルの3',
    'tarot.threeOfPentacles.meaning': 'ペンタクルの3は協力と技能のカードで、チームワークと専門性の発展を象徴します。',
    'tarot.threeOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/3-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfPentacles.name': 'ペンタクルの4',
    'tarot.fourOfPentacles.meaning': 'ペンタクルの4は物質的安定と保守のカードで、財政管理と変化への抵抗を象徴します。',
    'tarot.fourOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/4-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfPentacles.name': 'ペンタクルの5',
    'tarot.fiveOfPentacles.meaning': 'ペンタクルの5は困窮と不安のカードで、物質的困難と支援の必要を象徴します。',
    'tarot.fiveOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/5-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfPentacles.name': 'ペンタクルの6',
    'tarot.sixOfPentacles.meaning': 'ペンタクルの6は寛大さとバランスのカードで、与えることと受け取ることの調和を象徴します。',
    'tarot.sixOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/6-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfPentacles.name': 'ペンタクルの7',
    'tarot.sevenOfPentacles.meaning': 'ペンタクルの7は忍耐と評価のカードで、長期的投資と成果の検討を象徴します。',
    'tarot.sevenOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/7-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfPentacles.name': 'ペンタクルの8',
    'tarot.eightOfPentacles.meaning': 'ペンタクルの8は技能と献身のカードで、職人精神と継続的な改善を象徴します。',
    'tarot.eightOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/8-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfPentacles.name': 'ペンタクルの9',
    'tarot.nineOfPentacles.meaning': 'ペンタクルの9は独立と豊かさのカードで、自立した成功と物質的安定を象徴します。',
    'tarot.nineOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/9-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfPentacles.name': 'ペンタクルの10',
    'tarot.tenOfPentacles.meaning': 'ペンタクルの10は遺産と安定のカードで、世代を超えた富と家族の安全を象徴します。',
    'tarot.tenOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/10-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfPentacles.name': 'ペンタクルのペイジ',
    'tarot.pageOfPentacles.meaning': 'ペンタクルのペイジは学習と実用性のカードで、新しいスキルと現実的なアプローチを象徴します。',
    'tarot.pageOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/page-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfPentacles.name': 'ペンタクルのナイト',
    'tarot.knightOfPentacles.meaning': 'ペンタクルのナイトは勤勉と責任のカードで、着実な進歩と信頼性を象徴します。',
    'tarot.knightOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/knight-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfPentacles.name': 'ペンタクルのクイーン',
    'tarot.queenOfPentacles.meaning': 'ペンタクルのクイーンは養育と実用性のカードで、物質的な豊かさと家庭的な知恵を象徴します。',
    'tarot.queenOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/queen-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfPentacles.name': 'ペンタクルのキング',
    'tarot.kingOfPentacles.meaning': 'ペンタクルのキングは物質的成功と安定のカードで、財政的豊かさとビジネスの才能を象徴します。',
    'tarot.kingOfPentacles.link': 'https://tarotreadingcard.online/ja/tarot-card-meanings/king-of-pentacles-tarot-card-meanings-free-tarot-reading/',

    // 逆位置の意味 - 大アルカナ
    'tarot.fool.reversedMeaning': '愚者が逆位置で現れるとき、それはしばしば躊躇、無謀さ、または新しい始まりに向けての準備不足を意味します。',
    'tarot.magician.reversedMeaning': '魔術師が逆位置で現れるとき、それはしばしばブロックされた可能性、力の悪用、または集中力の欠如を意味します。',
    'tarot.highPriestess.reversedMeaning': '女教皇が逆位置で現れるとき、直感からの切断、隠された真実の暴露、または内なる知恵へのアクセスの困難を意味します。',
    'tarot.empress.reversedMeaning': '女帝が逆位置で現れるとき、創造的ブロック、ネグレクト、または育成エネルギーの不均衡を意味します。',
    'tarot.emperor.reversedMeaning': '皇帝が逆位置で現れるとき、権威、コントロール、または構造に関する問題を意味します。',
    'tarot.hierophant.reversedMeaning': '法王が逆位置で現れるとき、反逆、非協調主義、または伝統的価値観とシステムの拒絶を意味します。',
    'tarot.lovers.reversedMeaning': '恋人が逆位置で現れるとき、不調和、不一致、または関係や自分自身の中での対立を意味します。',
    'tarot.chariot.reversedMeaning': '戦車が逆位置で現れるとき、コントロール、方向性、または集中力の欠如を意味します。',
    'tarot.strength.reversedMeaning': '力が逆位置で現れるとき、内的弱さ、自信の欠如、または個人的力の悪用を表します。',
    'tarot.hermit.reversedMeaning': '隠者の逆位置は混乱、孤立、または内なるガイダンスを求めることを拒む感覚をもたらします。',
    'tarot.wheelOfFortune.reversedMeaning': '運命の輪の逆位置は不運のサイクルに止まっているような感じと、状況をコントロールできない感覚を示唆します。',
    'tarot.justice.reversedMeaning': '正義の逆位置は不公平、責任の欠如、または自分の行動の結果を避けることを表します。',
    'tarot.hangedMan.reversedMeaning': '吊られた男の逆位置は変化への抵抗、我慢のなさ、または異なる視点を見ることを拒むことを示します。',
    'tarot.death.reversedMeaning': '死神の逆位置は変化への抵抗、停滞、または必要な終わりと変容への恐れを示唆します。',
    'tarot.temperance.reversedMeaning': '節制の逆位置は不均衡、過度、または節制と調和の欠如を表します。',
    'tarot.devil.reversedMeaning': '悪魔の逆位置は束縛からの解放、依存症の克服、または制限的な信念からの解放を意味します。',
    'tarot.tower.reversedMeaning': '塔の逆位置は必要な変化を避けること、内的な激動、または変容への抵抗を示します。',
    'tarot.star.reversedMeaning': '星の逆位置は希望の喪失、信頼の欠如、またはスピリチュアルなガイダンスからの切断を反映します。',
    'tarot.moon.reversedMeaning': '月の逆位置は明晰さ、幻想の解放、または混乱と恐れの克服をもたらします。',
    'tarot.sun.reversedMeaning': '太陽の逆位置は一時的な後退、熱意の欠如、またはポジティブな面を見る困難を示唆します。',
    'tarot.judgement.reversedMeaning': '審判の逆位置は自己反省を避けること、厳しい自己批判、または過去の過ちから学ぶことを拒むことを表します。',
    'tarot.world.reversedMeaning': '世界の逆位置は未完の目標、閉鎖の欠如、または内的充実のための外的な承認を求めることを示します。',

    // 逆位置の意味 - 小アルカナ
    // Wands Reversed
    'tarot.aceOfWands.reversedMeaning': 'ワンドのエースの逆位置は延期された計画、創造的な停滞、または新しいプロジェクトの困難な始まりを示します。',
    'tarot.twoOfWands.reversedMeaning': 'ワンドの2の逆位置は計画の欠如、個人的な目標、または将来の恐れを表します。',
    'tarot.threeOfWands.reversedMeaning': 'ワンドの3の逆位置は遅延、予期しない障害、または限定的な視野を示します。',
    'tarot.fourOfWands.reversedMeaning': 'ワンドの4の逆位置は不安定な基盤、家庭の不和、または祝賀の欠如を反映します。',
    'tarot.fiveOfWands.reversedMeaning': 'ワンドの5の逆位置は内的葛藤、競争の回避、または解決策の発見を示します。',
    'tarot.sixOfWands.reversedMeaning': 'ワンドの6の逆位置は私的な達成、自信の欠如、または認識への渇望を表します。',
    'tarot.sevenOfWands.reversedMeaning': 'ワンドの7の逆位置は降伏、圧倒感、または立場を守ることの困難を示します。',
    'tarot.eightOfWands.reversedMeaning': 'ワンドの8の逆位置は遅延、忍耐力、または内的変化を反映します。',
    'tarot.nineOfWands.reversedMeaning': 'ワンドの9の逆位置は疲労、偏執症、または最後の一押しを諦めることを示します。',
    'tarot.tenOfWands.reversedMeaning': 'ワンドの10の逆位置は責任の委任、燃え尽き、または負荷の軽減を表します。',
    'tarot.pageOfWands.reversedMeaning': 'ワンドのペイジの逆位置は不完全な計画、短気、または学習への抵抗を示します。',
    'tarot.knightOfWands.reversedMeaning': 'ワンドのナイトの逆位置は衝動性、怒り、または無謀な行動を反映します。',
    'tarot.queenOfWands.reversedMeaning': 'ワンドのクイーンの逆位置は自己疑念、嫉妬、または支配的な行動を表します。',
    'tarot.kingOfWands.reversedMeaning': 'ワンドのキングの逆位置は独裁的なリーダーシップ、傲慢、または無力感を示します。',

    // Cups Reversed
    'tarot.aceOfCups.reversedMeaning': 'カップのエースの逆位置は感情的な空虚、創造的な停滞、または霊的な切断を示します。',
    'tarot.twoOfCups.reversedMeaning': 'カップの2の逆位置は関係の不調和、自己愛、または別れを表します。',
    'tarot.threeOfCups.reversedMeaning': 'カップの3の逆位置は社会的孤立、三角関係、または表面的な関係を反映します。',
    'tarot.fourOfCups.reversedMeaning': 'カップの4の逆位置は動機の回復、新しい機会への開放性、または受け入れを示します。',
    'tarot.fiveOfCups.reversedMeaning': 'カップの5の逆位置は受け入れ、前進、または失ったものからの回復を表します。',
    'tarot.sixOfCups.reversedMeaning': 'カップの6の逆位置は過去からの解放、成長、または独立を示します。',
    'tarot.sevenOfCups.reversedMeaning': 'カップの7の逆位置は明晰さ、決断、または現実的な選択を反映します。',
    'tarot.eightOfCups.reversedMeaning': 'カップの8の逆位置は恐れ、回避、または現状を去ることへの抵抗を表します。',
    'tarot.nineOfCups.reversedMeaning': 'カップの9の逆位置は内的な不幸、貪欲、または満足の誤った感覚を示します。',
    'tarot.tenOfCups.reversedMeaning': 'カップの10の逆位置は家族の不和、関係の問題、または調和の欠如を反映します。',
    'tarot.pageOfCups.reversedMeaning': 'カップのペイジの逆位置は感情的な未熟さ、創造的な停滞、または直感の無視を表します。',
    'tarot.knightOfCups.reversedMeaning': 'カップのナイトの逆位置は気分の変動、非現実的な期待、または感情的な操作を示します。',
    'tarot.queenOfCups.reversedMeaning': 'カップのクイーンの逆位置は感情的な不安定、共依存、または直感の欠如を反映します。',
    'tarot.kingOfCups.reversedMeaning': 'カップのキングの逆位置は感情的な操作、不安定さ、または感情の抑制を表します。',

    // Swords Reversed
    'tarot.aceOfSwords.reversedMeaning': 'ソードのエースの逆位置は混乱した思考、遅延された正義、または精神的な霧を示します。',
    'tarot.twoOfSwords.reversedMeaning': 'ソードの2の逆位置は情報の過剰、優柔不断、または真実の回避を表します。',
    'tarot.threeOfSwords.reversedMeaning': 'ソードの3の逆位置は治癒、赦し、または感情的な解放を反映します。',
    'tarot.fourOfSwords.reversedMeaning': 'ソードの4の逆位置は不安、停滞、または休息への抵抗を示します。',
    'tarot.fiveOfSwords.reversedMeaning': 'ソードの5の逆位置は和解、赦し、または過去の対立からの学びを表します。',
    'tarot.sixOfSwords.reversedMeaning': 'ソードの6の逆位置は未解決の問題、難しい移行、または過去に囚われることを反映します。',
    'tarot.sevenOfSwords.reversedMeaning': 'ソードの7の逆位置は告白、責任を取ること、または欺瞞の露呈を示します。',
    'tarot.eightOfSwords.reversedMeaning': 'ソードの8の逆位置は自由への道、自己解放、または新しい視点を表します。',
    'tarot.nineOfSwords.reversedMeaning': 'ソードの9の逆位置は希望の回復、不安からの解放、または内なる癒しを反映します。',
    'tarot.tenOfSwords.reversedMeaning': 'ソードの10の逆位置は回復、再生、または困難な時期の終了を示します。',
    'tarot.pageOfSwords.reversedMeaning': 'ソードのペイジの逆位置は欠点のあるコミュニケーション、悪意のあるゴシップ、または準備不足を表します。',
    'tarot.knightOfSwords.reversedMeaning': 'ソードのナイトの逆位置は衝動的な行動、攻撃性、または計画の欠如を反映します。',
    'tarot.queenOfSwords.reversedMeaning': 'ソードのクイーンの逆位置は辛辣さ、冷酷さ、または感情的な切断を示します。',
    'tarot.kingOfSwords.reversedMeaning': 'ソードのキングの逆位置は権力の濫用、残酷さ、または判断力の欠如を表します。',

    // Pentacles Reversed
    'tarot.aceOfPentacles.reversedMeaning': 'ペンタクルのエースの逆位置は機会の喪失、財政問題、または物質的な不安定を示します。',
    'tarot.twoOfPentacles.reversedMeaning': 'ペンタクルの2の逆位置は不均衡、優先順位の混乱、または組織の欠如を表します。',
    'tarot.threeOfPentacles.reversedMeaning': 'ペンタクルの3の逆位置は仕事の欠如、チームワークの問題、または技能の軽視を反映します。',
    'tarot.fourOfPentacles.reversedMeaning': 'ペンタクルの4の逆位置は寛大さ、手放すこと、または財政の無謀を示します。',
    'tarot.fiveOfPentacles.reversedMeaning': 'ペンタクルの5の逆位置は財政回復、精神的な貧困からの解放、または支援の受諾を表します。',
    'tarot.sixOfPentacles.reversedMeaning': 'ペンタクルの6の逆位置は一方的な関係、借金、または不公平な交換を反映します。',
    'tarot.sevenOfPentacles.reversedMeaning': 'ペンタクルの7の逆位置は即座の結果への欲求、忍耐の欠如、または投資の見直しを示します。',
    'tarot.eightOfPentacles.reversedMeaning': 'ペンタクルの8の逆位置は完璧主義、不満足、または技能開発の欠如を表します。',
    'tarot.nineOfPentacles.reversedMeaning': 'ペンタクルの9の逆位置は財政依存、過度の労働、または質素さの過度を反映します。',
    'tarot.tenOfPentacles.reversedMeaning': 'ペンタクルの10の逆位置は財政損失、家族紛争、または遺産問題を示します。',
    'tarot.pageOfPentacles.reversedMeaning': 'ペンタクルのペイジの逆位置は現実逃避、学習抵抗、または財政の不注意を表します。',
    'tarot.knightOfPentacles.reversedMeaning': 'ペンタクルのナイトの逆位置は怠惰、完璧主義、または進歩の欠如を反映します。',
    'tarot.queenOfPentacles.reversedMeaning': 'ペンタクルのクイーンの逆位置は金融不安、働きすぎ、または自己ケアの無視を示します。',
    'tarot.kingOfPentacles.reversedMeaning': 'ペンタクルのキングの逆位置は貪欲、物質主義、または財政の無責任を表します。',

    // Positions
    'tarot.position.upright': '正位置',
    'tarot.position.reversed': '逆位置'
  },
  
  'zh-TW': {
    // Language Switcher
    'lang.current': '語言',
    
    // Navigation
    'nav.home': '首頁',
    'nav.meanings': '意義',
    'nav.spreads': '牌陣',
    'nav.diary': '我的日記', 
    'nav.about': '關於',
    'nav.signin': '登入',
    'nav.signup': '註冊',
    
    // User Menu
    'user.profileSettings': '個人資料設定',
    'user.signOut': '登出',
    
    // Profile Page
    'profile.title': '個人資料設定',
    'profile.subtitle': '管理您的個人資訊和帳戶設定',
    'profile.loading': '載入中...',
    'profile.tabProfile': '個人資料',
    'profile.tabPassword': '更改密碼',
    'profile.personalInfo': '個人資訊',
    'profile.firstName': '名字',
    'profile.lastName': '姓氏',
    'profile.firstNamePlaceholder': '請輸入您的名字',
    'profile.lastNamePlaceholder': '請輸入您的姓氏',
    'profile.emailAddress': '電子郵件地址',
    'profile.emailCannotChange': '電子郵件無法更改',
    'profile.saveChanges': '儲存變更',
    'profile.saving': '儲存中...',
    'profile.changePasswordTitle': '更改密碼',
    'profile.currentPassword': '目前密碼',
    'profile.newPassword': '新密碼',
    'profile.confirmPassword': '確認新密碼',
    'profile.currentPasswordPlaceholder': '請輸入目前密碼',
    'profile.newPasswordPlaceholder': '請輸入新密碼',
    'profile.confirmPasswordPlaceholder': '請確認您的新密碼',
    'profile.passwordRequirements': '密碼必須至少8個字元，包含大寫、小寫、數字和特殊字元',
    'profile.passwordsDoNotMatch': '密碼不符',
    'profile.changePasswordButton': '更改密碼',
    'profile.changing': '更改中...',
    'profile.profileUpdated': '個人資料更新成功！',
    'profile.passwordChanged': '密碼更改成功！',
    
    // Homepage
    'home.title': 'TAROCA - 免費塔羅牌占卜',
    'home.subtitle': '將生活中的挑戰轉化為成長的機會',
    'home.drawCard': '抽取你的今日㙮羅牌',
    'home.dailyCard': '今日塔羅牌',
    'home.categoriesTitle': '免費塔羅牌．解讀及牌陣分類',
    'home.categoriesDescription': '選擇與您當前需求和問題產生共鳴的分類',
    'home.sampleQuestions': '範例問題：',
    'home.getStarted': '開始',
    
    // Categories
    'category.love': '愛情塔羅與人際關係',
    'category.career': '事業與目標', 
    'category.growth': '個人成長',
    'category.health': '健康',
    'category.loveDesc': '探索心靈和連結的事務',
    'category.careerDesc': '為您的職業旅程提供指導',
    'category.growthDesc': '為您的精神進化提供洞察',
    'category.healthDesc': '身體、心靈和靈魂的健康',
    
    // Category Questions
    'questions.love1': '如何改善我的戀愛關係？',
    'questions.love2': '愛情對今天的我意味著什麼？',
    'questions.love3': '如何更愛自己？',
    'questions.career1': '什麼職業道路符合我的靈魂？',
    'questions.career2': '如何在工作中找到更多滿足感？',
    'questions.career3': '我應該發展什麼技能？',
    'questions.growth1': '我應該學習什麼教訓？',
    'questions.growth2': '如何成為最好的自己？',
    'questions.growth3': '我應該釋放什麼限制性信念？',
    'questions.health1': '如何獲得身體的智慧？',
    'questions.health2': '如何釋放舊傷？',
    'questions.health3': '什麼能帶給我真正的平靜？',
    
    // Inspirational Questions
    'inspire.question1': '今天你的靈魂需要聽到什麼？',
    'inspire.question2': '這週你應該擁抱什麼能量？',
    'inspire.question3': '宇宙試圖教給你什麼課程？',
    'inspire.question4': '今天你可以利用什麼隱藏的力量？',
    'inspire.question5': '你需要釋放什麼才能向前邁進？',
    'inspire.question6': '什麼機會正在等待你的關注？',
    'inspire.question7': '你已經擁有什麼智慧？',
    'inspire.question8': '什麼新視角可以改變你的一天？',
    'inspire.question9': '你的心想要分享什麼信息？',
    'inspire.question10': '你生活中已經存在什麼魔法？',
    
    // General Reading Section
    'home.notSureCategory': '不確定哪個類別適合？',
    'home.generalReadingDesc': '透過我們靈活的一般占卜牌陣獲得個人化指導',
    'home.createOwnReading': '創建您自己的占卜',
    
    // Growth Section
    'home.whyTaroca': '為什麼選擇TAROCA？',
    'home.growthTitle': '感覺真實、深度學習、幫助您成長的塔羅體驗',
    'home.growthDesc': 'TAROCA 結合了真實塔羅儀式的魔法與引導洞察和個人反思，讓每次占卜都成為您旅程中的前進步驟。',
    
    // Features
    'home.featureCategory1': '憑直覺選擇，不只是機會',
    'home.featureTitle1': '像真正的儀式一樣抽牌',
    'home.featureDesc1': '體驗塔羅本來的樣子 — 塔羅牌以神秘的圓圈或扇形排列，等待您來選擇。每次抽牌都感覺真實，就像坐在真正的塔羅桌前一樣。',
    'home.featureCategory2': '不只是牌義，而是真正的理解和實踐',
    'home.featureTitle2': '深度學習與探索塔羅牌的世界',
    'home.featureDesc2': '訪問完整的塔羅牌庫，包含正位和逆位意義、象徵性洞察，以及為愛情、事業和自我成長預建的牌陣。無論您是尋求答案還是僅僅學習，TAROCA都會清晰地指導您。',
    'home.featureCategory3': '將占卜轉化為人生的經歷和成長課程',
    'home.featureTitle3': '反思、紀錄和成長',
    'home.featureDesc3': '您抽到的每個牌陣都可以保存到您的個人塔羅日記中。透過引導提示進行反思，追蹤重複出現的卡片，並查看針對您的抽牌量身定制的建設性建議 — 幫助您隨時間看到模式、歴練和進步。',
    
    // Diary Section
    'home.diaryTitle': '您的塔羅日記',
    'home.diaryDesc': '用您的個人塔羅日記記錄您的心靈之旅。保存占卜結果，反思洞察，並看著您的直覺隨時間成長。',
    'home.diaryFeature1': '保存和整理占卜結果',
    'home.diaryFeature2': '追蹤模式和洞察',
    'home.diaryFeature3': '觀察您的心靈成長',
    'home.viewDiary': '查看我的日記',
    
    // Diary Page
    'diary.title': '我的塔羅日記',
    'diary.subtitle': '您個人的塔羅牌占卜結果與洞察收藏',
    'diary.signInRequired': '需要登入',
    'diary.signInMessage': '請登入以訪問您的塔羅日記',
    'diary.signIn': '登入',
    'diary.loading': '正在載入您的塔羅日記...',
    'diary.totalReadings': '總占卜次數',
    'diary.favoriteSpread': '最喜愛的牌陣',
    'diary.filterAll': '所有牌陣',
    'diary.filterLove': '愛情',
    'diary.filterCareer': '事業',
    'diary.filterPersonal': '個人成長',
    'diary.filterMental': '心靈療癒',
    'diary.filterSingle': '今日塔羅牌',
    'diary.noReadings': '還沒有占卜結果',
    'diary.emptyMessage': '您的塔羅日記是空的。完成塔羅牌陣並保存以開始您的收藏！',
    'diary.importReading': '匯入占卜結果',
    'diary.deleteConfirm': '您確定要刪除這個占卜結果嗎？',
    'diary.deleteReading': '刪除占卜結果',
    'diary.generateAI': '生成AI洞察',
    'diary.generatingAI': '正在生成洞察...',
    'diary.aiInsight': 'TAROCA的洞察:',
    'diary.question': '問題:',
    'diary.notes': '筆記:',
    'diary.cardsDrawn': '張牌已抽取',
    'diary.untitledReading': '無標題占卜',
    'diary.moreCards': '張以上',
    'diary.previous': '上一頁',
    'diary.next': '下一頁',
    'diary.page': '頁面',
    'diary.of': '共',
    'diary.refreshing': '更新中...',
    
    // Reading Detail Page
    'readingDetail.loading': '正在載入占卜結果...',
    'readingDetail.notFound': '找不到占卜結果',
    'readingDetail.backToDiary': '返回日記',
    'readingDetail.editNotes': '編輯筆記',
    'readingDetail.save': '保存',
    'readingDetail.cancel': '取消',
    'readingDetail.defaultTitle': '塔羅牌占卜詳情',
    'readingDetail.questionAsked': '提出的問題:',
    'readingDetail.yourNotes': '您的筆記:',
    'readingDetail.questionPlaceholder': '您問了什麼問題？',
    'readingDetail.notesPlaceholder': '添加您的想法、解釋和洞察...',
    'readingDetail.askTarocaTitle': '向TAROCA尋求更深層的洞察',
    'readingDetail.askTarocaDesc': '獲得個人化指導，將所有牌卡連接成有意義的故事和可行的下一步。',
    'readingDetail.benefit1': '整個牌陣的詳細解釋',
    'readingDetail.benefit2': '針對您問題的實用指導',
    'readingDetail.benefit3': '牌卡關係和模式的洞察',
    'readingDetail.benefit4': '下一步的可行建議',
    'readingDetail.askTarocaBtn': '詢問TAROCA - 港幣$22',
    'readingDetail.tarocaRevealing': 'TAROCA正在揭示中...',
    'readingDetail.tarocaExplanation': '✨ TAROCA的解釋',
    'readingDetail.aiError': 'AI生成錯誤:',
    'readingDetail.learnMore': '📖 了解更多關於此牌的信息',
    
    // Spreads  
    'spread.love': '愛情占卜',
    'spread.career': '事業占卜',
    'spread.personal': '個人成長占卜',
    'spread.mental': '心靈療癒占卜',
    'spread.general': '一般占卜',
    'spread.chooseCards': '選擇牌數',
    'spread.askQuestion': '提出您的問題（可選）',
    'spread.questionPlaceholder': '您想了解您愛情生活的什麼？',
    'spread.startReading': '開始塔羅占卜',
    'spread.selectType': '選擇您的情況',
    'spread.welcomePersonal': '免費個人成長塔羅牌占卜',
    'spread.welcomeHealing': '免費健康塔羅牌占卜',
    'spread.welcomeGeneral': '免費自訂塔羅牌占卜',
    'spread.welcomeLove': '免費愛情塔羅牌占卜',
    'spread.welcomeCareer': '免費事業塔羅牌占卜',

    // Footer
    'footer.spreadsHeading': '塔羅牌陣',
    'footer.learnHeading': '學習塔羅',
    'footer.companyHeading': 'TAROCA',
    'footer.privacy': '隱私權政策',
    'footer.terms': '服務條款',
    'footer.disclaimer': '塔羅牌占卜僅供自我省思與個人啟發之用，並非醫療、法律或財務建議。',
    'footer.copyright': '© TAROCA. All rights reserved.',

    // 404 page
    'notFound.title': '找不到這個頁面',
    'notFound.body': '您要找的頁面可能已經移動或不存在。不妨開始一次新的占卜。',
    'notFound.cta': '返回塔羅牌陣',

    // Image alt text
    'alt.demoScreen': 'TAROCA 應用程式顯示塔羅牌占卜與解讀的畫面',
    'alt.generalReading': '打造專屬的自訂塔羅牌陣',
    'alt.iconRitual': '代表每日塔羅習慣的圖示',
    'alt.iconInsight': '代表從塔羅占卜獲得洞察的圖示',
    'alt.iconGrowth': '代表透過塔羅實現個人成長的圖示',

    // Spread Drawing Page
    'spreadDrawing.customSpread': '自定牌陣',
    'spreadDrawing.drawingCard': '抽牌中...',
    'spreadDrawing.drawCard': '正在抽塔羅牌 - 第',
    'spreadDrawing.drawAllCards': 'TAROCA幫你抽牌',
    'spreadDrawing.drawingCards': '正在抽牌...',
    'spreadDrawing.of': '張 - 共',
    'spreadDrawing.for': '張：',
    'spreadDrawing.newReading': '重新占卜',
    'spreadDrawing.saveToDiary': '保存到塔羅日記',
    'spreadDrawing.saving': '保存中...',
    'spreadDrawing.tryAgain': '再試一次',
    'spreadDrawing.signIn': '登錄',
    'spreadDrawing.signUpFree': '免費註冊',
    'spreadDrawing.unlockExperience': '解鎖您的完整塔羅體驗',
    'spreadDrawing.signInToUnlock': '全新註冊用戶可得到一次免費的解讀！登錄以解鎖您塔羅旅程的全部力量：',
    'spreadDrawing.signInMessage': '登錄以保存您的占卜並解鎖 TAROCA 的解讀!',
    'spreadDrawing.personalDiary': '📖 個人日記',
    'spreadDrawing.personalDiaryDesc': '保存和跟踪您的占卜，以發現您靈性成長的模式',
    'spreadDrawing.progressTracking': '📊 進度紀錄',
    'spreadDrawing.progressTrackingDesc': '查看您的占卜歷史和靈性旅程統計',
    'spreadDrawing.joinUsers': '加入成千上萬的用戶，啟發您的靈性道路',
    'spreadDrawing.failedToSave': '無法保存占卜。請再試一次。',
    'spreadDrawing.paymentSuccess': '付款成功！占卜結果將會保存到您的塔羅日記。',
    'spreadDrawing.cardPosition': '牌位',
    'spreadDrawing.personalNotes': '個人筆記',
    'spreadDrawing.notesPlaceholder': '寫下您對這次占卜的想法和反思...',
    'spreadDrawing.findLesson': '從 TAROCA 的個人化解讀中，找到當中的啟示和明確指引。全新註冊用戶可得到一次免費的解讀。',
    'spreadDrawing.getTarocaWisdom': '由 TAROCA 為您解讀 - 港幣$22',
    'spreadDrawing.tarocaRevealing': 'TAROCA 正在揭示中...',
    'spreadDrawing.interpretingSpread': '正在解讀您的牌陣並相關啟示。。。',
    'spreadDrawing.spreadOverview': '您的牌陣概覽',
    'spreadDrawing.learnMoreCard': '📖 瞭解更多關於此塔羅牌',
    'spreadDrawing.yourQuestion': '您的問題',
    'spreadDrawing.cardMeaning': '塔羅牌意思：',
    'spreadDrawing.tarocaExplanationTitle': '🔮 TAROCA 的解釋',
    'spreadDrawing.drawAllCards': 'TAROCA幫你抽牌',
    'spreadDrawing.drawingCards': '正在抽牌...',

    // Payment Modal
    'payment.unlockWisdom': '解鎖 TAROCA 的智慧',
    'payment.personalizedInsights': '為您的塔羅占卜獲得個人化洞察',
    'payment.whatYouGet': '您將獲得：',
    'payment.tarocaInterpretation': 'TAROCA 對您牌陣的深度解讀',
    'payment.personalizedGuidance': '基於您問題提供的個人化指引',
    'payment.actionableInsights': '針對您情況的可行建議',
    'payment.savedToDiary': '保存到您的日記以供日後參考',
    'payment.couponApplied': '已應用優惠券：',
    'payment.save': '節省',
    'payment.haveCoupon': '有優惠券代碼嗎？點擊這裡',
    'payment.couponCode': '優惠券代碼',
    'payment.enterCoupon': '輸入優惠券代碼',
    'payment.applyCoupon': '應用優惠券',
    'payment.applying': '應用中...',
    'payment.cancel': '取消',
    'payment.securePayment': '由 Stripe 提供的安全支付',
    'payment.initializing': '正在初始化安全支付...',
    'payment.processing': '處理中...',
    'payment.pay': '付款',
    'payment.thankYou': '感謝您的付款！🎉',
    'payment.paymentSuccessful': '付款成功！✨ 內容包括：',
    'payment.readingSaved': '您的占卜已保存到日記中',
    'payment.accessExplanations': '已成功解鎖 TAROCA 對今次占卜的深度解釋',
    'payment.nextSteps': '💫 下一步：',
    'payment.waitForExplanation': '請稍等。TAROCA 的解釋將為您的獨特情況提供深刻的靈性洞察。',
    'payment.viewAllReadings': '查看所有占卜',
    'payment.tarocaRevealing': 'TAROCA 正在揭示中...',
    'payment.viewThisReading': '查看此占卜結果',

    // Daily Card Positions
    'dailyCard.position': '今日運勢',
    'dailyCard.healingPosition': '今日療癒指引',
    'dailyCard.lovePosition': '今日愛情運勢',
    'dailyCard.careerPosition': '今日事業運勢',
    'dailyCard.growthPosition': '今日成長',
    
    // Daily Card Spread Names
    'dailyCard.spreadName': '今日占卜',
    'dailyCard.growthSpreadName': '今日成長占卜',

    // Love Spread
    'love.title': '愛情與人際關係占卜',
    'love.subtitle': '發現您浪漫生活和人際關係的洞察',
    'love.singles': '想要尋找愛情的單身者',
    'love.couple': '情侶關係',
    'love.singlesDesc': '吸引和尋找靈魂伴侶的指導',
    'love.coupleDesc': '加強和理解您的關係',
    'love.chooseLoveSpread': '選擇您的愛情牌陣',
    'love.selectCardCount': '為您的愛情占卜選擇塔羅牌數量',
    'love.selectSituation': '選擇您的關係狀況',
    'love.askLoveQuestion': '詢問您的愛情問題',
    'love.drawLoveCard': '抽一張愛情塔羅牌',
    'love.loveCardOfDay': '今日的愛情牌',
    'love.selectRelationship': '選擇您的關係類型',
    'love.startLoveReading': '開始愛情塔羅占卜',
    'love.focusAreas': '重點領域：',
    'love.questionPlaceholder': '您想了解您的人際關係的什麼？（可選）',
    'love.selfLovePrep': '自愛準備',
    'love.attractPartner': '吸引合適的伴侶',
    'love.removeLoveBlocks': '移除愛情阻礙',
    'love.communication': '溝通',
    'love.intimacy': '親密關係',
    'love.relationshipGrowth': '關係成長',
    'love.family': '家庭關係',
    'love.familyDesc': '治癒並協調家庭關係',
    'love.familyDynamics': '家庭動態',
    'love.healingOldWounds': '治癒傷痛',
    'love.creatingHarmony': '創造和諧',
    'love.business': '商業合作關係',
    'love.businessDesc': '專業合作夥伴關係與業務聯繫',
    'love.networking': '人脈拓展',
    'love.partnerships': '商業夥伴關係',
    'love.professionalBounds': '專業界限',
    'love.reset': '重置',
    'love.yourLoveGuidance': '您的愛情指引',

    // Love Spread Card Positions - Singles (Chinese)
    'loveSpread.singles.1.loveReadiness': '愛情運',
    'loveSpread.singles.3.pastLessons': '過去的教訓',
    'loveSpread.singles.3.currentEnergy': '當前能量',
    'loveSpread.singles.3.futureLove': '未來愛情',
    'loveSpread.singles.5.yourDesire': '您的渴望',
    'loveSpread.singles.5.whatAttractsLove': '吸引愛情的因素',
    'loveSpread.singles.5.loveBlocks': '愛情障礙',
    'loveSpread.singles.5.actionToTake': '應採取的行動',
    'loveSpread.singles.5.loveManifestation': '愛情顯化',
    'loveSpread.singles.6.selfLoveStatus': '自愛狀態',
    'loveSpread.singles.6.whatYouSeek': '您所尋求的',
    'loveSpread.singles.6.whatYouOffer': '您所提供的',
    'loveSpread.singles.6.currentObstacles': '當前障礙',
    'loveSpread.singles.6.divineTiming': '神聖時機',
    'loveSpread.singles.6.loveOutcome': '愛情結果',
    'loveSpread.singles.7.rootSecurity': '根輪 - 愛情安全感',
    'loveSpread.singles.7.sacralDesires': '生殖輪 - 浪漫渴望',
    'loveSpread.singles.7.solarConfidence': '太陽神經叢 - 自信',
    'loveSpread.singles.7.heartCapacity': '心輪 - 愛的能力',
    'loveSpread.singles.7.throatExpression': '喉輪 - 愛的表達',
    'loveSpread.singles.7.thirdEyeIntuition': '眉心輪 - 愛情直覺',
    'loveSpread.singles.7.crownConnection': '頂輪 - 靈魂連接',
    'loveSpread.singles.10.loveSituation': '愛情狀況',
    'loveSpread.singles.10.challengeCross': '挑戰',
    'loveSpread.singles.10.distantPast': '過去',
    'loveSpread.singles.10.recentPast': '未來',
    'loveSpread.singles.10.possibleOutcome': '期望結果',
    'loveSpread.singles.10.nearFuture': '基礎',
    'loveSpread.singles.10.yourApproach': '自我',
    'loveSpread.singles.10.externalInfluences': '環境影響',
    'loveSpread.singles.10.hopesFears': '希望與恐懼',
    'loveSpread.singles.10.finalOutcome': '愛情結果',
    'loveSpread.singles.12.janNewLoveEnergy': '1月 - 新的愛情能量',
    'loveSpread.singles.12.febSelfLove': '2月 - 自愛',
    'loveSpread.singles.12.marTakingAction': '3月 - 採取行動',
    'loveSpread.singles.12.aprBuildingFoundation': '4月 - 建立基礎',
    'loveSpread.singles.12.mayAdventure': '5月 - 愛情冒險',
    'loveSpread.singles.12.junHarmony': '6月 - 和諧',
    'loveSpread.singles.12.julReflection': '7月 - 反思',
    'loveSpread.singles.12.augStrength': '8月 - 力量',
    'loveSpread.singles.12.sepCompletion': '9月 - 完成',
    'loveSpread.singles.12.octTransformation': '10月 - 轉化',
    'loveSpread.singles.12.novInnerWork': '11月 - 內在工作',
    'loveSpread.singles.12.decLoveManifestation': '12月 - 愛情顯化',

    // Love Spread Card Positions - Couple (Chinese)
    'loveSpread.couple.1.relationshipEnergy': '關係能量',
    'loveSpread.couple.3.relationshipPast': '過去',
    'loveSpread.couple.3.currentDynamic': '現在',
    'loveSpread.couple.3.futureTogether': '未來',
    'loveSpread.couple.5.youInRelationship': '關係中的您',
    'loveSpread.couple.5.yourPartner': '您的伴侶',
    'loveSpread.couple.5.relationshipBond': '關係紐帶',
    'loveSpread.couple.5.challengeToAddress': '需要處理的挑戰',
    'loveSpread.couple.5.relationshipFuture': '未來關係',
    'loveSpread.couple.6.yourFeelings': '您的感受',
    'loveSpread.couple.6.partnersFeelings': '伴侶的感受',
    'loveSpread.couple.6.relationshipFoundation': '關係基礎',
    'loveSpread.couple.6.currentChallenge': '當前挑戰',
    'loveSpread.couple.6.whatsNeeded': '需要什麼',
    'loveSpread.couple.6.relationshipPath': '未來關係',
    'loveSpread.couple.7.rootSecurity': '根輪 - 共同安全感',
    'loveSpread.couple.7.sacralIntimacy': '生殖輪 - 親密與激情',
    'loveSpread.couple.7.solarConfidence': '太陽神經叢 - 作為情侶的自信',
    'loveSpread.couple.7.heartLove': '心輪 - 愛與慈悲',
    'loveSpread.couple.7.throatCommunication': '喉輪 - 溝通',
    'loveSpread.couple.7.thirdEyeVision': '眉心輪 - 共同願景',
    'loveSpread.couple.7.crownBond': '頂輪 - 精神紐帶',
    'loveSpread.couple.10.relationshipStatus': '關係現狀',
    'loveSpread.couple.10.challengeCross': '挑戰',
    'loveSpread.couple.10.foundation': '過去',
    'loveSpread.couple.10.recentPast': '未來',
    'loveSpread.couple.10.possibleOutcome': '期望',
    'loveSpread.couple.10.nearFuture': '基礎',
    'loveSpread.couple.10.yourRole': '您的角色',
    'loveSpread.couple.10.externalInfluences': '環境影響',
    'loveSpread.couple.10.hopesFears': '希望與恐懼',
    'loveSpread.couple.10.relationshipFuture': '關係的長遠未來',
    'loveSpread.couple.12.janNewBeginnings': '1月 - 新的開始',
    'loveSpread.couple.12.febGrowthTogether': '2月 - 共同成長',
    'loveSpread.couple.12.marTakingAction': '3月 - 採取行動',
    'loveSpread.couple.12.aprStability': '4月 - 穩定',
    'loveSpread.couple.12.mayAdventure': '5月 - 冒險',
    'loveSpread.couple.12.junHarmony': '6月 - 和諧',
    'loveSpread.couple.12.julReflection': '7月 - 反思',
    'loveSpread.couple.12.augStrength': '8月 - 力量',
    'loveSpread.couple.12.sepCompletion': '9月 - 完成',
    'loveSpread.couple.12.octChange': '10月 - 變化',
    'loveSpread.couple.12.novDeepWork': '11月 - 深度工作',
    'loveSpread.couple.12.decCelebration': '12月 - 慶祝',

    // Love Spread Card Positions - Family (Chinese)
    'loveSpread.family.1.familyEnergy': '家庭能量',
    'loveSpread.family.3.familyPast': '家庭過去',
    'loveSpread.family.3.currentDynamic': '當前動態',
    'loveSpread.family.3.familyFuture': '家庭未來',
    'loveSpread.family.5.yourRole': '您的角色',
    'loveSpread.family.5.familyFoundation': '家庭基礎',
    'loveSpread.family.5.currentChallenge': '當前挑戰',
    'loveSpread.family.5.healingNeeded': '所需的療癒',
    'loveSpread.family.5.familyHarmony': '家庭和諧',
    'loveSpread.family.6.yourFeelings': '您的感受',
    'loveSpread.family.6.familysNeeds': '家庭需求',
    'loveSpread.family.6.familyStrengths': '家庭優勢',
    'loveSpread.family.6.currentIssues': '當前問題',
    'loveSpread.family.6.healingPath': '療癒之路',
    'loveSpread.family.6.familyFuture': '家庭未來',
    'loveSpread.family.7.rootSecurity': '根輪 - 家庭安全感',
    'loveSpread.family.7.sacralCreativity': '生殖輪 - 家庭創造力',
    'loveSpread.family.7.solarPower': '太陽神經叢 - 家庭力量',
    'loveSpread.family.7.heartLove': '心輪 - 家庭之愛',
    'loveSpread.family.7.throatCommunication': '喉輪 - 家庭溝通',
    'loveSpread.family.7.thirdEyeWisdom': '眉心輪 - 家庭智慧',
    'loveSpread.family.7.crownSpirit': '頂輪 - 家庭精神',
    'loveSpread.family.10.familySituation': '家庭現狀',
    'loveSpread.family.10.challengeCross': '挑戰',
    'loveSpread.family.10.familyFoundation': '過去',
    'loveSpread.family.10.recentPast': '未來',
    'loveSpread.family.10.possibleOutcome': '期望',
    'loveSpread.family.10.nearFuture': '基礎',
    'loveSpread.family.10.yourRole': '您的角色',
    'loveSpread.family.10.externalInfluences': '外部影響',
    'loveSpread.family.10.familyHopesFears': '家庭希望與恐懼',
    'loveSpread.family.10.familyFuture': '長遠的未來',
    'loveSpread.family.12.janNewFamilyStart': '1月 - 新的家庭開始',
    'loveSpread.family.12.febFamilyGrowth': '2月 - 家庭成長',
    'loveSpread.family.12.marFamilyAction': '3月 - 家庭行動',
    'loveSpread.family.12.aprFamilyStability': '4月 - 家庭穩定',
    'loveSpread.family.12.mayFamilyFun': '5月 - 家庭樂趣',
    'loveSpread.family.12.junFamilyHarmony': '6月 - 家庭和諧',
    'loveSpread.family.12.julFamilyReflection': '7月 - 家庭反思',
    'loveSpread.family.12.augFamilyStrength': '8月 - 家庭力量',
    'loveSpread.family.12.sepFamilyCompletion': '9月 - 家庭完成',
    'loveSpread.family.12.octFamilyChange': '10月 - 家庭變化',
    'loveSpread.family.12.novFamilyHealing': '11月 - 家庭療癒',
    'loveSpread.family.12.decFamilyJoy': '12月 - 家庭喜悅',

    // Love Spread Card Positions - Business (Chinese)
    'loveSpread.business.1.businessRelationship': '商業關係',
    'loveSpread.business.3.professionalPast': '專業過去',
    'loveSpread.business.3.currentDynamic': '當前動態',
    'loveSpread.business.3.professionalFuture': '專業未來',
    'loveSpread.business.5.yourProfessionalRole': '您的專業角色',
    'loveSpread.business.5.partnerColleague': '合作夥伴/同事',
    'loveSpread.business.5.businessFoundation': '商業基礎',
    'loveSpread.business.5.challengeToAddress': '需要處理的挑戰',
    'loveSpread.business.5.professionalOutcome': '專業結果',
    'loveSpread.business.6.yourProfessionalFeelings': '您的專業感受',
    'loveSpread.business.6.theirPerspective': '他們的觀點',
    'loveSpread.business.6.businessFoundation': '商業基礎',
    'loveSpread.business.6.currentChallenge': '當前挑戰',
    'loveSpread.business.6.strategyNeeded': '所需策略',
    'loveSpread.business.6.businessFuture': '商業未來',
    'loveSpread.business.7.rootSecurity': '根輪 - 商業安全感',
    'loveSpread.business.7.sacralCollaboration': '生殖輪 - 創意合作',
    'loveSpread.business.7.solarPower': '太陽神經叢 - 專業力量',
    'loveSpread.business.7.heartRespect': '心輪 - 相互尊重',
    'loveSpread.business.7.throatCommunication': '喉輪 - 商業溝通',
    'loveSpread.business.7.thirdEyeVision': '眉心輪 - 商業願景',
    'loveSpread.business.7.crownPurpose': '頂輪 - 更高目標',
    'loveSpread.business.10.businessSituation': '現狀',
    'loveSpread.business.10.challengeCross': '挑戰',
    'loveSpread.business.10.businessFoundation': '過去',
    'loveSpread.business.10.recentPast': '未來',
    'loveSpread.business.10.possibleOutcome': '期望',
    'loveSpread.business.10.nearFuture': '商業基礎',
    'loveSpread.business.10.yourApproach': '您的方法',
    'loveSpread.business.10.externalInfluences': '環境影響',
    'loveSpread.business.10.businessHopesFears': '商業希望與恐懼',
    'loveSpread.business.10.professionalFuture': '可發展的未來',
    'loveSpread.business.12.janNewBusinessEnergy': '1月 - 新的商業能量',
    'loveSpread.business.12.febProfessionalGrowth': '2月 - 專業成長',
    'loveSpread.business.12.marBusinessAction': '3月 - 商業行動',
    'loveSpread.business.12.aprStableFoundation': '4月 - 穩定基礎',
    'loveSpread.business.12.mayBusinessAdventure': '5月 - 商業冒險',
    'loveSpread.business.12.junProfessionalHarmony': '6月 - 專業和諧',
    'loveSpread.business.12.julBusinessReflection': '7月 - 商業反思',
    'loveSpread.business.12.augBusinessStrength': '8月 - 商業力量',
    'loveSpread.business.12.sepProjectCompletion': '9月 - 專案完成',
    'loveSpread.business.12.octBusinessTransformation': '10月 - 商業轉型',
    'loveSpread.business.12.novStrategicPlanning': '11月 - 戰略規劃',
    'loveSpread.business.12.decBusinessSuccess': '12月 - 商業成功',

    // Personal Growth
    'growth.chooseGrowthSpread': '選擇您的個人成長牌陣',
    'growth.selectCardCount': '為您的個人發展占卜選擇塔羅牌數量',
    'growth.growthTypeTitle': '成長類型',
    'growth.selectGrowthType': '選擇您希望獲得指導的個人成長類型',
    'growth.focusIntention': '專注您的意圖',
    'growth.intentionDesc': '您希望在個人成長的哪個方面獲得指導？',
    'growth.focusAreas': '重點領域：',
    'growth.questionPlaceholder': '您想要發展或了解自己的哪些方面？（可選）',
    'growth.startDrawing': '開始抽塔羅牌',
    'growth.cards': '張牌',
    'growth.singleInsight': '單張洞察',
    'growth.singleInsightDesc': '當前的成長機會或課題',
    'growth.growthTriangle': '成長三角牌陣',
    'growth.growthTriangleDesc': '過去的學習、當前的狀態、未來的潛能',
    'growth.personalDevelopmentCross': '個人發展十字牌陣',
    'growth.personalDevelopmentCrossDesc': '核心自我、挑戰、優勢、指引與結果',
    'growth.lifeAspectsSpread': '人生面向牌陣',
    'growth.lifeAspectsSpreadDesc': '心靈、身體、精神、人際關係、事業與使命',
    'growth.chakraGrowth': '脈輪成長牌陣',
    'growth.chakraGrowthDesc': '透過能量中心的個人成長',
    'growth.lifeJourneyCelticCross': '人生旅程凱爾特十字塔羅牌陣',
    'growth.lifeJourneyCelticCrossDesc': '全面性的個人發展解讀',
    'growth.yearOfGrowth': '成長之年',
    'growth.yearOfGrowthDesc': '為您的個人成長旅程提供每月指引',
    'growth.lifeJourney': '人生旅程',
    'growth.lifeJourneyDesc': '導航人生的重要轉折並找到屬於您的道路',
    'growth.lifePurpose': '人生目的',
    'growth.majorDecisions': '重大決策',
    'growth.lifeDirection': '人生方向',
    'growth.personalMission': '個人使命',
    'growth.heroJourney': '英雄之旅',
    'growth.heroJourneyDesc': '擁抱挑戰並在試煉中轉化自己',
    'growth.overcomingObstacles': '克服障礙',
    'growth.innerStrength': '內在力量',
    'growth.personalTrials': '個人試煉',
    'growth.transformation': '蛻變',
    'growth.spiritualGrowth': '靈性成長',
    'growth.spiritualGrowthDesc': '深化與神聖及高我之間的連結',
    'growth.spiritualAwakening': '靈性覺醒',
    'growth.higherConsciousness': '更高意識',
    'growth.divineConnection': '神聖連結',
    'growth.soulPurpose': '靈魂目的',
    'growth.transformationDesc': '擁抱改變與個人的蛻變過程',
    'growth.personalChange': '個人改變',
    'growth.breakingPatterns': '打破舊有模式',
    'growth.newBeginnings': '新的開始',
    'growth.lifeRenewal': '生命的重生',
    'growth.selfReflection': '自我反思',
    'growth.selfReflectionDesc': '深入的內省與自我理解',
    'growth.selfAwareness': '自我覺察',
    'growth.innerWisdom': '內在智慧',
    'growth.personalInsights': '個人洞察',
    'growth.selfDiscovery': '自我探索',
    'growth.selfImprovement': '自我提升',
    'growth.selfImprovementDesc': '培養技能、習慣與個人能力',
    'growth.skillDevelopment': '技能發展',
    'growth.habitFormation': '習慣養成',
    'growth.personalGoals': '個人目標',
    'growth.performanceGrowth': '表現成長',
    'growth.selfDefeating': '自我限制',
    'growth.selfDefeatingDesc': '辨識並克服限制性的模式與行為',
    'growth.breakingLimitations': '突破限制',
    'growth.selfSabotagePatterns': '自我阻礙模式',
    'growth.negativeBeliefs': '負面信念',
    'growth.personalBarriers': '個人障礙',
    'growth.drawGrowthCard': '抽取您的成長卡',
    'growth.reset': '重設',


    // Growth spread position translations (Chinese placeholders)
    'growth.spread.lifeJourney.1.position1': '當前的成長焦點',
    'growth.spread.lifeJourney.3.position1': '過去的課題',
    'growth.spread.lifeJourney.3.position2': '當前的狀態',
    'growth.spread.lifeJourney.3.position3': '成長潛能',
    'growth.spread.lifeJourney.5.position1': '你的核心自我',
    'growth.spread.lifeJourney.5.position2': '當前的挑戰',
    'growth.spread.lifeJourney.5.position3': '隱藏的力量',
    'growth.spread.lifeJourney.5.position4': '需要的指引',
    'growth.spread.lifeJourney.5.position5': '成長結果',

    'growth.spread.heroJourney.1.position1': '英雄的召喚',
    'growth.spread.heroJourney.3.position1': '挑戰',
    'growth.spread.heroJourney.3.position2': '內在力量',
    'growth.spread.heroJourney.3.position3': '勝利之路',
    'growth.spread.heroJourney.5.position1': '當前的試煉',
    'growth.spread.heroJourney.5.position2': '需要面對的恐懼',
    'growth.spread.heroJourney.5.position3': '夥伴的支持',
    'growth.spread.heroJourney.5.position4': '獲得的智慧',
    'growth.spread.heroJourney.5.position5': '蛻變的自我',

    'growth.spread.spiritualGrowth.1.position1': '靈性焦點',
    'growth.spread.spiritualGrowth.3.position1': '靈魂的召喚',
    'growth.spread.spiritualGrowth.3.position2': '神聖的連結',
    'growth.spread.spiritualGrowth.3.position3': '覺醒之路',
    'growth.spread.spiritualGrowth.5.position1': '靈性阻礙',
    'growth.spread.spiritualGrowth.5.position2': '高我',
    'growth.spread.spiritualGrowth.5.position3': '神聖指引',
    'growth.spread.spiritualGrowth.5.position4': '神聖的修練',
    'growth.spread.spiritualGrowth.5.position5': '覺醒的狀態',

    'growth.spread.transformation.1.position1': '變化焦點',
    'growth.spread.transformation.3.position1': '必須結束的事物',
    'growth.spread.transformation.3.position2': '轉化過程',
    'growth.spread.transformation.3.position3': '新的開始',
    'growth.spread.transformation.5.position1': '舊有模式',
    'growth.spread.transformation.5.position2': '釋放過程',
    'growth.spread.transformation.5.position3': '催化能量',
    'growth.spread.transformation.5.position4': '整合步驟',
    'growth.spread.transformation.5.position5': '重生的自我',

    'growth.spread.selfReflection.1.position1': '內在真理',
    'growth.spread.selfReflection.3.position1': '隱藏面向',
    'growth.spread.selfReflection.3.position2': '自我覺察',
    'growth.spread.selfReflection.3.position3': '內在智慧',
    'growth.spread.selfReflection.5.position1': '陰影自我',
    'growth.spread.selfReflection.5.position2': '意識層面',
    'growth.spread.selfReflection.5.position3': '潛意識真相',
    'growth.spread.selfReflection.5.position4': '靈魂訊息',
    'growth.spread.selfReflection.5.position5': '整合的自我',

    'growth.spread.selfImprovement.1.position1': '發展焦點',
    'growth.spread.selfImprovement.3.position1': '現有技能',
    'growth.spread.selfImprovement.3.position2': '成長領域',
    'growth.spread.selfImprovement.3.position3': '精進之路',
    'growth.spread.selfImprovement.5.position1': '優勢基礎',
    'growth.spread.selfImprovement.5.position2': '技能缺口',
    'growth.spread.selfImprovement.5.position3': '學習方法',
    'growth.spread.selfImprovement.5.position4': '需要練習的部分',
    'growth.spread.selfImprovement.5.position5': '達成目標',

    'growth.spread.selfDefeating.1.position1': '限制性模式',
    'growth.spread.selfDefeating.3.position1': '自我阻礙',
    'growth.spread.selfDefeating.3.position2': '根本原因',
    'growth.spread.selfDefeating.3.position3': '解放之路',
    'growth.spread.selfDefeating.5.position1': '限制性信念',
    'growth.spread.selfDefeating.5.position2': '恐懼模式',
    'growth.spread.selfDefeating.5.position3': '內在力量',
    'growth.spread.selfDefeating.5.position4': '療癒行動',
    'growth.spread.selfDefeating.5.position5': '被賦權的自我',
    'growth.spread.selfDefeating.7.position1': '根輪 - 安全與基礎',
    'growth.spread.selfDefeating.7.position2': '腹輪 - 創造力與情感',
    'growth.spread.selfDefeating.7.position3': '太陽神經叢 - 個人力量',
    'growth.spread.selfDefeating.7.position4': '心輪 - 愛與慈悲',
    'growth.spread.selfDefeating.7.position5': '喉輪 - 自我表達',
    'growth.spread.selfDefeating.7.position6': '眉心輪 - 直覺與智慧',
    'growth.spread.selfDefeating.7.position7': '頂輪 - 靈性連結',

    'growth.spread.base.6.position1': '心智成長',
    'growth.spread.base.6.position2': '身體健康',
    'growth.spread.base.6.position3': '靈性發展',
    'growth.spread.base.6.position4': '人際關係成長',
    'growth.spread.base.6.position5': '事業進化',
    'growth.spread.base.6.position6': '人生目的',

    'growth.spread.base.7.position1': '根輪 - 安全與基礎',
    'growth.spread.base.7.position2': '腹輪 - 創造力與情感',
    'growth.spread.base.7.position3': '太陽神經叢 - 個人力量',
    'growth.spread.base.7.position4': '心輪 - 愛與慈悲',
    'growth.spread.base.7.position5': '喉輪 - 自我表達',
    'growth.spread.base.7.position6': '眉心輪 - 直覺與智慧',
    'growth.spread.base.7.position7': '頂輪 - 靈性連結',

    'growth.spread.base.10.position1': '當前狀況',
    'growth.spread.base.10.position2': '成長挑戰',
    'growth.spread.base.10.position3': '過去',
    'growth.spread.base.10.position4': '未來',
    'growth.spread.base.10.position5': '期望',
    'growth.spread.base.10.position6': '基礎',
    'growth.spread.base.10.position7': '你的應對方式',
    'growth.spread.base.10.position8': '外在影響',
    'growth.spread.base.10.position9': '內在的希望與恐懼',
    'growth.spread.base.10.position10': '最終成長潛能',

    'growth.spread.base.12.position1': '一月 - 新的開始',
    'growth.spread.base.12.position2': '二月 - 自我反思',
    'growth.spread.base.12.position3': '三月 - 採取行動',
    'growth.spread.base.12.position4': '四月 - 建立穩定',
    'growth.spread.base.12.position5': '五月 - 接納改變',
    'growth.spread.base.12.position6': '六月 - 尋求平衡',
    'growth.spread.base.12.position7': '七月 - 內在智慧',
    'growth.spread.base.12.position8': '八月 - 個人力量',
    'growth.spread.base.12.position9': '九月 - 完成與收穫',
    'growth.spread.base.12.position10': '十月 - 轉化',
    'growth.spread.base.12.position11': '十一月 - 內在修行',
    'growth.spread.base.12.position12': '十二月 - 整合與慶祝',


    // Mental Healing
    'healing.chooseHealingSpread': '選擇您的療癒牌陣',
    'healing.selectCardCount': '為您的療癒占卜選擇塔羅牌數量',
    'healing.healingTypeTitle': '療癒類型',
    'healing.selectHealingType': '選擇您需要指導的療癒類型',
    'healing.focusHealingIntention': '專注您的療癒意圖',
    'healing.healingIntentionDesc': '您希望在療癒旅程的哪個具體方面獲得指導？',
    'healing.healingPlaceholder': '您想要療癒或轉化什麼？（可選）',
    'healing.drawHealingCard': '抽取您的療癒卡',
    'healing.startDrawing': '開始抽塔羅牌',
    'healing.cards': '張牌',
    'healing.focusAreas': '重點領域:',

    // Healing spread position translations
    // Self Healing
    'healing.spread.selfHealing.1.position1': '當前的療癒焦點',
    'healing.spread.selfHealing.3.position1': '內在的傷口',
    'healing.spread.selfHealing.3.position2': '自我慈悲的練習',
    'healing.spread.selfHealing.3.position3': '療癒的整合',
    'healing.spread.selfHealing.5.position1': '核心傷痛',
    'healing.spread.selfHealing.5.position2': '自我評判',
    'healing.spread.selfHealing.5.position3': '內在力量',
    'healing.spread.selfHealing.5.position4': '自我照顧的行動',
    'healing.spread.selfHealing.5.position5': '被療癒的自我',

    // Energy Healing
    'healing.spread.energyHealing.1.position1': '能量焦點',
    'healing.spread.energyHealing.3.position1': '能量阻塞',
    'healing.spread.energyHealing.3.position2': '當前的能量狀態',
    'healing.spread.energyHealing.3.position3': '能量淨化之路',
    'healing.spread.energyHealing.5.position1': '能量消耗',
    'healing.spread.energyHealing.5.position2': '被阻塞的脈輪',
    'healing.spread.energyHealing.5.position3': '自然能量',
    'healing.spread.energyHealing.5.position4': '能量修習',
    'healing.spread.energyHealing.5.position5': '平衡的能量',

    // Soulmate Healing
    'healing.spread.soulmateHealing.1.position1': '靈魂連結焦點',
    'healing.spread.soulmateHealing.3.position1': '靈魂創傷',
    'healing.spread.soulmateHealing.3.position2': '當前的連結',
    'healing.spread.soulmateHealing.3.position3': '神聖之愛之路',
    'healing.spread.soulmateHealing.5.position1': '前世的模式',
    'healing.spread.soulmateHealing.5.position2': '當前的挑戰',
    'healing.spread.soulmateHealing.5.position3': '靈魂力量',
    'healing.spread.soulmateHealing.5.position4': '療癒行動',
    'healing.spread.soulmateHealing.5.position5': '靈魂融合',


    // Pregnancy
    'healing.spread.pregnancy.1.position1': '受孕焦點',
    'healing.spread.pregnancy.3.position1': '受孕阻礙',
    'healing.spread.pregnancy.3.position2': '當前狀態',
    'healing.spread.pregnancy.3.position3': '受孕之路',
    'healing.spread.pregnancy.5.position1': '身體準備度',
    'healing.spread.pregnancy.5.position2': '情感阻礙',
    'healing.spread.pregnancy.5.position3': '自然生育力',
    'healing.spread.pregnancy.5.position4': '所需支持',
    'healing.spread.pregnancy.5.position5': '新生命能量',

    // Physical Healing
    'healing.spread.physicalHealing.1.position1': '身體焦點',
    'healing.spread.physicalHealing.3.position1': '身體失衡',
    'healing.spread.physicalHealing.3.position2': '身體智慧',
    'healing.spread.physicalHealing.3.position3': '療癒方案',
    'healing.spread.physicalHealing.5.position1': '根本原因',
    'healing.spread.physicalHealing.5.position2': '身體抗拒',
    'healing.spread.physicalHealing.5.position3': '自然療癒力',
    'healing.spread.physicalHealing.5.position4': '生活方式改變',
    'healing.spread.physicalHealing.5.position5': '恢復健康',

    // Rejection Healing
    'healing.spread.rejectionHealing.1.position1': '自信焦點',
    'healing.spread.rejectionHealing.3.position1': '被拒絕的傷口',
    'healing.spread.rejectionHealing.3.position2': '當前自我價值',
    'healing.spread.rejectionHealing.3.position3': '自信之路',
    'healing.spread.rejectionHealing.5.position1': '核心拒絕經驗',
    'healing.spread.rejectionHealing.5.position2': '自我懷疑',
    'healing.spread.rejectionHealing.5.position3': '內在價值',
    'healing.spread.rejectionHealing.5.position4': '療癒實踐',
    'healing.spread.rejectionHealing.5.position5': '重建自信',


    // Base healing spread positions
    'healing.spread.base.6.position1': '心智療癒',
    'healing.spread.base.6.position2': '身體療癒',
    'healing.spread.base.6.position3': '情感療癒',
    'healing.spread.base.6.position4': '靈性療癒',
    'healing.spread.base.6.position5': '過去的療癒',
    'healing.spread.base.6.position6': '未來的完整',

    'healing.spread.base.7.position1': '根輪 - 安全與穩定',
    'healing.spread.base.7.position2': '腹輪 - 情感與創造力',
    'healing.spread.base.7.position3': '太陽神經叢 - 個人力量',
    'healing.spread.base.7.position4': '心輪 - 愛與慈悲',
    'healing.spread.base.7.position5': '喉輪 - 表達與真理',
    'healing.spread.base.7.position6': '眉心輪 - 直覺與清晰',
    'healing.spread.base.7.position7': '頂輪 - 靈性連結',

    'healing.spread.base.10.position1': '當前狀況',
    'healing.spread.base.10.position2': '主要挑戰',
    'healing.spread.base.10.position3': '過去的影響',
    'healing.spread.base.10.position4': '近期的未來',
    'healing.spread.base.10.position5': '期望',
    'healing.spread.base.10.position6': '潛能',
    'healing.spread.base.10.position7': '你的方式',
    'healing.spread.base.10.position8': '外部支持',
    'healing.spread.base.10.position9': '內在智慧',
    'healing.spread.base.10.position10': '療癒未來結果',


    'healing.spread.base.12.position1': '一月 - 新的療癒循環',
    'healing.spread.base.12.position2': '二月 - 情感釋放',
    'healing.spread.base.12.position3': '三月 - 採取行動',
    'healing.spread.base.12.position4': '四月 - 建立基礎',
    'healing.spread.base.12.position5': '五月 - 成長與改變',
    'healing.spread.base.12.position6': '六月 - 平衡與和諧',
    'healing.spread.base.12.position7': '七月 - 內在反思',
    'healing.spread.base.12.position8': '八月 - 個人力量',
    'healing.spread.base.12.position9': '九月 - 統合',
    'healing.spread.base.12.position10': '十月 - 轉化',
    'healing.spread.base.12.position11': '十一月 - 深層療癒',
    'healing.spread.base.12.position12': '十二月 - 更新與慶祝',

    'healing.singleHealingFocus': '單一療癒焦點',
    'healing.singleHealingFocusDesc': '專注於一個關鍵療癒領域',
    'healing.healingTriangle': '療癒三角牌陣',
    'healing.healingTriangleDesc': '根本原因、當前狀態、療癒之路',
    'healing.healingCross': '療癒十字牌陣',
    'healing.healingCrossDesc': '全方位的療癒指引',
    'healing.holisticHealing': '整體療癒',
    'healing.holisticHealingDesc': '心智、身體、靈魂、情感、過去與未來的療癒',
    'healing.chakraHealing': '脈輪療癒',
    'healing.chakraHealingDesc': '透過能量中心的療癒',
    'healing.deepHealingJourney': '深層療癒之旅',
    'healing.deepHealingJourneyDesc': '完整的療癒與轉化牌陣',
    'healing.yearOfHealing': '療癒之年',
    'healing.yearOfHealingDesc': '每月的療癒指導與進展',
    'healing.selfHealing': '自我療癒',
    'healing.selfHealingDesc': '內在修煉與個人療癒旅程',
    'healing.selfCompassion': '自我慈悲',
    'healing.innerChildWork': '內在小孩療癒',
    'healing.personalBoundaries': '個人界線',
    'healing.selfForgiveness': '自我寬恕',
    'healing.energyHealing': '能量療癒',
    'healing.energyHealingDesc': '清理與平衡能量場',
    'healing.chakraAlignment': '脈輪調和',
    'healing.auraCleansing': '光環淨化',
    'healing.energyBlocks': '能量阻塞',
    'healing.spiritualProtection': '靈性防護',
    'healing.soulmateHealing': '靈魂伴侶療癒',
    'healing.soulmateHealingDesc': '針對深層靈魂連結與雙生火焰旅程的療癒',
    'healing.soulContracts': '靈魂契約',
    'healing.twinFlameHealing': '雙生火焰療癒',
    'healing.karmicRelationships': '業力關係',
    'healing.divineLove': '神聖之愛',
    'healing.pregnancyFertility': '懷孕與受孕',
    'healing.pregnancyFertilityDesc': '為受孕、懷孕與母性提供療癒支持',
    'healing.fertilityJourney': '受孕旅程',
    'healing.pregnancySupport': '懷孕支持',
    'healing.maternalHealing': '母性療癒',
    'healing.newLifeEnergy': '新生命能量',
    'healing.physicalHealing': '身體療癒',
    'healing.physicalHealingDesc': '支持身體的自然療癒過程',
    'healing.bodyWisdom': '身體智慧',
    'healing.healingEnergy': '療癒能量',
    'healing.vitalityRestoration': '活力恢復',
    'healing.physicalStrength': '身體力量',
    'healing.rejectionHealing': '拒絕創傷療癒',
    'healing.rejectionHealingDesc': '將拒絕的傷痛轉化為智慧與力量',
    'healing.selfWorthHealing': '自我價值療癒',
    'healing.rejectionRecovery': '拒絕後的復原',
    'healing.confidenceRebuilding': '自信重建',
    'healing.trustRestoration': '信任重建',
    'healing.reset': '重設',


    // General Spread
    'general.generalReading': '綜合占卜',
    'general.chooseSpreadSize': '選擇您的牌陣大小',
    'general.selectCardCount': '為您的占卜選擇塔羅牌數量',
    'general.singleCard': '單張牌',
    'general.singleCardDesc': '針對您的問題提供快速指引',
    'general.pastPresentFuture': '過去、現在、未來',
    'general.pastPresentFutureDesc': '以時間軸的角度解析您的狀況',
    'general.fiveCardCross': '五張十字牌陣',
    'general.fiveCardCrossDesc': '顯示狀況、挑戰、過去、未來與結果',
    'general.hexagramSpread': '六芒星牌陣',
    'general.hexagramSpreadDesc': '針對您的人生道路提供全面指引',
    'general.chakraAlignment': '脈輪平衡',
    'general.chakraAlignmentDesc': '協調生活中各個層面的能量平衡',
    'general.celticCross': '凱爾特十字塔羅牌陣',
    'general.celticCrossDesc': '深入洞察您的現況與未來',
    'general.yearAhead': '年度預測',
    'general.yearAheadDesc': '提供全年每月的指引',
    'general.pastInfluences': '過去的影響',
    'general.presentSituation': '當前情況',
    'general.futurePotential': '未來潛能',
    'general.currentSituation': '現況',
    'general.challengeCross': '挑戰／十字',
    'general.pastFoundation': '過去的基礎',
    'general.futurePossibility': '未來的可能性',
    'general.finalOutcome': '最終結果',
    'general.generalGuidanceCard': '整體指導卡',
    'general.focusIntention': '專注您的意圖',
    'general.centerMindQuestion': '花片刻時間讓心靜下來，專注於您的問題',
    'general.drawGuidanceCard': '抽取您的指導卡',
    'general.drawCards': '抽牌',
    'general.reset': '重設',
    'general.questionPlaceholder': '您想了解什麼？（例如：「我該換工作嗎？」「如何改善健康？」「這個月該專注於什麼？」）— 選填',

    // General spread position translations (Chinese placeholders)
    'general.spread.1.position1': '啟示',

    'general.spread.3.position1': '過去的影響',
    'general.spread.3.position2': '當前情況',
    'general.spread.3.position3': '未來潛能',

    'general.spread.5.position1': '現況',
    'general.spread.5.position2': '挑戰／十字',
    'general.spread.5.position3': '過去的基礎',
    'general.spread.5.position4': '未來的可能性',
    'general.spread.5.position5': '最終結果',

    'general.spread.6.position1': '內在自我',
    'general.spread.6.position2': '當前道路',
    'general.spread.6.position3': '隱藏的影響',
    'general.spread.6.position4': '您的優勢',
    'general.spread.6.position5': '外部因素',
    'general.spread.6.position6': '前進的指引',

    'general.spread.7.position1': '根輪 — 基礎與穩定',
    'general.spread.7.position2': '臍輪 — 創造力',
    'general.spread.7.position3': '太陽神經叢 — 個人力量',
    'general.spread.7.position4': '心輪 — 情感',
    'general.spread.7.position5': '喉輪 — 溝通',
    'general.spread.7.position6': '眉心輪 — 直覺',
    'general.spread.7.position7': '頂輪 — 靈性指引',

    'general.spread.10.position1': '當前狀況',
    'general.spread.10.position2': '挑戰',
    'general.spread.10.position3': '過去',
    'general.spread.10.position4': '未來',
    'general.spread.10.position5': '期望的結果',
    'general.spread.10.position6': '基礎和潛能',
    'general.spread.10.position7': '您的應對方式',
    'general.spread.10.position8': '外部影響',
    'general.spread.10.position9': '希望與恐懼',
    'general.spread.10.position10': '占卜結果',

    'general.spread.12.position1': '一月',
    'general.spread.12.position2': '二月',
    'general.spread.12.position3': '三月',
    'general.spread.12.position4': '四月',
    'general.spread.12.position5': '五月',
    'general.spread.12.position6': '六月',
    'general.spread.12.position7': '七月',
    'general.spread.12.position8': '八月',
    'general.spread.12.position9': '九月',
    'general.spread.12.position10': '十月',
    'general.spread.12.position11': '十一月',
    'general.spread.12.position12': '十二月',

    // Inspirational Quotes
    'quotes.love': [
        "我的命中注定之人是怎樣的人？",
        "那個人現在是怎麼看我的？",
        "我該主動還是退一步？請教我愛情的攻防之道！",
        "誰正在暗戀我？",
        "我的伴侶有出軌的念頭嗎？！",
        "為什麼那個人渴望我的身體？",
        "我的下一段戀情會在什麼時候出現？",
        "我還能和那個讓我放不下的人復合嗎？",
        "為什麼他明明已讀卻不回我訊息？",
        "那個人想見我嗎？",
        "我會迎來我的桃花期嗎？",
        "那個人是我的命定之人嗎？",
        "現在可以傳訊息給他嗎？",
        "如果我現在告白，會發生什麼事？",
        "愛上已經有伴侶的人可以嗎？",
        "我會遇到讓我心動的人嗎？",
        "他是喜歡我，還是覺得我煩？",
        "那個人現在是不是對我有所隱瞞？",
        "這段愛情的終點，以及即將到來的新戀情。",
        "他對我的第一印象是真實的嗎？",
        "我應該原諒我的伴侶嗎？",
        "那個人對我有意思嗎？",
        "我可以相信伴侶的愛嗎？",
        "為什麼我總是無法擁有戀人？",
        "我該邁向下一段戀情嗎？",
        "我和那個人的契合度如何？",
        "我的前任是否已經開始新的戀情？"
    ],
    'quotes.career': [
      "我應該照現在的想法去做嗎？",
      "我該換工作嗎？",
      "我能找到一份更好的工作嗎？",
      "我能升職加薪嗎？",
      "我該創業嗎？",
      "我能在職場上獲得成功嗎？",
      "我該如何改善與同事的關係？",
      "我能克服工作中的挑戰嗎？",
      "我該如何提升我的專業技能？",
      "我能在新的工作環境中適應得很好嗎？",
      "我該如何平衡工作與生活？",
      "我能找到一份讓我感到滿足的工作嗎？",
      "我該如何應對工作中的壓力？",
      "我能在職場上建立良好的人際關係嗎？",
      "我該如何實現我的職業目標？",
      "我能在工作中發揮我的潛力嗎？",
      "我該如何提升我的領導能力？",
      "我能在職場上獲得認可和尊重嗎？",
      "我該如何應對工作中的變化？",
      "我能在職場上保持積極的態度嗎？",
      "我該如何提升我的時間管理能力？",
      "我能在工作中找到更多的樂趣嗎？",
      "我該如何應對工作中的挑戰和困難？",
      "我能在職場上實現我的夢想嗎？",
      "我該如何提升我的溝通能力？",
      "我的真正使命是什麼？",
      "現在是換工作的好時機嗎？",
      "現在的工作適合我嗎？",
      "我未來的收入是多少？",
      "換工作？獨立工作？兼職？對我來說，最好的工作方式是什麼？",
      "我該如何與相處不融洽的老闆或下屬相處？",
      "我在工作上是如何被評估的？",
      "我能克服工作中的失敗嗎？"
    ],
    'quotes.growth': [
      "我應該照現在的想法去做嗎？",
      "我能變得富有嗎？",
      "我怎樣才能變得幸福？",
      "未來三個月，我將面臨一個決定命運的抉擇。",
      "這個選擇是錯的嗎？",
      "人們對我的印像是我始料未及的。",
      "我應該疏遠那個我不喜歡的人嗎？",
      "這個問題會解決嗎？",
      "我該如何修復與一個鬧翻了的朋友的關係？",
      "我該追尋我的夢想嗎？",
      "這是一年中最幸運的時刻嗎？",
      "我會在新的環境中茁壯成長嗎？",
      "當你身心煥然一新時，幸福就在你面前。",
      "我可以和誰在一起，讓自己充滿活力？",
      "哪些生活習慣可以改善你的財運？",
      "我該如何提升我的自信心？",
      "我能克服生活中的挑戰嗎？",
      "我該如何實現我的個人成長目標？",
      "我能在生活中找到更多的快樂嗎？",
      "我該如何提升我的人際關係技能？",
      "我能在生活中保持積極的態度嗎？",
      "我該如何提升我的情緒管理能力？",
      "我能在生活中找到更多的意義嗎？",
      "我該如何提升我的自律能力？",
      "我能在生活中實現我的夢想嗎？",
      "我該如何提升我的創造力？",
      "一場浪漫的邂逅在等著我？",
      "未來三個月需要注意的事項？",
      "年底前會發生的邂逅？",
      "我將如何花掉今年最後的錢？"
    ],
    'quotes.healing': [
      "我現在可以做些什麼來改善我的整體健康狀況？",
      "我應該關注身體的哪個方面？",
      "我如何才能更好地照顧自己？",
      "我目前的健康狀況有什麼需要注意的地方？",
      "我應該採取哪些步驟來促進我的身體康復？",
      "我如何才能更好地理解我的身體訊號？",
      "我應該關注哪些生活習慣來改善我的健康？",
      "我如何才能更好地管理我的壓力水平？",
      "我應該尋求哪些替代療法來支持我的健康？",
      "我如何才能更好地平衡我的身心靈？",
      "塔羅牌對我的身體健康有什麼啟示？",
      "我可以採取哪些步驟來支持我的心理健康？",
      "我如何培養我的情緒療癒？",
      "哪些靈性修行可以幫助我的療癒之旅？",
      "是什麼阻礙了我的療癒過程？",
      "我如何為自己創造一個療癒環境？",
      "寬恕在我的療癒中扮演什麼角色？",
      "我如何才能更好地傾聽我身體的需求？",
      "我應該探索哪些療癒方式？",
      "我目前健康問題的根本原因是什麼？",
      "我如何在療癒期間培養更多的自愛和同情心？",
      "哪些生活方式的改變可以支持我的療癒之旅？",
      "我如何平衡我的身心和精神以獲得最佳健康？",
      "我應該在療癒過程中學到什麼？",
      "我如何釋放阻礙我療癒的過去創傷？",
      "我應該尋求哪些支持系統來幫助我療癒？",
      "我目前的生活方式是否支持我的身體需求？",
      "我如何才能更好地平衡我的身心？",
      "是什麼阻礙了我的康復過程？",
      "我目前壓力或焦慮的根源是什麼？",
      "我如何找到更多的平靜和情緒穩定？",
      "我需要做出哪些改變來支持我的心理健康？",
      "在這場情緒鬥爭中，我需要學習什麼教訓？",
      "我如何釋放影響我健康的負能量或自我破壞模式？",
      "我的身體現在最需要什麼來治癒？",
      "我應該採取哪些步驟來從疾病或傷害中恢復？",
      "我如何增強我的免疫系統或活力？",
      "哪些生活習慣正在損害或幫助我的健康？",
      "我如何才能最好地支持我的身體康復之旅？",
      "哪些情感創傷需要我關注才能治癒？",
      "我如何在康復過程中培養更多的自愛和同情心？",
      "哪些精神實踐可以幫助我的整體康復和幸福？",
      "我的身體是否試圖透過目前的症狀來告訴我什麼？",
      "我該如何調整我的能量以促進健康？",
      "哪些精神修行可以改善我的健康？",
      "哪些業力或情緒模式可能會影響我的健康？",
      "我需要釋放什麼才能體驗完全的療癒和活力？"
    ],
    'quotes.general': [
      "我人生的真正目標是什麼？",
      "我現在應該朝哪個方向走？",
      "在人生旅程的這個階段，我應該吸取哪些教訓？",
      "我需要做出哪些改變才能與更高的自我保持一致？",
      "是什麼阻礙了我的個人成長和精神進步？",
      "我的愛情生活未來會怎樣？",
      "我如何才能擁有健康、充滿愛的感情？",
      "我應該從過去的感情中學到哪些教訓？",
      "我如何才能鞏固我現在的感情？",
      "我應該從過去的感情中學到哪些教訓？",
      "我能做些什麼來鞏固我現在的感情？",
      "這個人適合我嗎，還是我應該離開他？",
      "我如何才能吸引到合適的伴侶？",
      "我如何才能改善我與伴侶的溝通？",
      "我如何才能更好地理解我的伴侶？",
      "我如何才能在感情中建立更多的信任？",
      "我如何才能在感情中培養更多的親密感？",
      "我如何才能釋放過去的感情創傷？",
      "我如何才能更好地愛自己？",
      "我如何才能在感情中保持獨立性？",
      "我如何才能在感情中找到更多的快樂？",
      "我如何才能在感情中實現我的夢想？",
      "我如何才能在感情中找到更多的意義？",
      "我如何才能在感情中保持積極的態度？",
      "我如何才能在感情中提升我的自信心？",
      "我如何才能在感情中提升我的溝通能力？",
      "我如何才能在感情中提升我的情緒管理能力？",
      "我如何才能在感情中提升我的創造力？",
      "我如何才能在感情中提升我的自律能力？",
      "我如何才能在感情中實現我的夢想？",
      "我如何才能在感情中找到更多的意義？",
      "我如何才能在感情中保持積極的態度？",
      "哪條職業道路最符合我的才能和目標？",
      "我應該追求哪種職業？",
      "我應該換工作嗎？",
      "我能找到一份更好的工作嗎？",
      "我能升職加薪嗎？",
      "我該創業嗎？",
      "我能在職場上獲得成功嗎？",
      "我該如何改善與同事的關係？",
      "我能克服工作中的挑戰嗎？",
      "我該如何提升我的專業技能？",
      "我能在新的工作環境中適應得很好嗎？",
      "我該如何平衡工作與生活？",
      "我能找到一份讓我感到滿足的工作嗎？",
      "我該如何應對工作中的壓力？",
      "我能在職場上建立良好的人際關係嗎？",
      "我該如何實現我的職業目標？",
      "我能在工作中發揮我的潛力嗎？",
      "我該如何提升我的領導能力？",
      "我能在職場上獲得認可和尊重嗎？",
      "我該如何應對工作中的變化？",
      "我能在職場上保持積極的態度嗎？",
      "我該如何提升我的時間管理能力？",
      "我能在工作中找到更多的樂趣嗎？",
      "我該如何應對工作中的挑戰和困難？",
      "我能在職場上實現我的夢想嗎？",
      "我如何才能改善我的財務狀況？",
      "我如何在工作中獲得更大的成就感？",
      "工作中有哪些機會？",
      "我應該培養哪些技能來提升我的職業發展？",
      "實現我目標的最佳方法是什麼？"
    ],
    
    // Card Options
    'cards.singleCard': '單張牌',
    'cards.pastPresentFuture': '過去、現在、未來',
    'cards.loveCross': '愛情十字',
    'cards.relationshipSpread': '關係牌陣',
    'cards.chakraLove': '脈輪愛情',
    'cards.celticCross': '凱爾特十字塔羅牌陣',
    'cards.zodiacLove': '全年愛情運占卜',
    
    // Card Descriptions
    'cards.singleCardDesc': '您愛情生活的現狀',
    'cards.pastPresentFutureDesc': '您浪漫生活的旅程',
    'cards.loveCrossDesc': '想了解他 / 她在想什麼',
    'cards.relationshipSpreadDesc': '全面的關係分析',
    'cards.chakraLoveDesc': '通過脈輪的愛情能量',
    'cards.celticCrossDesc': '深入了解您的愛情狀況和未來',
    'cards.zodiacLoveDesc': '完整的愛情預測',
    
// Career Spread
    'career.title': '事業與目標占卜',
    'career.subtitle': '探索您職業旅程與人生目標的洞見',
    'career.chooseCareerSpread': '選擇您的事業牌陣',
    'career.selectCareerSituation': '選擇您的事業狀況',
    'career.askCareerQuestion': '提出您的事業問題',
    'career.drawCareerCard': '抽取您的事業牌',
    'career.careerCardOfDay': '今日事業牌',
    'career.startCareerReading': '開始事業塔羅占卜',
    'career.careerCross': '事業十字陣',
    'career.careerCrossDesc': '技能、挑戰與機會',
    'career.professionalPath': '職業之路',
    'career.professionalPathDesc': '全面的職業分析',
    'career.careerChakra': '事業脈輪',
    'career.careerChakraDesc': '工作能量的對齊',
    'career.zodiacCareer': '星座事業',
    'career.zodiacCareerDesc': '完整的職業運勢預測',
    'career.reset': '重設',
    'career.singleCardDesc': '您事業的當前狀態',
    'career.pastPresentFutureDesc': '您的職業旅程時間線',
    'career.celticCrossDesc': '深入探索您的事業狀況',
    'career.currentJob': '目前工作與職場',
    'career.jobSeeking': '求職與新機會',
    'career.entrepreneur': '創業與商業',
    'career.currentJobDesc': '在目前角色中導航挑戰與機會',
    'career.jobSeekingDesc': '為您的求職與新職涯道路提供指引',
    'career.entrepreneurDesc': '創業或擴展事業的洞察',
    'career.focusAreas': '重點領域：',
    'career.questionPlaceholder': '您想了解關於職業道路的哪一部分？（可選）',
    'career.identifyPassions': '識別熱情',
    'career.marketOpportunities': '市場機會',
    'career.manifestationTechniques': '顯化技巧',
    'career.soulPurpose': '靈魂目的',
    'career.naturalTalents': '天賦才能',
    'career.serviceToOthers': '服務他人',
    'career.developCareerPath': '發展您的職業道路',
    'career.careerPathDesc': '導航您的職業方向與成長',
    'career.preparationStrategies': '準備策略',
    'career.firstImpressions': '第一印象',
    'career.integrationTips': '融入建議',
    'career.strategicPlanning': '策略規劃',
    'career.skillDevelopment': '技能發展',
    'career.longTermVision': '長期願景',
    'career.workplaceCollaboration': '職場合作',
    'career.workplaceCollaborationDesc': '改善職場人際關係與團隊合作',
    'career.communicationSkills': '溝通技巧',
    'career.teamDynamics': '團隊動態',
    'career.leadershipPresence': '領導氣場',
    'career.careerType': '職業類型',
    'career.selectCareerGuidance': '選擇您想要的職業指引類型',
    'career.selectNumberCards': '選擇事業占卜的牌數',
    'career.focusIntention': '專注您的意圖',
    'career.centerMindQuestion': '花片刻時間讓心靜下來，聚焦於您的具體事業問題',
    'career.startDrawingCards': '開始抽牌',
    'career.reset': '重設',
    'career.careerGuidance': '職業指引',

    // Career Spread Card Positions - New Job
    'career.spread.newJob.1.position1': '工作運',
    'career.spread.newJob.3.position1': '過去經驗',
    'career.spread.newJob.3.position2': '當前準備',
    'career.spread.newJob.3.position3': '新潛能',
    'career.spread.newJob.5.position1': '您的技能',
    'career.spread.newJob.5.position2': '主要挑戰',
    'career.spread.newJob.5.position3': '團隊融入',
    'career.spread.newJob.5.position4': '成長機會',
    'career.spread.newJob.5.position5': '工作成果',
    'career.spread.newJob.6.position1': '當前狀況',
    'career.spread.newJob.6.position2': '您的獨特價值',
    'career.spread.newJob.6.position3': '需要培養的技能',
    'career.spread.newJob.6.position4': '潛在障礙',
    'career.spread.newJob.6.position5': '最佳時機',
    'career.spread.newJob.6.position6': '工作成就',
    'career.spread.newJob.7.position1': '根輪 - 工作安全的基礎',
    'career.spread.newJob.7.position2': '腹輪 - 創意貢獻',
    'career.spread.newJob.7.position3': '太陽神經叢 - 專業自信',
    'career.spread.newJob.7.position4': '心輪 - 工作熱情',
    'career.spread.newJob.7.position5': '喉輪 - 溝通技巧',
    'career.spread.newJob.7.position6': '眉心輪 - 職業願景',
    'career.spread.newJob.7.position7': '頂輪 - 專業使命',
    'career.spread.newJob.10.position1': '當前工作狀況',
    'career.spread.newJob.10.position2': '主要挑戰',
    'career.spread.newJob.10.position3': '職業過去',
    'career.spread.newJob.10.position4': '職業未來',
    'career.spread.newJob.10.position5': '最高潛能',
    'career.spread.newJob.10.position6': '近期工作變化',
    'career.spread.newJob.10.position7': '您的策略',
    'career.spread.newJob.10.position8': '公司觀點',
    'career.spread.newJob.10.position9': '職業希望與恐懼',
    'career.spread.newJob.10.position10': '最終工作成果',
    'career.spread.newJob.12.position1': '第1-2月:有力的開始',
    'career.spread.newJob.12.position2': '第3-4月:建立關係',
    'career.spread.newJob.12.position3': '第5-6月:展現自我',
    'career.spread.newJob.12.position4': '第7-8月:主動行動',
    'career.spread.newJob.12.position5': '第9-10月:獲得認可',
    'career.spread.newJob.12.position6': '第11-12月:建立專業地位',
    'career.spread.newJob.12.position7': '核心專業強項',
    'career.spread.newJob.12.position8': '主要挑戰',
    'career.spread.newJob.12.position9': '最佳成長機會',
    'career.spread.newJob.12.position10': '策略性職業建議',
    'career.spread.newJob.12.position11': '最佳職業時機',
    'career.spread.newJob.12.position12': '長期成功願景',

// Career Spread Card Positions - Dream Job
    'career.spread.dreamJob.1.position1': '夢想職業能量',
    'career.spread.dreamJob.3.position1': '過去模式',
    'career.spread.dreamJob.3.position2': '當前動機',
    'career.spread.dreamJob.3.position3': '實現之路',
    'career.spread.dreamJob.5.position1': '您的熱情',
    'career.spread.dreamJob.5.position2': '市場現實',
    'career.spread.dreamJob.5.position3': '技能差距',
    'career.spread.dreamJob.5.position4': '策略行動',
    'career.spread.dreamJob.5.position5': '職業實現',
    'career.spread.dreamJob.6.position1': '當前的不滿足',
    'career.spread.dreamJob.6.position2': '待開發的隱藏才能',
    'career.spread.dreamJob.6.position3': '夢想職業願景',
    'career.spread.dreamJob.6.position4': '需要克服的障礙',
    'career.spread.dreamJob.6.position5': '可獲得的支持',
    'career.spread.dreamJob.6.position6': '成就時間線',
    'career.spread.dreamJob.7.position1': '根輪 - 夢想工作的財務安全',
    'career.spread.dreamJob.7.position2': '腹輪 - 創意表達',
    'career.spread.dreamJob.7.position3': '太陽神經叢 - 個人力量',
    'career.spread.dreamJob.7.position4': '心輪 - 工作滿足感',
    'career.spread.dreamJob.7.position5': '喉輪 - 自我表達',
    'career.spread.dreamJob.7.position6': '眉心輪 - 職業清晰度',
    'career.spread.dreamJob.7.position7': '頂輪 - 人生一致性',
    'career.spread.dreamJob.10.position1': '當前工作的現實',
    'career.spread.dreamJob.10.position2': '內在阻礙',
    'career.spread.dreamJob.10.position3': '潛意識渴望',
    'career.spread.dreamJob.10.position4': '顯意識目標',
    'career.spread.dreamJob.10.position5': '可能的夢想結果',
    'career.spread.dreamJob.10.position6': '近期職業事件',
    'career.spread.dreamJob.10.position7': '您的實現方式',
    'career.spread.dreamJob.10.position8': '他人的觀點',
    'career.spread.dreamJob.10.position9': '夢想與抱負',
    'career.spread.dreamJob.10.position10': '職業可發展的未來',
    'career.spread.dreamJob.12.position1': '第1-2月: 願景清晰',
    'career.spread.dreamJob.12.position2': '第3-4月: 技能培養',
    'career.spread.dreamJob.12.position3': '第5-6月: 人脈擴展',
    'career.spread.dreamJob.12.position4': '第7-8月: 創造機會',
    'career.spread.dreamJob.12.position5': '第9-10月: 勇敢躍進',
    'career.spread.dreamJob.12.position6': '第11-12月: 夢想實現',
    'career.spread.dreamJob.12.position7': '核心職業強項',
    'career.spread.dreamJob.12.position8': '主要挑戰',
    'career.spread.dreamJob.12.position9': '最佳機會窗口',
    'career.spread.dreamJob.12.position10': '神聖職業指引',
    'career.spread.dreamJob.12.position11': '完美時機',
    'career.spread.dreamJob.12.position12': '最終夢想成功',

    // Career Spread Card Positions - Life Purpose
    'career.spread.lifePurpose.1.position1': '靈魂使命',
    'career.spread.lifePurpose.3.position1': '過去的職業',
    'career.spread.lifePurpose.3.position2': '當前的覺醒',
    'career.spread.lifePurpose.3.position3': '顯化之旅',
    'career.spread.lifePurpose.5.position1': '天生的靈魂本質',
    'career.spread.lifePurpose.5.position2': '人生課題',
    'career.spread.lifePurpose.5.position3': '服務領域的召喚',
    'career.spread.lifePurpose.5.position4': '成長之路',
    'career.spread.lifePurpose.5.position5': '使命的實現',
    'career.spread.lifePurpose.6.position1': '當前的目標困惑',
    'career.spread.lifePurpose.6.position2': '隱藏的人生使命',
    'career.spread.lifePurpose.6.position3': '真實的召喚願景',
    'career.spread.lifePurpose.6.position4': '靈性阻礙',
    'career.spread.lifePurpose.6.position5': '可獲得的神聖支持',
    'career.spread.lifePurpose.6.position6': '覺醒的職業道路',
    'career.spread.lifePurpose.7.position1': '根輪 - 人生基礎與使命',
    'career.spread.lifePurpose.7.position2': '腹輪 - 創造性生活表達',
    'career.spread.lifePurpose.7.position3': '太陽神經叢 - 個人意志的調和',
    'career.spread.lifePurpose.7.position4': '心輪 - 以愛為本的服務',
    'career.spread.lifePurpose.7.position5': '喉輪 - 真理的表達',
    'career.spread.lifePurpose.7.position6': '眉心輪 - 靈性智慧',
    'career.spread.lifePurpose.7.position7': '頂輪 - 神聖連結',
    'career.spread.lifePurpose.10.position1': '當前人生狀況',
    'career.spread.lifePurpose.10.position2': '靈魂挑戰',
    'career.spread.lifePurpose.10.position3': '前世業力',
    'career.spread.lifePurpose.10.position4': '靈魂未來',
    'career.spread.lifePurpose.10.position5': '高我指引',
    'career.spread.lifePurpose.10.position6': '近期靈性課題',
    'career.spread.lifePurpose.10.position7': '靈魂之路策略',
    'career.spread.lifePurpose.10.position8': '世界服務的需求',
    'career.spread.lifePurpose.10.position9': '靈魂的願望',
    'career.spread.lifePurpose.10.position10': '宇宙人生藍圖',
    'career.spread.lifePurpose.12.position1': '靈性循環:覺醒',
    'career.spread.lifePurpose.12.position2': '靈性循環2:學習',
    'career.spread.lifePurpose.12.position3': '靈性循環3:發展',
    'career.spread.lifePurpose.12.position4': '靈性循環4:服務',
    'career.spread.lifePurpose.12.position5': '靈性循環5:精通',
    'career.spread.lifePurpose.12.position6': '靈性循環6:教導',
    'career.spread.lifePurpose.12.position7': '靈魂核心力量',
    'career.spread.lifePurpose.12.position8': '需療癒的業力挑戰',
    'career.spread.lifePurpose.12.position9': '神聖機會',
    'career.spread.lifePurpose.12.position10': '高層指引訊息',
    'career.spread.lifePurpose.12.position11': '神聖時機',
    'career.spread.lifePurpose.12.position12': '靈魂命運的實現',


    // Career Spread Card Positions - Career Path
    'career.spread.careerPath.1.position1': '職業方向',
    'career.spread.careerPath.3.position1': '已建立的基礎',
    'career.spread.careerPath.3.position2': '當前的十字路口',
    'career.spread.careerPath.3.position3': '最佳前進道路',
    'career.spread.careerPath.5.position1': '核心優勢',
    'career.spread.careerPath.5.position2': '行業趨勢',
    'career.spread.careerPath.5.position3': '技能發展',
    'career.spread.careerPath.5.position4': '人脈建立',
    'career.spread.careerPath.5.position5': '職業成就',
    'career.spread.careerPath.6.position1': '目前職位',
    'career.spread.careerPath.6.position2': '可運用的天賦才能',
    'career.spread.careerPath.6.position3': '職業願景清晰度',
    'career.spread.careerPath.6.position4': '需改進的發展領域',
    'career.spread.careerPath.6.position5': '市場力量的影響',
    'career.spread.careerPath.6.position6': '長期成長之路',
    'career.spread.careerPath.7.position1': '根輪 - 職業穩定的基礎',
    'career.spread.careerPath.7.position2': '腹輪 - 專業創新',
    'career.spread.careerPath.7.position3': '太陽神經叢 - 職業野心',
    'career.spread.careerPath.7.position4': '心輪 - 工作與生活的滿足',
    'career.spread.careerPath.7.position5': '喉輪 - 領導之聲',
    'career.spread.careerPath.7.position6': '眉心輪 - 策略願景',
    'career.spread.careerPath.7.position7': '頂輪 - 專業傳承',
    'career.spread.careerPath.10.position1': '當前狀況',
    'career.spread.careerPath.10.position2': '職業挑戰',
    'career.spread.careerPath.10.position3': '過去經驗',
    'career.spread.careerPath.10.position4': '未來潛力',
    'career.spread.careerPath.10.position5': '期待的成果',
    'career.spread.careerPath.10.position6': '基礎評估',
    'career.spread.careerPath.10.position7': '您的策略',
    'career.spread.careerPath.10.position8': '行業觀點',
    'career.spread.careerPath.10.position9': '職業希望與恐懼',
    'career.spread.careerPath.10.position10': '職業可發展未來',
    'career.spread.careerPath.12.position1': '第一季度：職業重點',
    'career.spread.careerPath.12.position2': '第二季度：技能發展',
    'career.spread.careerPath.12.position3': '第三季度：追求機會',
    'career.spread.careerPath.12.position4': '第四季度：成就目標',
    'career.spread.careerPath.12.position5': '第二年願景',
    'career.spread.careerPath.12.position6': '長期傳承目標',
    'career.spread.careerPath.12.position7': '關鍵專業強項',
    'career.spread.careerPath.12.position8': '主要挑戰應對',
    'career.spread.careerPath.12.position9': '最佳機會時機',
    'career.spread.careerPath.12.position10': '策略性職業建議',
    'career.spread.careerPath.12.position11': '最佳時機',
    'career.spread.careerPath.12.position12': '職業傳承成就',


    // Career Spread Card Positions - Workplace Collaboration
    'career.spread.workplace.1.position1': '團隊動能',
    'career.spread.workplace.3.position1': '過去經驗',
    'career.spread.workplace.3.position2': '當前動態',
    'career.spread.workplace.3.position3': '未來合作',
    'career.spread.workplace.5.position1': '您的溝通風格',
    'career.spread.workplace.5.position2': '團隊化學反應潛力',
    'career.spread.workplace.5.position3': '共同目標的一致性',
    'career.spread.workplace.5.position4': '衝突解決方式',
    'career.spread.workplace.5.position5': '集體成功的達成',
    'career.spread.workplace.6.position1': '當前的團隊緊張狀況',
    'career.spread.workplace.6.position2': '您的獨特貢獻',
    'career.spread.workplace.6.position3': '團隊願景的清晰度',
    'career.spread.workplace.6.position4': '溝通落差',
    'career.spread.workplace.6.position5': '領導支持',
    'career.spread.workplace.6.position6': '達成和諧之路',
    'career.spread.workplace.7.position1': '根輪 - 團隊安全的基礎',
    'career.spread.workplace.7.position2': '腹輪 - 創意團隊協同',
    'career.spread.workplace.7.position3': '太陽神經叢 - 共享權力平衡',
    'career.spread.workplace.7.position4': '心輪 - 相互尊重',
    'career.spread.workplace.7.position5': '喉輪 - 開放的溝通',
    'career.spread.workplace.7.position6': '眉心輪 - 集體願景',
    'career.spread.workplace.7.position7': '頂輪 - 統一的高層目的',
    'career.spread.workplace.10.position1': '當前狀況',
    'career.spread.workplace.10.position2': '協作挑戰',
    'career.spread.workplace.10.position3': '過去歷史',
    'career.spread.workplace.10.position4': '未來潛力',
    'career.spread.workplace.10.position5': '理想的成果',
    'career.spread.workplace.10.position6': '基礎',
    'career.spread.workplace.10.position7': '您的策略',
    'career.spread.workplace.10.position8': '他人的觀點',
    'career.spread.workplace.10.position9': '團隊願景與期望',
    'career.spread.workplace.10.position10': '集體成功的未來',
    'career.spread.workplace.12.position1': '第1個月:建立關係',
    'career.spread.workplace.12.position2': '第2個月:培養信任',
    'career.spread.workplace.12.position3': '第3個月:面對挑戰',
    'career.spread.workplace.12.position4': '第4個月:共同成長',
    'career.spread.workplace.12.position5': '第5個月:慶祝成功',
    'career.spread.workplace.12.position6': '第6個月:達成精通',
    'career.spread.workplace.12.position7': '溝通核心優勢',
    'career.spread.workplace.12.position8': '人際關係挑戰',
    'career.spread.workplace.12.position9': '團隊機會',
    'career.spread.workplace.12.position10': '領導指導',
    'career.spread.workplace.12.position11': '協作時機',
    'career.spread.workplace.12.position12': '團隊傳承的建立',

    
    // Payment
    'payment.title': '獲取 TAROCA 的個人化解析',
    'payment.price': '港幣$22',
    'payment.description': '解鎖您占卜結果的詳細 TAROCA 洞察分析',
    'payment.coupon': '有優惠券代碼嗎？點擊這裡',
    'payment.applyCoupon': '使用優惠券',
    'payment.processing': '正在處理付款...',
    'payment.success': '付款成功！',
    'payment.getExplanation': '獲取 TAROCA 解釋',
    
    // Auth
    'auth.login': '登入',
    'auth.register': '建立帳戶',
    'auth.email': '電子郵件',
    'auth.password': '密碼', 
    'auth.confirmPassword': '確認密碼',
    'auth.firstName': '名字',
    'auth.lastName': '姓氏',
    'auth.welcome': '加入我們開始您的塔羅之旅。',
    'auth.switchToLogin': '已有帳戶？登入',
    'auth.switchToRegister': '沒有帳戶？註冊',
    'auth.forgotPassword': '忘記密碼？',
    'auth.createAccount': '建立帳戶',
    'auth.signIn': '登入',
    'auth.resetPassword': '重置密碼',
    'auth.newPassword': '新密碼',
    'auth.resetTitle': '重置您的密碼',
    'auth.resetSubtitle': '在下方輸入您的新密碼',
    'auth.resetSuccess': '密碼重置成功！',
    'auth.invalidToken': '無效或過期的重置令牌',
    'auth.passwordMismatch': '密碼不匹配',
    'auth.backToLogin': '返回登入',
    'auth.subtitle': '登入以解鎖您的完整塔羅體驗',
    'auth.registerSubtitle': '建立您的帳戶開始塔羅之旅。全新註冊用戶可得到一次免費的解讀！',
    'auth.emailPlaceholder': '輸入您的電子郵件',
    'auth.passwordPlaceholder': '輸入您的密碼',
    'auth.firstNamePlaceholder': '輸入您的名字',
    'auth.lastNamePlaceholder': '輸入您的姓氏',
    'auth.confirmPasswordPlaceholder': '確認您的密碼',
    'auth.signingIn': '登入中...',
    'auth.creatingAccount': '建立帳戶中...',
    'auth.showPassword': '顯示密碼',
    'auth.hidePassword': '隱藏密碼',
    'auth.switchToRegisterText': '沒有帳戶？ ',
    'auth.switchToLoginText': '已有帳戶？ ',
    'auth.signUpLink': '註冊',
    'auth.signInLink': '登入',
    'auth.uspDrawTarot': '像真實一樣抽塔羅牌',
    'auth.uspTarotLibrary': '您完整的塔羅牌庫以及牌義',
    'auth.uspGuidedSpreads': '適用於各種情況的引導式牌陣',
    'auth.uspPersonalDiary': '建立您的個人塔羅日記',
    'auth.uspInsights': '練習您對未來的洞察力',
    'auth.validationFirstNameRequired': '名字為必填項',
    'auth.validationLastNameRequired': '姓氏為必填項',
    'auth.validationEmailRequired': '電子郵件為必填項',
    'auth.validationEmailInvalid': '請輸入有效的電子郵件地址',
    'auth.validationPasswordRequired': '密碼為必填項',
    'auth.validationPasswordTooShort': '密碼必須至少8個字符',
    'auth.validationPasswordWeak': '密碼必須包含至少一個大寫字母、一個小寫字母和一個數字',
    'auth.validationConfirmPasswordRequired': '請確認您的密碼',
    'auth.validationPasswordsNotMatch': '密碼不匹配',
    'auth.forgotPasswordTitle': '忘記密碼',
    'auth.forgotPasswordSubtitle': '輸入您的電子郵件地址，我們將向您發送重置連結',
    'auth.sendResetLink': '發送重置連結',
    'auth.sendingResetLink': '發送中...',
    'auth.checkEmailTitle': '檢查您的電子郵件',
    'auth.checkEmailMessage': '我們已將密碼重置連結發送到',
    'auth.resetTokenLabel': '重置令牌（開發用）',
    'auth.copyToken': '複製令牌',
    'auth.resetTokenCopied': '重置令牌已複製到剪貼板！',
    'auth.validationEmailRequired': '電子郵件為必填項',
    'auth.rememberPassword': '記起您的密碼了嗎？ ',
    
    // Cards and Readings
    'card.upright': '正位',
    'card.reversed': '逆位',
    'reading.question': '您想詢問什麼問題？',
    'reading.questionPlaceholder': '在此輸入您的問題...',
    'reading.yourReading': '您的占卜結果',
    'reading.saveReading': '保存占卜結果',
    
    // Common
    'common.loading': '載入中...',
    'common.save': '保存',
    'common.cancel': '取消',
    'common.close': '關閉',
    'common.continue': '繼續',
    'common.today': '今天',
    'common.card': '卡牌',

    // Tarot Cards
    // Major Arcana
    'tarot.fool.name': '0. 愚人',
    'tarot.fool.meaning': '愚人是大阿爾卡納的第一張牌，代表新旅程的開始。',
    'tarot.fool.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/0-the-fool-tarot-card-meanings-free-tarot-reading/',
    'tarot.magician.name': '1. 魔術師',
    'tarot.magician.meaning': '魔術師是潛能與顯化之牌，象徵將想法變為現實的力量。',
    'tarot.magician.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/1-the-magician-tarot-card-meanings-free-tarot-reading/',
    'tarot.highPriestess.name': '2. 女祭司',
    'tarot.highPriestess.meaning': '女祭司代表直覺、神秘和與潛意識的連接。',
    'tarot.highPriestess.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/2-the-high-priestess-tarot-card-meanings-free-tarot-reading/',
    'tarot.empress.name': '3. 皇后',
    'tarot.empress.meaning': '皇后是豐富、滋養能量和創造力的卡牌。',
    'tarot.empress.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/3-the-empress-tarot-card-meanings-free-tarot-reading/',
    'tarot.emperor.name': '4. 皇帝',
    'tarot.emperor.meaning': '皇帝是權威、結構和穩定的卡牌。',
    'tarot.emperor.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/4-the-emperor-tarot-card-meanings-free-tarot-reading/',
    'tarot.hierophant.name': '5. 教皇',
    'tarot.hierophant.meaning': '教皇也稱為大祭司，代表傳統、靈性和指導。',
    'tarot.hierophant.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/5-the-hierophant-tarot-card-meanings-free-tarot-reading/',
    'tarot.lovers.name': '6. 戀人',
    'tarot.lovers.meaning': '戀人卡象徵結合、和諧和選擇的力量。',
    'tarot.lovers.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/6-the-lovers-tarot-card-meanings-free-tarot-reading/',
    'tarot.chariot.name': '7. 戰車',
    'tarot.chariot.meaning': '戰車代表決心、意志力以及通過專注和控制獲得的勝利。',
    'tarot.chariot.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/7-the-chariot-tarot-card-meanings-free-tarot-reading/',
    'tarot.strength.name': '8. 力量',
    'tarot.strength.meaning': '力量卡是內在勇氣、自我掌控和慈悲的深刻象徵。',
    'tarot.strength.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/8-strength-tarot-card-meanings-free-tarot-reading/',
    'tarot.hermit.name': '9. 隱士',
    'tarot.hermit.meaning': '隱士是內省和內在指導的卡牌，象徵尋求智慧與理解的內在之旅。',
    'tarot.hermit.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/9-the-hermit-tarot-card-meanings-free-tarot-reading/',
    'tarot.wheelOfFortune.name': '10. 命運之輪',
    'tarot.wheelOfFortune.meaning': '命運之輪是循環、命運和生活變遷的卡牌，象徵我們都會經歷的起伏。',
    'tarot.wheelOfFortune.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/10-wheel-of-fortune-tarot-card-meanings-free-tarot-reading/',
    'tarot.justice.name': '11. 正義',
    'tarot.justice.meaning': '正義是代表公平、真理和追求平衡的卡牌。',
    'tarot.justice.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/11-justice-tarot-card-meanings-free-tarot-reading/',
    'tarot.hangedMan.name': '12. 倒吊者',
    'tarot.hangedMan.meaning': '倒吊者是投降、犧牲和新視角的卡牌，代表放手和從不同角度看事物的變革力量。',
    'tarot.hangedMan.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/12-the-hanged-man-tarot-card-meanings-free-tarot-reading/',
    'tarot.death.name': '13. 死神',
    'tarot.death.meaning': '死神卡是變化和重生的強大象徵，代表生命的循環、結束和新的開始。',
    'tarot.death.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/13-death-tarot-card-meanings-free-tarot-reading/',
    'tarot.temperance.name': '14. 節制',
    'tarot.temperance.meaning': '節制是平衡、和諧和調和的卡牌，象徵融合對立面以達到更大整體的藝術。',
    'tarot.temperance.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/14-temperance-tarot-card-meanings-free-tarot-reading/',
    'tarot.devil.name': '15. 惡魔',
    'tarot.devil.meaning': '惡魔是體現誘惑、束縛和人性黑暗面的卡牌。',
    'tarot.devil.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/15-the-devil-tarot-card-meanings-free-tarot-reading/',
    'tarot.tower.name': '16. 塔',
    'tarot.tower.meaning': '塔是劇變和啟示的卡牌，象徵突然的變化和舊結構的瓦解。',
    'tarot.tower.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/16-the-tower-tarot-card-meanings-free-tarot-reading/',
    'tarot.star.name': '17. 星星',
    'tarot.star.meaning': '星星是希望、靈感和寧靜的卡牌，象徵重生感和更光明日子的承諾。',
    'tarot.star.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/17-the-star-tarot-card-meanings-free-tarot-reading/',
    'tarot.moon.name': '18. 月亮',
    'tarot.moon.meaning': '月亮是直覺、幻象和潛意識的卡牌，代表通往未知的旅程和探索自我隱藏面的過程。',
    'tarot.moon.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/18-the-moon-tarot-card-meanings-free-tarot-reading/',
    'tarot.sun.name': '19. 太陽',
    'tarot.sun.meaning': '太陽是喜悅、成功和活力的卡牌，體現正面能量和清晰度帶來的溫暖與光明。',
    'tarot.sun.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/19-the-sun-tarot-card-meanings-free-tarot-reading/',
    'tarot.judgement.name': '20. 審判',
    'tarot.judgement.meaning': '審判是更新、重生和覺醒的卡牌，象徵深刻的轉變和評估人生選擇的呼喚。',
    'tarot.judgement.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/20-judgement-tarot-card-meanings-free-tarot-reading/',
    'tarot.world.name': '21. 世界',
    'tarot.world.meaning': '世界是完成、實現和和諧的卡牌，代表旅程的終結和目標的達成。',
    'tarot.world.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/21-the-world-tarot-card-meanings-free-tarot-reading/',

    // 權杖套牌
    'tarot.aceOfWands.name': '權杖王牌',
    'tarot.aceOfWands.meaning': '權杖王牌是靈感、新開始和創造潛力的卡牌，象徵點燃行動和創新的熱情火花。',
    'tarot.aceOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/ace-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfWands.name': '權杖二',
    'tarot.twoOfWands.meaning': '權杖二是探索和未來規劃的卡牌，象徵選擇的時刻和對前路的深思。',
    'tarot.twoOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/2-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfWands.name': '權杖三',
    'tarot.threeOfWands.meaning': '權杖三是擴展和遠見的卡牌，象徵對未來的希望和長期成功。',
    'tarot.threeOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/3-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfWands.name': '權杖四',
    'tarot.fourOfWands.meaning': '權杖四是慶祝和穩定的卡牌，象徵和諧與社區幸福。',
    'tarot.fourOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/4-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfWands.name': '權杖五',
    'tarot.fiveOfWands.meaning': '權杖五是競爭和鬥爭的卡牌，象徵挑戰和意見分歧。',
    'tarot.fiveOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/5-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfWands.name': '權杖六',
    'tarot.sixOfWands.meaning': '權杖六是勝利和成功的卡牌，象徵認可和成就。',
    'tarot.sixOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/6-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfWands.name': '權杖七',
    'tarot.sevenOfWands.meaning': '權杖七是防守和勇氣的卡牌，象徵面對挑戰時的決心。',
    'tarot.sevenOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/7-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfWands.name': '權杖八',
    'tarot.eightOfWands.meaning': '權杖八是快速行動和進展的卡牌，象徵能量和勢頭。',
    'tarot.eightOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/8-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfWands.name': '權杖九',
    'tarot.nineOfWands.meaning': '權杖九是毅力和決心的卡牌，象徵在困難中堅持的力量。',
    'tarot.nineOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/9-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfWands.name': '權杖十',
    'tarot.tenOfWands.meaning': '權杖十是負擔和責任的卡牌，象徵成功的沉重代價。',
    'tarot.tenOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/10-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfWands.name': '權杖侍者',
    'tarot.pageOfWands.meaning': '權杖侍者是冒險和探索的卡牌，象徵新的熱情和學習的渴望。',
    'tarot.pageOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/page-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfWands.name': '權杖騎士',
    'tarot.knightOfWands.meaning': '權杖騎士是行動和衝動的卡牌，象徵勇敢和冒險精神。',
    'tarot.knightOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/knight-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfWands.name': '權杖皇后',
    'tarot.queenOfWands.meaning': '權杖皇后是自信和魅力的卡牌，象徵領導力和溫暖的心。',
    'tarot.queenOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/queen-of-wands-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfWands.name': '權杖國王',
    'tarot.kingOfWands.meaning': '權杖國王是統御力和野心的卡牌，象徵遠見和成就。',
    'tarot.kingOfWands.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/king-of-wands-tarot-card-meanings-free-tarot-reading/',

    // 聖杯套牌
    'tarot.aceOfCups.name': '聖杯王牌',
    'tarot.aceOfCups.meaning': '聖杯王牌是新愛情和情感開始的卡牌，象徵心靈開放和精神充實。',
    'tarot.aceOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/ace-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfCups.name': '聖杯二',
    'tarot.twoOfCups.meaning': '聖杯二是愛情和和諧的卡牌，象徵靈魂連結和伙伴關係。',
    'tarot.twoOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/2-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfCups.name': '聖杯三',
    'tarot.threeOfCups.meaning': '聖杯三是友誼和慶祝的卡牌，象徵社區和喜悅的分享。',
    'tarot.threeOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/3-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfCups.name': '聖杯四',
    'tarot.fourOfCups.meaning': '聖杯四是冷漠和內省的卡牌，象徵對現狀的不滿和忽視新機會。',
    'tarot.fourOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/4-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfCups.name': '聖杯五',
    'tarot.fiveOfCups.meaning': '聖杯五是悲傷和失望的卡牌，象徵失去的悲傷和恢復的希望。',
    'tarot.fiveOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/5-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfCups.name': '聖杯六',
    'tarot.sixOfCups.meaning': '聖杯六是懷舊和純真的卡牌，象徵過去的記憶和童年的無邪。',
    'tarot.sixOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/6-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfCups.name': '聖杯七',
    'tarot.sevenOfCups.meaning': '聖杯七是幻想和選擇的卡牌，象徵眾多選擇和逃避現實的誘惑。',
    'tarot.sevenOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/7-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfCups.name': '聖杯八',
    'tarot.eightOfCups.meaning': '聖杯八是放棄和探索的卡牌，象徵從現狀中離開和踏上新道路的旅程。',
    'tarot.eightOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/8-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfCups.name': '聖杯九',
    'tarot.nineOfCups.meaning': '聖杯九是滿足和願望實現的卡牌，象徵情感充實和幸福。',
    'tarot.nineOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/9-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfCups.name': '聖杯十',
    'tarot.tenOfCups.meaning': '聖杯十是家庭和諧和完全幸福的卡牌，象徵充滿愛的關係和情感充實。',
    'tarot.tenOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/10-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfCups.name': '聖杯侍者',
    'tarot.pageOfCups.meaning': '聖杯侍者是敏感和創造性的卡牌，象徵新的情感和藝術靈感。',
    'tarot.pageOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/page-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfCups.name': '聖杯騎士',
    'tarot.knightOfCups.meaning': '聖杯騎士是浪漫和理想主義的卡牌，象徵情感追求和騎士精神。',
    'tarot.knightOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/knight-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfCups.name': '聖杯皇后',
    'tarot.queenOfCups.meaning': '聖杯皇后是直覺和同理心的卡牌，象徵情感智慧和養育之愛。',
    'tarot.queenOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/queen-of-cups-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfCups.name': '聖杯國王',
    'tarot.kingOfCups.meaning': '聖杯國王是情感成熟和平衡的卡牌，象徵慈悲和情感控制。',
    'tarot.kingOfCups.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/king-of-cups-tarot-card-meanings-free-tarot-reading/',

    // 寶劍套牌
    'tarot.aceOfSwords.name': '寶劍王牌',
    'tarot.aceOfSwords.meaning': '寶劍王牌是新想法和精神清晰的卡牌，象徵真理和智力突破。',
    'tarot.aceOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/ace-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfSwords.name': '寶劍二',
    'tarot.twoOfSwords.meaning': '寶劍二是困難選擇和僵局的卡牌，象徵決策困難和內在衝突。',
    'tarot.twoOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/2-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfSwords.name': '寶劍三',
    'tarot.threeOfSwords.meaning': '寶劍三是心痛和悲傷的卡牌，象徵情感創傷和分離之痛。',
    'tarot.threeOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/3-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfSwords.name': '寶劍四',
    'tarot.fourOfSwords.meaning': '寶劍四是休息和冥想的卡牌，象徵平靜和精神恢復。',
    'tarot.fourOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/4-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfSwords.name': '寶劍五',
    'tarot.fiveOfSwords.meaning': '寶劍五是衝突和失敗的卡牌，象徵勝利的空虛和人際關係的損失。',
    'tarot.fiveOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/5-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfSwords.name': '寶劍六',
    'tarot.sixOfSwords.meaning': '寶劍六是過渡和恢復的卡牌，象徵從困難中逃脫和治癒之旅。',
    'tarot.sixOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/6-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfSwords.name': '寶劍七',
    'tarot.sevenOfSwords.meaning': '寶劍七是欺騙和策略的卡牌，象徵單獨行動和狡猾計劃。',
    'tarot.sevenOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/7-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfSwords.name': '寶劍八',
    'tarot.eightOfSwords.meaning': '寶劍八是束縛和限制的卡牌，象徵自我限制和受害者心態。',
    'tarot.eightOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/8-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfSwords.name': '寶劍九',
    'tarot.nineOfSwords.meaning': '寶劍九是焦慮和惡夢的卡牌，象徵精神痛苦和恐懼。',
    'tarot.nineOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/9-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfSwords.name': '寶劍十',
    'tarot.tenOfSwords.meaning': '寶劍十是結束和重生的卡牌，象徵完全終結和新的開始。',
    'tarot.tenOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/10-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfSwords.name': '寶劍侍者',
    'tarot.pageOfSwords.meaning': '寶劍侍者是好奇和警覺的卡牌，象徵新想法和謹慎觀察。',
    'tarot.pageOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/page-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfSwords.name': '寶劍騎士',
    'tarot.knightOfSwords.meaning': '寶劍騎士是行動和決斷的卡牌，象徵快速思考和衝動行動。',
    'tarot.knightOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/knight-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfSwords.name': '寶劍皇后',
    'tarot.queenOfSwords.meaning': '寶劍皇后是智慧獨立和清晰的卡牌，象徵敏銳判斷力和誠實。',
    'tarot.queenOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/queen-of-swords-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfSwords.name': '寶劍國王',
    'tarot.kingOfSwords.meaning': '寶劍國王是智慧權威和公正的卡牌，象徵邏輯思維和道德領導力。',
    'tarot.kingOfSwords.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/king-of-swords-tarot-card-meanings-free-tarot-reading/',

    // 錢幣套牌
    'tarot.aceOfPentacles.name': '錢幣王牌',
    'tarot.aceOfPentacles.meaning': '錢幣王牌是新機會和物質開始的卡牌，象徵繁榮和實現的可能性。',
    'tarot.aceOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/ace-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.twoOfPentacles.name': '錢幣二',
    'tarot.twoOfPentacles.meaning': '錢幣二是平衡和適應的卡牌，象徵對變化的靈活性和優先順序管理。',
    'tarot.twoOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/2-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.threeOfPentacles.name': '錢幣三',
    'tarot.threeOfPentacles.meaning': '錢幣三是合作和技能的卡牌，象徵團隊合作和專業技能發展。',
    'tarot.threeOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/3-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.fourOfPentacles.name': '錢幣四',
    'tarot.fourOfPentacles.meaning': '錢幣四是物質穩定和保守的卡牌，象徵財務管理和對變化的抵抗。',
    'tarot.fourOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/4-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.fiveOfPentacles.name': '錢幣五',
    'tarot.fiveOfPentacles.meaning': '錢幣五是困頓和不安的卡牌，象徵物質困難和對支援的需要。',
    'tarot.fiveOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/5-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.sixOfPentacles.name': '錢幣六',
    'tarot.sixOfPentacles.meaning': '錢幣六是慷慨和平衡的卡牌，象徵給予和接受的和諧。',
    'tarot.sixOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/6-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.sevenOfPentacles.name': '錢幣七',
    'tarot.sevenOfPentacles.meaning': '錢幣七是耐心和評估的卡牌，象徵長期投資和成果審視。',
    'tarot.sevenOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/7-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.eightOfPentacles.name': '錢幣八',
    'tarot.eightOfPentacles.meaning': '錢幣八是技能和奉獻的卡牌，象徵工匠精神和持續改進。',
    'tarot.eightOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/8-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.nineOfPentacles.name': '錢幣九',
    'tarot.nineOfPentacles.meaning': '錢幣九是獨立和富足的卡牌，象徵自立的成功和物質穩定。',
    'tarot.nineOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/9-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.tenOfPentacles.name': '錢幣十',
    'tarot.tenOfPentacles.meaning': '錢幣十是遺產和穩定的卡牌，象徵跨世代財富和家族安全。',
    'tarot.tenOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/10-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.pageOfPentacles.name': '錢幣侍者',
    'tarot.pageOfPentacles.meaning': '錢幣侍者是學習和實用性的卡牌，象徵新技能和現實方法。',
    'tarot.pageOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/page-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.knightOfPentacles.name': '錢幣騎士',
    'tarot.knightOfPentacles.meaning': '錢幣騎士是勤奮和責任的卡牌，象徵穩定進步和可靠性。',
    'tarot.knightOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/knight-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.queenOfPentacles.name': '錢幣皇后',
    'tarot.queenOfPentacles.meaning': '錢幣皇后是養育和實用性的卡牌，象徵物質豐富和家庭智慧。',
    'tarot.queenOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/queen-of-pentacles-tarot-card-meanings-free-tarot-reading/',
    'tarot.kingOfPentacles.name': '錢幣國王',
    'tarot.kingOfPentacles.meaning': '錢幣國王是物質成功和穩定的卡牌，象徵財務豐富和商業天賦。',
    'tarot.kingOfPentacles.link': 'https://tarotreadingcard.online/zh/tarot-card-meanings/king-of-pentacles-tarot-card-meanings-free-tarot-reading/',

    // 逆位意義 - 大阿爾卡納
    'tarot.fool.reversedMeaning': '愚人逆位時，通常表示猶豫、魯莽或在面對新開始時缺乏準備。',
    'tarot.magician.reversedMeaning': '魔術師逆位時，通常表示被阻礙的潛力、濫用權力或缺乏專注。',
    'tarot.highPriestess.reversedMeaning': '女祭司逆位時，表示與直覺的斷連、隱藏真相被揭露或難以獲得內在智慧。',
    'tarot.empress.reversedMeaning': '皇后逆位時，表示創造性阻塞、忽視或養育能量的失衡。',
    'tarot.emperor.reversedMeaning': '皇帝逆位時，表示權威、控制或結構方面的問題。',
    'tarot.hierophant.reversedMeaning': '教皇逆位時，表示反叛、不符合傳統或拒絕傳統價值觀和制度。',
    'tarot.lovers.reversedMeaning': '戀人逆位時，表示不和諧、不一致或關係中或內心的衝突。',
    'tarot.chariot.reversedMeaning': '戰車逆位時，表示缺乏控制、方向或專注力。',
    'tarot.strength.reversedMeaning': '力量逆位時，代表內在弱點、自我懷疑或個人力量的誤用。',
    'tarot.hermit.reversedMeaning': '隱士逆位帶來困惑、孤立或拒絕尋求內在指導的感覺。',
    'tarot.wheelOfFortune.reversedMeaning': '命運之輪逆位暗示陷入厄運循環和對環境缺乏控制感。',
    'tarot.justice.reversedMeaning': '正義逆位代表不公平、缺乏責任感或逃避行為後果。',
    'tarot.hangedMan.reversedMeaning': '倒吊者逆位表示抗拒改變、缺乏耐心或拒絕看見不同觀點。',
    'tarot.death.reversedMeaning': '死神逆位暗示抗拒改變、停滯或對必要結束和轉變的恐懼。',
    'tarot.temperance.reversedMeaning': '節制逆位代表失衡、過度或缺乏適度和和諧。',
    'tarot.devil.reversedMeaning': '惡魔逆位表示擺脫束縛、克服成癮或從限制性信念中解脫。',
    'tarot.tower.reversedMeaning': '塔逆位表示避免必要改變、內在動盪或抗拒轉變。',
    'tarot.star.reversedMeaning': '星星逆位反映希望喪失、缺乏信仰或與靈性指導斷連。',
    'tarot.moon.reversedMeaning': '月亮逆位帶來清晰、釋放幻象或克服困惑和恐懼。',
    'tarot.sun.reversedMeaning': '太陽逆位暗示暫時挫折、缺乏熱忱或難以看見積極面。',
    'tarot.judgement.reversedMeaning': '審判逆位代表避免自我反省、嚴苛自我批判或拒絕從過去錯誤中學習。',
    'tarot.world.reversedMeaning': '世界逆位表示未完成目標、缺乏結束或為內在滿足尋求外在認可。',

    // 逆位意義 - 小阿爾卡納
    // 權杖逆位
    'tarot.aceOfWands.reversedMeaning': '權杖王牌逆位表示延遲的計劃、創意停滯或新項目的艱難開始。',
    'tarot.twoOfWands.reversedMeaning': '權杖二逆位表示缺乏規劃、個人目標或對未來的恐懼。',
    'tarot.threeOfWands.reversedMeaning': '權杖三逆位表示延遲、意外障礙或有限的遠見。',
    'tarot.fourOfWands.reversedMeaning': '權杖四逆位反映不穩定的基礎、家庭不和或缺乏慶祝。',
    'tarot.fiveOfWands.reversedMeaning': '權杖五逆位表示內在衝突、避免競爭或尋找解決方案。',
    'tarot.sixOfWands.reversedMeaning': '權杖六逆位表示私人成就、缺乏自信或渴望認可。',
    'tarot.sevenOfWands.reversedMeaning': '權杖七逆位表示投降、感到不堪重負或難以守住立場。',
    'tarot.eightOfWands.reversedMeaning': '權杖八逆位反映延遲、耐心或內在變化。',
    'tarot.nineOfWands.reversedMeaning': '權杖九逆位表示疲勞、偏執或放棄最後的努力。',
    'tarot.tenOfWands.reversedMeaning': '權杖十逆位表示委派責任、倦怠或減輕負擔。',
    'tarot.pageOfWands.reversedMeaning': '權杖侍者逆位表示不完整的計劃、脾氣暴躁或抗拒學習。',
    'tarot.knightOfWands.reversedMeaning': '權杖騎士逆位反映衝動、憤怒或魯莽行為。',
    'tarot.queenOfWands.reversedMeaning': '權杖皇后逆位表示自我懷疑、嫉妒或控制行為。',
    'tarot.kingOfWands.reversedMeaning': '權杖國王逆位表示專制的領導、傲慢或感到無力。',

    // 聖杯逆位
    'tarot.aceOfCups.reversedMeaning': '聖杯王牌逆位表示情感空虛、創意停滯或精神斷連。',
    'tarot.twoOfCups.reversedMeaning': '聖杯二逆位表示關係不和、自戀或分離。',
    'tarot.threeOfCups.reversedMeaning': '聖杯三逆位反映社交孤立、三角關係或膚淺的聯繫。',
    'tarot.fourOfCups.reversedMeaning': '聖杯四逆位表示重新獲得動力、對新機會開放或接受。',
    'tarot.fiveOfCups.reversedMeaning': '聖杯五逆位表示接受、前進或從失落中恢復。',
    'tarot.sixOfCups.reversedMeaning': '聖杯六逆位表示從過去解脫、成長或獨立。',
    'tarot.sevenOfCups.reversedMeaning': '聖杯七逆位反映清晰、決斷或現實的選擇。',
    'tarot.eightOfCups.reversedMeaning': '聖杯八逆位表示恐懼、逃避或抗拒離開現狀。',
    'tarot.nineOfCups.reversedMeaning': '聖杯九逆位表示內在不幸、貪婪或虛假的滿足感。',
    'tarot.tenOfCups.reversedMeaning': '聖杯十逆位反映家庭不和、關係問題或缺乏和諧。',
    'tarot.pageOfCups.reversedMeaning': '聖杯侍者逆位表示情感不成熟、創意停滯或忽視直覺。',
    'tarot.knightOfCups.reversedMeaning': '聖杯騎士逆位表示情緒波動、不切實際的期望或情感操控。',
    'tarot.queenOfCups.reversedMeaning': '聖杯皇后逆位反映情感不穩定、相互依賴或缺乏直覺。',
    'tarot.kingOfCups.reversedMeaning': '聖杯國王逆位表示情感操控、不穩定或壓抑情感。',

    // 寶劍逆位
    'tarot.aceOfSwords.reversedMeaning': '寶劍王牌逆位表示思維混亂、延遲的正義或精神迷霧。',
    'tarot.twoOfSwords.reversedMeaning': '寶劍二逆位表示信息過載、優柔寡斷或逃避真相。',
    'tarot.threeOfSwords.reversedMeaning': '寶劍三逆位反映治癒、寬恕或情感釋放。',
    'tarot.fourOfSwords.reversedMeaning': '寶劍四逆位表示焦慮、停滯或抗拒休息。',
    'tarot.fiveOfSwords.reversedMeaning': '寶劍五逆位表示和解、寬恕或從過去衝突中學習。',
    'tarot.sixOfSwords.reversedMeaning': '寶劍六逆位反映未解決的問題、艱難過渡或被過去困住。',
    'tarot.sevenOfSwords.reversedMeaning': '寶劍七逆位表示坦白、承擔責任或揭露欺騙。',
    'tarot.eightOfSwords.reversedMeaning': '寶劍八逆位表示自由的道路、自我解放或新的視角。',
    'tarot.nineOfSwords.reversedMeaning': '寶劍九逆位反映希望恢復、從焦慮中解脫或內在治癒。',
    'tarot.tenOfSwords.reversedMeaning': '寶劍十逆位表示恢復、重生或困難時期的結束。',
    'tarot.pageOfSwords.reversedMeaning': '寶劍侍者逆位表示溝通有缺陷、惡意八卦或準備不足。',
    'tarot.knightOfSwords.reversedMeaning': '寶劍騎士逆位反映衝動行為、攻擊性或缺乏規劃。',
    'tarot.queenOfSwords.reversedMeaning': '寶劍皇后逆位表示刻薄、冷酷或情感斷連。',
    'tarot.kingOfSwords.reversedMeaning': '寶劍國王逆位表示濫用權力、殘酷或缺乏判斷力。',

    // 錢幣逆位
    'tarot.aceOfPentacles.reversedMeaning': '錢幣王牌逆位表示失去機會、財務問題或物質不穩定。',
    'tarot.twoOfPentacles.reversedMeaning': '錢幣二逆位表示不平衡、優先順序混亂或缺乏組織。',
    'tarot.threeOfPentacles.reversedMeaning': '錢幣三逆位反映缺乏工作、團隊合作問題或低估技能。',
    'tarot.fourOfPentacles.reversedMeaning': '錢幣四逆位表示慷慨、放手或財務魯莽。',
    'tarot.fiveOfPentacles.reversedMeaning': '錢幣五逆位表示財務恢復、從精神貧困中解脫或接受幫助。',
    'tarot.sixOfPentacles.reversedMeaning': '錢幣六逆位反映單方面關係、債務或不公平交換。',
    'tarot.sevenOfPentacles.reversedMeaning': '錢幣七逆位表示渴望立即結果、缺乏耐心或重新評估投資。',
    'tarot.eightOfPentacles.reversedMeaning': '錢幣八逆位表示完美主義、不滿意或缺乏技能發展。',
    'tarot.nineOfPentacles.reversedMeaning': '錢幣九逆位反映財務依賴、過度工作或過度節儉。',
    'tarot.tenOfPentacles.reversedMeaning': '錢幣十逆位表示財務損失、家庭爭議或遺產問題。',
    'tarot.pageOfPentacles.reversedMeaning': '錢幣侍者逆位表示逃避現實、抗拒學習或財務不注意。',
    'tarot.knightOfPentacles.reversedMeaning': '錢幣騎士逆位反映懶惰、完美主義或缺乏進步。',
    'tarot.queenOfPentacles.reversedMeaning': '錢幣皇后逆位表示財務不安全、過度工作或忽視自我照顧。',
    'tarot.kingOfPentacles.reversedMeaning': '錢幣國王逆位表示貪婪、物質主義或財務不負責任。',

    // Positions
    'tarot.position.upright': '正位',
    'tarot.position.reversed': '逆位'
  }
};